import React, { useState, useEffect, useRef } from 'react';
import { Ticket, User, LogOut, Plus, Calendar, DollarSign, Users, ArrowLeft, ShoppingCart, CheckCircle, Trophy, Bell, Mail, Phone, Share2, MessageCircle, RefreshCw, Image as ImageIcon, X, Search, Pencil } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { db, auth, storage, googleProvider } from './firebase';
import { collection, addDoc, getDocs, getDoc, doc, updateDoc, deleteDoc, query, where, orderBy, setDoc, limit } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { iniciarNotificacionesPush } from './pushNotifications';
import { generarImagenCompartirRifa, descargarOCompartirImagenRifa } from './utils/generarImagenCompartirRifa';
import { signInWithPopup, signInWithCredential, signOut, onAuthStateChanged, GoogleAuthProvider } from 'firebase/auth';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Capacitor } from '@capacitor/core';

/** Compartir imagen/flyer para WhatsApp (desactivado temporalmente) */
const MOSTRAR_COMPARTIR_FLYER = true;

/** Un número solo puede figurar una vez en pendientes/reservados */
const normalizarNumerosLista = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return [...new Set(arr)];
};

const agregarNumerosSinDuplicar = (actuales, nuevos) => {
  const base = normalizarNumerosLista(actuales);
  const set = new Set(base);
  const agregar = (nuevos || []).filter(n => n != null && !set.has(n));
  return agregar.length ? [...base, ...agregar] : base;
};

/** Quita una entrada por cada valor en aQuitar (sirve para liberar/confirmar de a uno) */
const quitarInstanciasPorLista = (lista, aQuitar) => {
  const resto = [...(lista || [])];
  for (const n of aQuitar || []) {
    const i = resto.indexOf(n);
    if (i !== -1) resto.splice(i, 1);
  }
  return resto;
};

const copiarAlPortapapeles = async (texto, etiqueta = 'Texto') => {
  const t = String(texto ?? '').trim();
  if (!t) return;
  try {
    await navigator.clipboard.writeText(t);
    alert(`✅ ${etiqueta} copiado al portapapeles:\n${t}`);
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = t;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      alert(`✅ ${etiqueta} copiado al portapapeles:\n${t}`);
    } catch {
      alert(`No se pudo copiar. ${etiqueta}: ${t}`);
    }
    document.body.removeChild(textArea);
  }
};

/** Badge Alias/CBU: al tocar copia al portapapeles */
function CopiableAliasCbu({ alias, cbu, className = '', soloValor = false }) {
  if (!alias && !cbu) return null;
  const esAlias = !!alias;
  const valor = alias || cbu;
  const etiqueta = esAlias ? 'Alias' : 'CBU';
  const texto = soloValor ? valor : (esAlias ? `Alias: ${valor}` : `CBU: ${valor}`);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        copiarAlPortapapeles(valor, etiqueta);
      }}
      className={`bg-yellow-600 text-white px-3 py-1 rounded-lg font-bold text-sm cursor-pointer hover:bg-yellow-500 active:scale-[0.98] transition ${className}`.trim()}
      title={`Tocá para copiar ${etiqueta}`}
    >
      💳 {texto} <span className="opacity-75 text-xs font-normal">(tocá para copiar)</span>
    </button>
  );
}

/** Filtra rifas por título, descripción, premio, creador o tipo */
const filtrarRifasLista = (lista, termino) => {
  const q = String(termino || '').trim().toLowerCase();
  if (!q) return lista;
  return lista.filter((r) => {
    const tipoLabel =
      r.tipo === 'rapadita' ? 'rapadita raspadita' : r.tipo === 'sorteo' ? 'sorteo' : 'rifa';
    const texto = [
      r.titulo,
      r.descripcion,
      r.premio,
      r.creadorNombre,
      tipoLabel,
      ...(Array.isArray(r.premios) ? r.premios : []),
      r.id
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return texto.includes(q);
  });
};

const esCreadorDeRifa = (rifa, usuario) => {
  if (!rifa || !usuario) return false;
  const uid = usuario.uid || usuario.id;
  return (
    rifa.creadorId === usuario.id ||
    rifa.creadorId === uid ||
    rifa.creadorUid === uid ||
    rifa.creadorUid === usuario.id
  );
};

/** Texto copiable (alias, CBU u otro dato bancario) */
function TextoCopiable({ texto, etiqueta, className = '', children }) {
  const t = String(texto ?? '').trim();
  if (!t) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        copiarAlPortapapeles(t, etiqueta);
      }}
      className={`cursor-pointer hover:opacity-90 active:scale-[0.98] transition ${className}`.trim()}
      title={`Tocá para copiar ${etiqueta}`}
    >
      {children ?? t}
      <span className="sr-only"> (tocá para copiar)</span>
    </button>
  );
}

// Simulación de Router (en producción usarías React Router)
function Router({ children }) {
  return children;
}

export default function Acceso7Rifas() {
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'rifa-detail', 'datos-personales', 'payment', 'mis-rifas', 'admin-panel', 'ganadores'
  const [selectedRifa, setSelectedRifa] = useState(null);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [showSorteoModal, setShowSorteoModal] = useState(false);
  const [numeroGanador, setNumeroGanador] = useState(null);
  const [sorteoEnCurso, setSorteoEnCurso] = useState(false);
  const [premioActualSorteo, setPremioActualSorteo] = useState(null); // Premio específico que se está sorteando
  const [numerosPendientes, setNumerosPendientes] = useState([]); // Números pendientes de confirmación
  const [showSeleccionarPremioModal, setShowSeleccionarPremioModal] = useState(false);
  const [selectedPremioForSorteo, setSelectedPremioForSorteo] = useState(null);
  const [showEditarPremiosModal, setShowEditarPremiosModal] = useState(false);
  const [premiosEditando, setPremiosEditando] = useState([]);
  const [showEditarRifaModal, setShowEditarRifaModal] = useState(false);
  const [rifaEdicionForm, setRifaEdicionForm] = useState({
    rifaId: '',
    tipo: 'rifa',
    titulo: '',
    descripcion: '',
    alias: '',
    linkMercadoPago: ''
  });
  const [guardandoEdicionRifa, setGuardandoEdicionRifa] = useState(false);
  const [generandoFlyerId, setGenerandoFlyerId] = useState(null);
  const [showInstrucciones, setShowInstrucciones] = useState(false);
  const [pasoActual, setPasoActual] = useState(0);
  const [showQRModal, setShowQRModal] = useState(false);
  const [currentQRToken, setCurrentQRToken] = useState(null);
  const [currentQRUrl, setCurrentQRUrl] = useState(null);
  const [sorteoTokenValid, setSorteoTokenValid] = useState(null);
  
  // Restaurar sesión desde localStorage al cargar (evita pedir "Iniciar sesión" al actualizar)
  const [user, setUser] = useState(() => {
    try {
      if (typeof window === 'undefined') return null;
      const s = localStorage.getItem('acceso7_user');
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCreateRifa, setShowCreateRifa] = useState(false);
  const [rifas, setRifas] = useState([]);
  const [users, setUsers] = useState([]);
  const [compras, setCompras] = useState([]);
  const [ganadores, setGanadores] = useState([]); // [{rifaId, numeroGanador, ganadorNombre, ganadorEmail, fechaSorteo}]
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]); // Usuarios pendientes de aprobación
  const [showCompradorModal, setShowCompradorModal] = useState(false);
  const [compradorInfo, setCompradorInfo] = useState(null);
  const [showCondicionesModal, setShowCondicionesModal] = useState(false);
  const [aceptoCondiciones, setAceptoCondiciones] = useState(false);
  const [isCreatingRifa, setIsCreatingRifa] = useState(false);
  const [isLoadingRifa, setIsLoadingRifa] = useState(false);
  const [rifaIdFromUrl, setRifaIdFromUrl] = useState(null);
  const [refreshingRifa, setRefreshingRifa] = useState(false);
  const [refreshingMisRifas, setRefreshingMisRifas] = useState(false);
  const [misRifasShowFinalizadas, setMisRifasShowFinalizadas] = useState(false);
  // Imagen por premio para crear rifa: array indexado por posición del premio
  // Cada elemento es null o { file, previewUrl }
  const [imagenesPremios, setImagenesPremios] = useState([null, null]);
  const [imagenUploading, setImagenUploading] = useState(false);

  // Validar y guardar imagen seleccionada para un premio específico
  const handleSeleccionarImagenPremio = (index, file) => {
    if (!file) {
      quitarImagenPremio(index);
      return;
    }
    if (!file.type || !file.type.startsWith('image/')) {
      alert('El archivo seleccionado no es una imagen válida.');
      return;
    }
    const MAX_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      alert('La imagen es muy grande. Máximo permitido: 5 MB.');
      return;
    }
    setImagenesPremios(prev => {
      const next = [...prev];
      if (next[index]?.previewUrl) {
        try { URL.revokeObjectURL(next[index].previewUrl); } catch (_) {}
      }
      next[index] = { file, previewUrl: URL.createObjectURL(file) };
      return next;
    });
  };

  const quitarImagenPremio = (index) => {
    setImagenesPremios(prev => {
      const next = [...prev];
      if (next[index]?.previewUrl) {
        try { URL.revokeObjectURL(next[index].previewUrl); } catch (_) {}
      }
      next[index] = null;
      return next;
    });
  };

  // MIME estable para Storage: evita reglas que rechazan contentType vacío u octet-stream sin extensión clara
  const contentTypeParaImagenSubida = (file) => {
    if (file?.type && file.type.startsWith('image/')) return file.type;
    const raw = (file?.name?.split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const map = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      jfif: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      bmp: 'image/bmp',
      heic: 'image/heic'
    };
    return map[raw] || 'image/jpeg';
  };

  // Sube una imagen de premio y devuelve { url, path }
  const subirImagenPremio = async (rifaId, file, premioIndex) => {
    if (!file) return null;
    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const path = `rifas/${rifaId}/premio-${premioIndex + 1}-${Date.now()}.${ext}`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file, { contentType: contentTypeParaImagenSubida(file) });
    const url = await getDownloadURL(ref);
    return { url, path };
  };

  // Elimina TODAS las imágenes asociadas a una rifa (portada legacy + por premio)
  const eliminarImagenRifa = async (rifa) => {
    try {
      if (rifa?.imagenPath) {
        await deleteObject(storageRef(storage, rifa.imagenPath));
        console.log('🖼️ Imagen (legacy) de rifa eliminada:', rifa.imagenPath);
      }
    } catch (e) {
      console.warn('No se pudo eliminar la imagen (legacy) de la rifa:', e.message);
    }
    if (Array.isArray(rifa?.imagenesPremios)) {
      for (const img of rifa.imagenesPremios) {
        if (img?.path) {
          try {
            await deleteObject(storageRef(storage, img.path));
            console.log('🖼️ Imagen de premio eliminada:', img.path);
          } catch (e) {
            console.warn('No se pudo eliminar imagen de premio:', e.message);
          }
        }
      }
    }
  };
  
  // Actualizar datos de la rifa actual (números vendidos, pendientes, compras)
  const handleActualizarRifa = async () => {
    if (!selectedRifa?.id) return;
    setRefreshingRifa(true);
    try {
      const loaded = await loadRifaByIdFromFirestore(selectedRifa.id);
      if (loaded) {
        setSelectedRifa(loaded);
        setRifas(prev => {
          const idx = prev.findIndex(r => r.id === loaded.id);
          if (idx >= 0) { const next = [...prev]; next[idx] = loaded; return next; }
          return [...prev, loaded];
        });
      }
      const comprasData = await loadComprasFromFirestore();
      setCompras(comprasData);
    } catch (e) {
      console.error('Error al actualizar rifa:', e);
    } finally {
      setRefreshingRifa(false);
    }
  };

  // Actualizar Mis Rifas y compras (para ver nuevos pagos o confirmaciones)
  const handleActualizarMisRifas = async () => {
    setRefreshingMisRifas(true);
    try {
      const [rifasData, comprasData] = await Promise.all([
        loadRifasFromFirestore(),
        loadComprasFromFirestore()
      ]);
      setRifas(rifasData);
      setCompras(comprasData);
      if (selectedRifa) {
        const actualizada = rifasData.find(r => r.id === selectedRifa.id);
        if (actualizada) setSelectedRifa(actualizada);
      }
    } catch (e) {
      console.error('Error al actualizar mis rifas:', e);
    } finally {
      setRefreshingMisRifas(false);
    }
  };
  
  // Sistema de URLs
  const getCurrentPath = () => {
    return window.location.pathname;
  };

  const navigateTo = (path) => {
    window.history.pushState({}, '', path);
    handleRouteChange();
  };

  /** Abre el modal de crear rifa sin cambiar la URL a /crear-rifa (si no, cada vez que se actualiza `rifas` el router re-ejecuta y el cuadro vuelve a “parpadear”). */
  const openCrearRifaModal = () => {
    if (!user) {
      alert('Debes crear una cuenta para crear rifas');
      navigateTo('/registro');
      return;
    }
    const emailSugerido = user?.email || (users.find(u => u.id === user?.id || u.uid === user?.uid)?.email) || (users.find(u => u.id === user?.id)?.correo) || '';
    setRifaForm(prev => ({ ...prev, emailVendedor: (prev.emailVendedor && prev.emailVendedor.trim()) ? prev.emailVendedor : emailSugerido }));
    setShowCreateRifa(true);
  };

  const handleRouteChange = () => {
    const path = getCurrentPath();
    
    // Redirigir / a /inicio
    if (path === '/') {
      navigateTo('/inicio');
      return;
    }
    
    if (path === '/inicio') {
      setCurrentPage('home');
    } else if (path.startsWith('/rifa/')) {
      const rifaId = path.split('/')[2];
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      // Si hay token, es un acceso por QR de sorteo
      if (token) {
        validateSorteoToken(rifaId, token);
        return;
      }
      
      // Evitar recargar la misma rifa cuando rifas se actualiza (evita bucle)
      if (selectedRifa?.id === rifaId) {
        setCurrentPage('rifa-detail');
        return;
      }
      
      setRifaIdFromUrl(rifaId);
      
      // Mostrar página de carga inmediatamente
      setCurrentPage('rifa-loading');
      setIsLoadingRifa(true);
      
      // Cargar la rifa desde Firestore para tener datos actualizados
      const timeoutId = setTimeout(() => {
        console.log('⏰ Timeout alcanzado, redirigiendo al inicio');
        setIsLoadingRifa(false);
        navigateTo('/inicio');
      }, 10000);
      
      loadRifaByIdFromFirestore(rifaId).then(loadedRifa => {
        clearTimeout(timeoutId);
        setIsLoadingRifa(false);
        if (loadedRifa) {
          setSelectedRifa(loadedRifa);
          setCurrentPage('rifa-detail');
          // Actualizar la rifa en el estado para que el resto de la app use datos frescos
          setRifas(prevRifas => {
            const idx = prevRifas.findIndex(r => r.id === loadedRifa.id);
            if (idx >= 0) {
              const next = [...prevRifas];
              next[idx] = loadedRifa;
              return next;
            }
            return [...prevRifas, loadedRifa];
          });
        } else {
          console.log('❌ Rifa no encontrada, redirigiendo al inicio');
          navigateTo('/inicio');
        }
      }).catch(error => {
        clearTimeout(timeoutId);
        setIsLoadingRifa(false);
        console.error('Error al cargar rifa específica:', error);
        navigateTo('/inicio');
      });
    } else if (path.startsWith('/sorteo/') && path.endsWith('/qr')) {
      // Link fijo del QR - generar nuevo token y redirigir
      const rifaId = path.split('/')[2];
      generarTokenYRedirigir(rifaId);
      return;
    } else if (path === '/datos-personales') {
      setCurrentPage('datos-personales');
    } else if (path === '/confirmar-compra') {
        setCurrentPage('payment');
    } else if (path === '/mis-rifas') {
      setCurrentPage('mis-rifas');
      // Refrescar rifas desde Firestore para ver números pendientes y datos actualizados (igual que en la web)
      loadRifasFromFirestore().then(rifasData => {
        setRifas(rifasData);
      }).catch(() => {});
    } else if (path === '/admin') {
      if (isAdmin()) {
        setCurrentPage('admin-panel');
      } else {
        alert('❌ No tienes permisos de administrador');
        navigateTo('/inicio');
      }
    } else if (path === '/todas-las-rifas') {
      if (isAdmin()) {
        setCurrentPage('todas-las-rifas');
        } else {
        alert('❌ No tienes permisos de administrador');
        navigateTo('/inicio');
        }
    } else if (path === '/gestionar-rifas') {
      if (user) {
        setCurrentPage('gestionar-rifas');
      } else {
        alert('❌ Debes iniciar sesión para gestionar rifas');
        navigateTo('/inicio');
      }
    } else if (path === '/ganadores') {
      setCurrentPage('ganadores');
    } else if (path === '/login') {
      // Si ya hay sesión Firebase, no mostrar el modal otra vez (la URL /login + efecto al actualizar rifas lo reabrían)
      if (auth.currentUser) {
        setShowLogin(false);
        window.history.replaceState({}, '', '/inicio');
        setCurrentPage('home');
      } else {
        setShowLogin(true);
      }
    } else if (path === '/registro') {
      if (auth.currentUser) {
        setShowRegister(false);
        window.history.replaceState({}, '', '/inicio');
        setCurrentPage('home');
      } else {
        setShowRegister(true);
      }
    } else if (path === '/crear-rifa') {
      // Nunca abrir el modal por URL: al F5 o enlace guardado no debe reaparecer el formulario.
      // Solo el botón "Crear rifa" abre el modal (openCrearRifaModal).
      setShowCreateRifa(false);
      if (typeof window !== 'undefined' && window.location.pathname === '/crear-rifa') {
        window.history.replaceState({}, '', '/inicio');
      }
      setCurrentPage('home');
    } else if (path === '/habilitar-rifa') {
      // Verificar si el usuario ya aceptó las condiciones
      const condicionesAceptadas = localStorage.getItem('condiciones_habilitar_rifa');
      if (!condicionesAceptadas) {
        setShowCondicionesModal(true);
      }
      setCurrentPage('habilitar-rifa');
    } else {
      navigateTo('/inicio');
    }
  };

  // Escuchar cambios en la URL (rifas: al cargar/actualizar datos vuelve a aplicar la ruta actual)
  useEffect(() => {
    handleRouteChange();
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [rifas]);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '' });
  const [showTransferenciaModal, setShowTransferenciaModal] = useState(false);
  const [showDatosModal, setShowDatosModal] = useState(false);
  const [rifaForm, setRifaForm] = useState({
    tipo: 'rifa', // 'rifa', 'rapadita' o 'sorteo'
    titulo: '',
    descripcion: '',
    premios: ['', ''], // Array de premios (mínimo 1, por defecto 2)
    precio: '',
    totalNumeros: '',
    fechaSorteo: '',
    tipoFinalizacion: 'fecha', // 'fecha' o 'venta_completa'
    alias: '',
    cbu: '',
    whatsapp: '',
    emailVendedor: '', // Correo donde recibir avisos cuando reserven números (pago pendiente)
    usarMercadoPago: false, // Checkbox para habilitar MercadoPago
    linkMercadoPago: '' // Link de pago de MercadoPago
  });
  const [buyerForm, setBuyerForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    direccion: ''
  });

  // Funciones para manejar múltiples premios
  const addPremio = () => {
    setRifaForm({
      ...rifaForm,
      premios: [...rifaForm.premios, '']
    });
    setImagenesPremios(prev => [...prev, null]);
  };

  const removePremio = (index) => {
    const newPremios = rifaForm.premios.filter((_, i) => i !== index);
    if (newPremios.length > 0) {
      setRifaForm({
        ...rifaForm,
        premios: newPremios
      });
      setImagenesPremios(prev => {
        const item = prev[index];
        if (item?.previewUrl) {
          try { URL.revokeObjectURL(item.previewUrl); } catch (_) {}
        }
        return prev.filter((_, i) => i !== index);
      });
    }
  };

  const updatePremio = (index, value) => {
    const newPremios = [...rifaForm.premios];
    newPremios[index] = value;
    setRifaForm({
      ...rifaForm,
      premios: newPremios
    });
  };

  // Funciones para editar premios de rifas existentes
  const handleEditarPremios = (rifa) => {
    const premios = rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : []);
    setPremiosEditando([...premios]);
    setSelectedRifa(rifa);
    setShowEditarPremiosModal(true);
  };

  const updatePremioEditando = (index, value) => {
    const newPremios = [...premiosEditando];
    newPremios[index] = value;
    setPremiosEditando(newPremios);
  };

  const addPremioEditando = () => {
    setPremiosEditando([...premiosEditando, '']);
  };

  const removePremioEditando = (index) => {
    const newPremios = premiosEditando.filter((_, i) => i !== index);
    if (newPremios.length > 0) {
      setPremiosEditando(newPremios);
    }
  };

  const guardarPremiosEditados = async () => {
    try {
      if (!selectedRifa) return;

      // Validar que al menos haya un premio
      const premiosValidos = premiosEditando.filter(p => p.trim() !== '');
      if (premiosValidos.length === 0) {
        alert('Por favor agrega al menos un premio');
        return;
      }

      // Actualizar la rifa en Firestore
      const rifaRef = doc(db, 'rifas', selectedRifa.id);
      await updateDoc(rifaRef, {
        premios: premiosValidos,
        premio: premiosValidos.join(', '), // Mantener compatibilidad
        fechaModificacion: new Date().toISOString()
      });

      // Actualizar el estado local
      const rifasActualizadas = rifas.map(r => {
        if (r.id === selectedRifa.id) {
          return {
            ...r,
            premios: premiosValidos,
            premio: premiosValidos.join(', ')
          };
        }
        return r;
      });
      setRifas(rifasActualizadas);

      // Actualizar selectedRifa si está seleccionada
      if (selectedRifa.id === selectedRifa?.id) {
        setSelectedRifa({
          ...selectedRifa,
          premios: premiosValidos,
          premio: premiosValidos.join(', ')
        });
      }

      alert('✅ Premios actualizados exitosamente');
      setShowEditarPremiosModal(false);
      setPremiosEditando([]);
      setSelectedRifa(null);
    } catch (error) {
      console.error('Error al actualizar premios:', error);
      alert('Error al actualizar los premios. Por favor intenta de nuevo.');
    }
  };

  const handleAbrirEditarRifa = (rifa) => {
    if (!esCreadorDeRifa(rifa, user)) {
      alert('Solo el creador de la rifa puede editar estos datos.');
      return;
    }
    setRifaEdicionForm({
      rifaId: rifa.id,
      tipo: rifa.tipo || 'rifa',
      titulo: rifa.titulo || '',
      descripcion: rifa.descripcion || '',
      alias: rifa.alias || '',
      linkMercadoPago: rifa.linkMercadoPago || ''
    });
    setShowEditarRifaModal(true);
  };

  const handleGuardarEdicionRifa = async () => {
    const titulo = rifaEdicionForm.titulo.trim();
    const descripcion = rifaEdicionForm.descripcion.trim();
    const alias = rifaEdicionForm.alias.trim();
    const linkMp = rifaEdicionForm.linkMercadoPago.trim();

    if (!titulo || !descripcion) {
      alert('El nombre y la descripción son obligatorios.');
      return;
    }

    if (linkMp && !/^https?:\/\//i.test(linkMp)) {
      alert('El link de Mercado Pago debe comenzar con http:// o https://');
      return;
    }

    setGuardandoEdicionRifa(true);
    try {
      const updateData = {
        titulo,
        descripcion,
        fechaModificacion: new Date().toISOString()
      };

      if (rifaEdicionForm.tipo !== 'sorteo') {
        updateData.alias = alias;
        updateData.linkMercadoPago = linkMp;
        updateData.usarMercadoPago = !!linkMp;
      }

      await updateRifaInFirestore(rifaEdicionForm.rifaId, updateData);

      const aplicar = (r) =>
        r.id === rifaEdicionForm.rifaId ? { ...r, ...updateData } : r;

      setRifas((prev) => prev.map(aplicar));
      if (selectedRifa?.id === rifaEdicionForm.rifaId) {
        setSelectedRifa((prev) => (prev ? { ...prev, ...updateData } : prev));
      }

      alert('✅ Datos de la rifa actualizados correctamente.');
      setShowEditarRifaModal(false);
    } catch (error) {
      console.error('Error al editar rifa:', error);
      alert('No se pudieron guardar los cambios. Intentá de nuevo.');
    } finally {
      setGuardandoEdicionRifa(false);
    }
  };

  // Funciones para las instrucciones de compra
  const iniciarInstrucciones = () => {
    setShowInstrucciones(true);
    setPasoActual(0);
  };

  const siguientePaso = () => {
    setPasoActual(prev => prev + 1);
  };

  const anteriorPaso = () => {
    setPasoActual(prev => prev - 1);
  };

  const cerrarInstrucciones = () => {
    setShowInstrucciones(false);
    setPasoActual(0);
  };

  const pasosInstrucciones = [
    {
      titulo: "🎯 Paso 1: Selecciona tus números",
      descripcion: "Haz clic en los números que quieres comprar. Verás que se marcan en naranja cuando los selecciones. Una vez que hayas elegido todos tus números, procede al pago haciendo clic en el botón 'Proceder al Pago' que aparece abajo.",
      icono: "🎯"
    },
    {
      titulo: "📝 Paso 2: Completa tus datos",
      descripcion: "Ingresa tu nombre completo y dirección de email válida. Estos datos son obligatorios y se utilizarán para contactarte en caso de que resultes ganador del sorteo.",
      icono: "📝"
    },
    {
      titulo: "💳 Paso 3: Realiza el pago",
      descripcion: "Transfiere el monto total correspondiente usando el Alias o CBU que se muestra en pantalla. Es importante que guardes el comprobante de la transferencia para el siguiente paso.",
      icono: "💳"
    },
    {
      titulo: "✅ Paso 4: Confirma tu compra",
      descripcion: "Envía el comprobante de transferencia por WhatsApp al vendedor utilizando el número que aparece en la información de contacto. El vendedor verificará el pago y confirmará tu compra.",
      icono: "✅"
    },
    {
      titulo: "🎉 ¡Compra Confirmada!",
      descripcion: "Una vez que el vendedor confirme tu pago, tus números seleccionados aparecerán en color rojo, indicando que están oficialmente registrados y listos para participar en el sorteo.",
      icono: "🎉"
    }
  ];

  // Funciones para Firestore
  const saveUserToFirestore = async (userData) => {
    try {
      const docRef = await addDoc(collection(db, 'usuarios'), {
        ...userData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Usuario guardado en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      throw error;
    }
  };

  const saveRifaToFirestore = async (rifaData) => {
    try {
      const docRef = await addDoc(collection(db, 'rifas'), {
        ...rifaData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Rifa guardada en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar rifa:', error);
      throw error;
    }
  };

  const loadUsersFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'usuarios'));
      const usersData = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const firestoreId = docSnap.id;
        usersData.push({
          ...data,
          firestoreId,
          id: firestoreId,
          uid: data.uid || data.id || null,
        });
      });
      return usersData;
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      return [];
    }
  };

  /** Encuentra el documento real en usuarios (id de Firestore o búsqueda por uid). */
  const resolverDocUsuario = async (userRef) => {
    const u =
      typeof userRef === 'object' && userRef !== null
        ? userRef
        : users.find((x) => x.id === userRef || x.firestoreId === userRef || x.uid === userRef);

    const idsProbar = [...new Set([u?.firestoreId, u?.id, typeof userRef === 'string' ? userRef : null, u?.uid].filter(Boolean))];

    for (const docId of idsProbar) {
      const ref = doc(db, 'usuarios', docId);
      const snap = await getDoc(ref);
      if (snap.exists()) return { ref, id: snap.id, data: snap.data() };
    }

    for (const val of idsProbar) {
      for (const campo of ['uid', 'id']) {
        const q = query(collection(db, 'usuarios'), where(campo, '==', val), limit(1));
        const qs = await getDocs(q);
        if (!qs.empty) {
          const d = qs.docs[0];
          return { ref: doc(db, 'usuarios', d.id), id: d.id, data: d.data() };
        }
      }
    }
    return null;
  };

  const loadRifasFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'rifas'));
      const rifasData = [];
      const seenIds = new Set(); // Para evitar duplicados
      const ahora = Date.now();
      const TRES_DIAS_MS = 3 * 24 * 60 * 60 * 1000;

      querySnapshot.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          const data = doc.data();
          // Rifas finalizadas: solo mostrar hasta 3 días después de fechaFinalizacion; luego no aparecen
          if (data.finalizada && data.fechaFinalizacion) {
            const fechaFin = new Date(data.fechaFinalizacion).getTime();
            if (ahora - fechaFin > TRES_DIAS_MS) return; // No incluir: ya pasaron más de 3 días
          }
          const pendientes = normalizarNumerosLista(data.numerosPendientes);
          const rifa = { id: doc.id, ...data, numerosPendientes: pendientes };
          if (pendientes.length !== (data.numerosPendientes || []).length) {
            updateDoc(doc(db, 'rifas', doc.id), {
              numerosPendientes: pendientes,
              updatedAt: new Date().toISOString()
            }).catch(e => console.warn('No se pudo deduplicar pendientes en rifa', doc.id, e));
          }
          rifasData.push(rifa);
        }
      });

      if (import.meta.env.DEV) console.log(`📦 Rifas: ${rifasData.length}`);
      return rifasData;
    } catch (error) {
      console.error('Error al cargar rifas:', error);
      return [];
    }
  };

  const loadRifaByIdFromFirestore = async (rifaId) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      const rifaSnapshot = await getDoc(rifaRef);
      
      if (rifaSnapshot.exists()) {
        const data = rifaSnapshot.data();
        const pendientes = normalizarNumerosLista(data.numerosPendientes);
        const rifaData = {
          id: rifaSnapshot.id,
          ...data,
          numerosPendientes: pendientes
        };
        if (pendientes.length !== (data.numerosPendientes || []).length) {
          updateDoc(rifaRef, {
            numerosPendientes: pendientes,
            updatedAt: new Date().toISOString()
          }).catch(e => console.warn('No se pudo deduplicar pendientes', rifaId, e));
        }
        return rifaData;
      } else {
        console.log('❌ Rifa no encontrada en Firestore:', rifaId);
        return null;
      }
    } catch (error) {
      console.error('❌ Error al cargar rifa por ID:', error);
      return null;
    }
  };

  const loadComprasFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'compras'));
      const comprasData = [];
      querySnapshot.forEach((doc) => {
        comprasData.push({ id: doc.id, ...doc.data() });
      });
      if (import.meta.env.DEV) console.log(`🛒 Compras: ${comprasData.length}`);
      return comprasData;
    } catch (error) {
      console.error('Error al cargar compras:', error);
      return [];
    }
  };

  const saveCompraToFirestore = async (compraData) => {
    try {
      const docRef = await addDoc(collection(db, 'compras'), {
        ...compraData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Compra guardada en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar compra:', error);
      throw error;
    }
  };

  const updateRifaInFirestore = async (rifaId, updateData) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      
      await updateDoc(rifaRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      console.log('Rifa actualizada en Firestore:', rifaId);
    } catch (error) {
      console.error('Error al actualizar rifa:', error);
      throw error;
    }
  };

  const saveGanadorToFirestore = async (ganadorData) => {
    try {
      const docRef = await addDoc(collection(db, 'ganadores'), {
        ...ganadorData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('🎉 Ganador guardado en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar ganador:', error);
      throw error;
    }
  };

  const loadGanadoresFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'ganadores'));
      const ganadoresData = [];
      querySnapshot.forEach((doc) => {
        ganadoresData.push({ id: doc.id, ...doc.data() });
      });
      if (import.meta.env.DEV) console.log(`🏆 Ganadores: ${ganadoresData.length}`);
      return ganadoresData;
    } catch (error) {
      console.error('Error al cargar ganadores:', error);
      return [];
    }
  };

  // Funciones para eliminar datos de Firestore
  const deleteRifaFromFirestore = async (rifaId) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      await deleteDoc(rifaRef);
      console.log('🗑️ Rifa eliminada de Firestore:', rifaId);
    } catch (error) {
      console.error('Error al eliminar rifa:', error);
      throw error;
    }
  };

  const deleteGanadoresFromFirestore = async (rifaId) => {
    try {
      const querySnapshot = await getDocs(collection(db, 'ganadores'));
      const deletePromises = [];
      
      querySnapshot.forEach((doc) => {
        const ganador = doc.data();
        if (ganador.rifaId === rifaId) {
          deletePromises.push(deleteDoc(doc.ref));
        }
      });
      
      await Promise.all(deletePromises);
      console.log(`🗑️ Ganadores eliminados de Firestore para rifa: ${rifaId}`);
    } catch (error) {
      console.error('Error al eliminar ganadores:', error);
      throw error;
    }
  };

  const deleteGanadorFromFirestore = async (ganadorId) => {
    try {
      const ganadorRef = doc(db, 'ganadores', ganadorId);
      await deleteDoc(ganadorRef);
      console.log('🗑️ Ganador eliminado de Firestore:', ganadorId);
    } catch (error) {
      console.error('Error al eliminar ganador:', error);
      throw error;
    }
  };

  const deleteComprasFromFirestore = async (rifaId) => {
    try {
      const querySnapshot = await getDocs(collection(db, 'compras'));
      const deletePromises = [];
      
      querySnapshot.forEach((doc) => {
        const compra = doc.data();
        if (compra.rifaId === rifaId) {
          deletePromises.push(deleteDoc(doc.ref));
        }
      });
      
      await Promise.all(deletePromises);
      console.log(`🗑️ Compras eliminadas de Firestore para rifa: ${rifaId}`);
    } catch (error) {
      console.error('Error al eliminar compras:', error);
      throw error;
    }
  };

  // Función para limpiar todos los datos de una rifa
  const limpiarDatosDeRifa = async (rifaId) => {
    try {
      console.log('🧹 Iniciando limpieza de datos para rifa:', rifaId);

      // Eliminar imagen de Storage (si existe) ANTES de borrar la rifa para tener el path
      try {
        const rifaSnapshot = await getDoc(doc(db, 'rifas', rifaId));
        if (rifaSnapshot.exists()) {
          await eliminarImagenRifa({ id: rifaId, ...rifaSnapshot.data() });
        }
      } catch (e) {
        console.warn('No se pudo eliminar imagen de la rifa:', e.message);
      }

      // Eliminar rifa
      await deleteRifaFromFirestore(rifaId);
      
      // Eliminar ganadores
      await deleteGanadoresFromFirestore(rifaId);
      
      // Eliminar compras
      await deleteComprasFromFirestore(rifaId);
      
      // Actualizar estado local
      setRifas(rifas.filter(r => r.id !== rifaId));
      setGanadores(ganadores.filter(g => g.rifaId !== rifaId));
      setCompras(compras.filter(c => c.rifaId !== rifaId));
      
      console.log('✅ Datos de rifa eliminados exitosamente:', rifaId);
    } catch (error) {
      console.error('Error al limpiar datos de rifa:', error);
    }
  };

  const handleConfirmarPago = async (rifaId, numerosSolicitados) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      const rifaSnap = await getDoc(rifaRef);
      if (!rifaSnap.exists()) {
        alert('Rifa no encontrada');
        return;
      }
      const rifa = { id: rifaSnap.id, ...rifaSnap.data() };
      const pendientesEnRifa = (rifa.numerosPendientes || []).slice();
      const solicitud = Array.isArray(numerosSolicitados) && numerosSolicitados.length > 0
        ? numerosSolicitados
        : pendientesEnRifa;
      const numerosAConfirmar = [...new Set(solicitud.filter(n => pendientesEnRifa.includes(n)))].sort((a, b) => a - b);

      if (!numerosAConfirmar.length) {
        alert('No hay números pendientes válidos para confirmar. Actualizá la página para ver los últimos datos.');
        return;
      }

      const precioUnit = parseFloat(rifa.precio || 0);

      let todasLasCompras = [];
      try {
        const comprasQuery = query(collection(db, 'compras'), where('rifaId', '==', rifaId));
        const comprasSnap = await getDocs(comprasQuery);
        todasLasCompras = comprasSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error('Error al cargar compras:', e);
        todasLasCompras = compras.filter(c => c.rifaId === rifaId);
      }

      const comprasAfectadas = todasLasCompras.filter(
        c => c.estado === 'pendiente' && (c.numeros || []).some(n => numerosAConfirmar.includes(n))
      );

      if (!comprasAfectadas.length) {
        alert('No hay compras pendientes que coincidan con esos números.');
        return;
      }

      for (const compra of comprasAfectadas) {
        const nums = compra.numeros || [];
        const confirmarAhora = nums.filter(n => numerosAConfirmar.includes(n));
        if (!confirmarAhora.length) continue;

        const restantesCompra = nums.filter(n => !numerosAConfirmar.includes(n));
        const compraRef = doc(db, 'compras', compra.id);

        try {
          if (restantesCompra.length === 0) {
            await updateDoc(compraRef, {
              estado: 'confirmado',
              fechaConfirmacion: new Date().toISOString()
            });
            const compraSnapUpd = await getDoc(compraRef);
            const compraData = compraSnapUpd.exists() ? compraSnapUpd.data() : compra;
            const emailComprador = (compraData.compradorEmail || compraData.email || compra.compradorEmail || compra.email || '').trim();
            if (!emailComprador) {
              console.warn('⚠️ Compra sin email:', compra.id);
            } else {
              const numerosCompra = compraData.numeros || nums;
              const totalCompra = compraData.total != null ? compraData.total : (numerosCompra.length * precioUnit);
              await notificarPagoConfirmado({
                compradorNombre: compraData.compradorNombre || compra.compradorNombre || compraData.nombre || 'Cliente',
                compradorEmail: emailComprador,
                compraId: compra.id,
                numeros: numerosCompra,
                total: totalCompra,
                rifaTitulo: compraData.rifaTitulo || compra.rifaTitulo || rifa.titulo,
                rifaId: rifaId
              });
            }
          } else {
            // Confirmación parcial: crear nueva compra para los números confirmados
            // y mantener la original solo con los restantes (pendientes)
            const subtotalConfirmado = confirmarAhora.length * precioUnit;
            const nuevoTotal = restantesCompra.length * precioUnit;

            // 1. Crear nuevo documento de compra para la porción confirmada
            const nuevaCompraId = await addDoc(collection(db, 'compras'), {
              rifaId: compra.rifaId,
              rifaTitulo: compra.rifaTitulo || rifa.titulo,
              compradorNombre: compra.compradorNombre || 'Cliente',
              compradorEmail: compra.compradorEmail || '',
              compradorTelefono: compra.compradorTelefono || '',
              compradorDireccion: compra.compradorDireccion || '',
              numeros: [...confirmarAhora],
              total: subtotalConfirmado,
              fecha: compra.fecha || new Date().toISOString(),
              estado: 'confirmado',
              metodoPago: compra.metodoPago || 'transferencia',
              fechaConfirmacion: new Date().toISOString(),
              createdAt: new Date().toISOString()
            });

            // 2. Actualizar compra original con solo los números restantes
            await updateDoc(compraRef, {
              numeros: restantesCompra,
              total: nuevoTotal
            });

            const emailComprador = (compra.compradorEmail || compra.email || '').trim();
            if (emailComprador) {
              await notificarPagoConfirmado({
                compradorNombre: compra.compradorNombre || 'Cliente',
                compradorEmail: emailComprador,
                compraId: nuevaCompraId,
                numeros: confirmarAhora,
                total: subtotalConfirmado,
                rifaTitulo: compra.rifaTitulo || rifa.titulo,
                rifaId: rifaId
              });
            }

            // Guardar el ID de la nueva compra en el objeto para usarlo en setCompras
            compra._nuevaCompraId = nuevaCompraId;
            compra._confirmarAhora = confirmarAhora;
            compra._subtotalConfirmado = subtotalConfirmado;
          }
        } catch (error) {
          console.error('Error al confirmar compra o enviar aviso al comprador:', error);
        }
      }

      const aRemoverDePendientes = solicitud.filter(n => pendientesEnRifa.includes(n));
      const pendientesActualizados = quitarInstanciasPorLista(pendientesEnRifa, aRemoverDePendientes);
      const reservados = [...(rifa.numerosReservados || []), ...numerosAConfirmar];
      const vendidos = (rifa.numerosVendidos || 0) + numerosAConfirmar.length;

      await updateRifaInFirestore(rifaId, {
        numerosReservados: reservados,
        numerosPendientes: pendientesActualizados,
        numerosVendidos: vendidos
      });

      const rifaActualizada = { ...rifa, numerosReservados: reservados, numerosPendientes: pendientesActualizados, numerosVendidos: vendidos };
      setRifas(prev => prev.map(r => r.id === rifaId ? rifaActualizada : r));
      if (selectedRifa?.id === rifaId) {
        setSelectedRifa(rifaActualizada);
      }

      setCompras(prev => {
        let next = [...prev];
        for (const compra of comprasAfectadas) {
          const nums = compra.numeros || [];
          const confirmarAhora = nums.filter(n => numerosAConfirmar.includes(n));
          if (!confirmarAhora.length) continue;
          const restantesCompra = nums.filter(n => !numerosAConfirmar.includes(n));
          const idx = next.findIndex(c => c.id === compra.id);
          if (restantesCompra.length === 0) {
            const upd = { ...compra, estado: 'confirmado' };
            if (idx >= 0) next[idx] = upd;
            else next.push(upd);
          } else {
            // Confirmación parcial: actualizar compra original con restantes
            const upd = { ...compra, numeros: restantesCompra, total: restantesCompra.length * precioUnit };
            delete upd._nuevaCompraId;
            delete upd._confirmarAhora;
            delete upd._subtotalConfirmado;
            if (idx >= 0) next[idx] = upd;
            else next.push(upd);

            // Agregar nueva compra para los números confirmados
            if (compra._nuevaCompraId) {
              next.push({
                id: compra._nuevaCompraId,
                rifaId: compra.rifaId,
                rifaTitulo: compra.rifaTitulo || rifa.titulo,
                compradorNombre: compra.compradorNombre || 'Cliente',
                compradorEmail: compra.compradorEmail || '',
                compradorTelefono: compra.compradorTelefono || '',
                compradorDireccion: compra.compradorDireccion || '',
                numeros: compra._confirmarAhora || confirmarAhora,
                total: compra._subtotalConfirmado || (confirmarAhora.length * precioUnit),
                fecha: compra.fecha || new Date().toISOString(),
                estado: 'confirmado',
                metodoPago: compra.metodoPago || 'transferencia',
                fechaConfirmacion: new Date().toISOString()
              });
            }
          }
        }
        return next;
      });

      alert(
        `✅ Pago confirmado\n\nNúmeros: ${numerosAConfirmar.join(', ')}\n` +
        `Total ingresado: $${(numerosAConfirmar.length * precioUnit).toFixed(2)}\n\nSe notificó al comprador cuando había email.`
      );
    } catch (error) {
      console.error('Error al confirmar pago:', error);
      alert('Error al confirmar el pago. Por favor intenta de nuevo.');
    }
  };

  /** Solo el creador: quita un número de pendientes y lo deja disponible (verde). Actualiza o elimina la compra asociada. */
  const handleLiberarNumeroPendiente = async (rifaId, numero) => {
    try {
      if (!user) return;
      const rifaRef = doc(db, 'rifas', rifaId);
      const rifaSnap = await getDoc(rifaRef);
      if (!rifaSnap.exists()) {
        alert('Rifa no encontrada');
        return;
      }
      const rifa = { id: rifaSnap.id, ...rifaSnap.data() };
      const soyCreador = rifa.creadorId === user.id || rifa.creadorId === user.uid || rifa.creadorUid === user.uid;
      if (!soyCreador) {
        alert('Solo quien creó la rifa puede liberar números pendientes.');
        return;
      }
      const pendientes = rifa.numerosPendientes || [];
      if (!pendientes.includes(numero)) {
        alert('Ese número ya no está como pendiente.');
        return;
      }
      if (!confirm(`¿Liberar el número ${numero}?\n\nVolverá a estar disponible (verde) y se anula esa reserva pendiente.`)) {
        return;
      }

      const precioUnit = parseFloat(rifa.precio || 0);
      const nuevosPendientes = quitarInstanciasPorLista(pendientes, [numero]);
      await updateRifaInFirestore(rifaId, { numerosPendientes: nuevosPendientes });

      const comprasQuery = query(collection(db, 'compras'), where('rifaId', '==', rifaId));
      const comprasSnap = await getDocs(comprasQuery);
      let compraLiberada = false;
      for (const d of comprasSnap.docs) {
        if (compraLiberada) break;
        const compra = { id: d.id, ...d.data() };
        if (compra.estado !== 'pendiente') continue;
        const nums = compra.numeros || [];
        if (!nums.includes(numero)) continue;
        const restantes = nums.filter(n => n !== numero);
        const cref = doc(db, 'compras', compra.id);
        if (restantes.length === 0) {
          await deleteDoc(cref);
        } else {
          await updateDoc(cref, {
            numeros: restantes,
            total: restantes.length * precioUnit
          });
        }
        compraLiberada = true;
      }

      const rifaUpd = { ...rifa, numerosPendientes: nuevosPendientes };
      setRifas(prev => prev.map(r => r.id === rifaId ? rifaUpd : r));
      if (selectedRifa?.id === rifaId) {
        setSelectedRifa(rifaUpd);
      }
      setCompras(prev => {
        const out = [];
        for (const c of prev) {
          if (c.rifaId !== rifaId || c.estado !== 'pendiente') {
            out.push(c);
            continue;
          }
          const nums = c.numeros || [];
          if (!nums.includes(numero)) {
            out.push(c);
            continue;
          }
          const restantes = nums.filter(n => n !== numero);
          if (restantes.length === 0) continue;
          out.push({ ...c, numeros: restantes, total: restantes.length * precioUnit });
        }
        return out;
      });

      alert(`✅ Número ${numero} liberado: ya está disponible para comprar de nuevo.`);
    } catch (e) {
      console.error('handleLiberarNumeroPendiente:', e);
      alert('No se pudo liberar el número. Intentá de nuevo.');
    }
  };



  const handleEliminarRifa = async (rifaId) => {
    try {
      const rifa = rifas.find(r => r.id === rifaId);
      if (!rifa) {
        alert('Rifa no encontrada');
        return;
      }

      // SOLO EL ADMIN PUEDE ELIMINAR RIFAS
      if (!isAdmin()) {
        alert('❌ SOLO EL ADMINISTRADOR PUEDE ELIMINAR RIFAS\n\nContacta al administrador si necesitas eliminar una rifa.');
        return;
      }

      // Verificar si hay números vendidos o pendientes
      const tieneVentas = rifa.numerosVendidos > 0 || (rifa.numerosPendientes?.length || 0) > 0;
      
      if (tieneVentas) {
        const confirmacion = confirm(
          `⚠️ ADVERTENCIA: Esta rifa tiene ${rifa.numerosVendidos} números vendidos y ${rifa.numerosPendientes?.length || 0} números pendientes.\n\n` +
          `¿Estás seguro de que quieres eliminar la rifa?\n\n` +
          `Esta acción no se puede deshacer.`
        );
        
        if (!confirmacion) {
          return;
        }
      } else {
        const confirmacion = confirm(
          `¿Estás seguro de que quieres eliminar la rifa "${rifa.titulo}"?\n\n` +
          `Esta acción no se puede deshacer.`
        );
        
        if (!confirmacion) {
          return;
        }
      }

      // Eliminar de Firestore usando la función de limpieza completa
      await limpiarDatosDeRifa(rifaId);
      
      // Si estamos viendo la rifa eliminada, volver a mis rifas
      if (selectedRifa?.id === rifaId) {
        navigateTo('/mis-rifas');
      }
      
      alert('✅ Rifa y todos sus datos (ganadores, compradores) eliminados exitosamente');
      
    } catch (error) {
      console.error('Error al eliminar rifa:', error);
      alert('Error al eliminar la rifa. Por favor intenta de nuevo.');
    }
  };

  // Funciones para el sistema de administración
  const isAdmin = () => {
    const adminByUid = user?.uid === 'Trz2xMBhVjc9pnOOGkTPegeNGGD2';
    const adminByEmail = user?.email === 'luxioage@gmail.com';
    return adminByUid || adminByEmail;
  };

  const canCreateRifa = () => {
    if (!user) return false;
    if (isAdmin()) return true; // Solo el admin puede crear rifas ilimitadas
    
    // Verificar si el usuario tiene rifas permitidas (solo usuarios aprobados)
    // Buscar por ID, UID o email para mayor compatibilidad
    const userData = users.find(u => 
      u.id === user.id || 
      u.uid === user.uid || 
      u.email === user.email
    );
    
    console.log('🔍 canCreateRifa check:', {
      user: user,
      userData: userData,
      hasUserData: !!userData,
      pagoRealizado: userData?.pagoRealizado,
      rifasPermitidas: userData?.rifasPermitidas
    });
    
    // Si no existe el usuario en la base de datos (usuario nuevo), NO puede crear rifas
    if (!userData) {
      console.log('❌ Usuario no encontrado en la base de datos - NO puede crear rifas');
      return false;
    }
    
    // El usuario debe estar aprobado (pagoRealizado = true)
    if (!userData.pagoRealizado) {
      console.log('❌ Usuario NO aprobado - NO puede crear rifas');
      return false;
    }
    
    // El usuario debe tener rifas permitidas > 0
    if (!userData.rifasPermitidas || userData.rifasPermitidas <= 0) {
      console.log('❌ Usuario no tiene rifas permitidas - NO puede crear rifas');
      return false;
    }
    
    // Contar cuántas rifas ha creado el usuario
    const rifasCreadas = rifas.filter(r => r.creadorId === user.id).length;
    
    // Verificar si ha alcanzado el límite
    const puedeCrear = rifasCreadas < userData.rifasPermitidas;
    
    console.log('📊 Validación detallada:', {
      rifasCreadas,
      rifasPermitidas: userData.rifasPermitidas,
      comparacion: `${rifasCreadas} < ${userData.rifasPermitidas} = ${puedeCrear}`,
      puedeCrear,
      mensaje: puedeCrear ? '✅ Puede crear rifas' : `❌ Ya alcanzó el límite de ${userData.rifasPermitidas} rifas`
    });
    
    return puedeCrear;
  };

  const handleApproveUser = async (userRef, rifasPermitidas) => {
    try {
      if (rifasPermitidas == null || rifasPermitidas === undefined) {
        alert('❌ Cantidad de rifas no válida');
        return;
      }

      const resolved = await resolverDocUsuario(userRef);
      if (!resolved) {
        alert('❌ No se encontró el usuario en Firestore');
        return;
      }

      await updateDoc(resolved.ref, {
        rifasPermitidas: rifasPermitidas,
        pagoRealizado: true,
        solicitudPendiente: false,
        fechaPago: new Date().toISOString(),
        ultimoAcceso: new Date().toISOString(),
        uid: resolved.data.uid || resolved.data.id || null,
      });

      const docId = resolved.id;
      const updatedUsers = users.map((u) => {
        if (u.id === docId || u.firestoreId === docId || u.uid === resolved.data?.uid) {
          return {
            ...u,
            id: docId,
            firestoreId: docId,
            rifasPermitidas: rifasPermitidas,
            pagoRealizado: true,
            solicitudPendiente: false,
            fechaPago: new Date().toISOString(),
          };
        }
        return u;
      });
      setUsers(updatedUsers);

      alert(`✅ Usuario aprobado exitosamente\n\nRifas permitidas: ${rifasPermitidas}`);
    } catch (error) {
      console.error('Error al aprobar usuario:', error);
      alert('Error al aprobar usuario. Por favor intenta de nuevo.');
    }
  };

  const handleRejectUser = async (userRef) => {
    try {
      if (!confirm('¿Estás seguro de que quieres rechazar este usuario?')) return;

      const resolved = await resolverDocUsuario(userRef);
      if (!resolved) {
        alert('❌ No se encontró el usuario en Firestore');
        return;
      }

      await deleteDoc(resolved.ref);

      const docId = resolved.id;
      setUsers(users.filter((u) => u.id !== docId && u.firestoreId !== docId && u.uid !== resolved.data?.uid));

      alert('✅ Usuario rechazado');
    } catch (error) {
      console.error('Error al rechazar usuario:', error);
      alert('Error al rechazar usuario. Por favor intenta de nuevo.');
    }
  };

  // Cargar datos desde Firestore y verificar sesión persistente
  useEffect(() => {
    const loadData = async () => {
      // Listener para cambios en el estado de autenticación de Firebase
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          console.log('Usuario autenticado con Firebase:', firebaseUser);
          
          // Crear objeto de usuario para la aplicación
          const appUser = {
            id: firebaseUser.uid,
            uid: firebaseUser.uid, // Agregar uid explícitamente para isAdmin()
            name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
            email: firebaseUser.email,
            avatar: firebaseUser.photoURL,
            provider: 'google'
          };
          
          setUser(appUser);
          localStorage.setItem('acceso7_user', JSON.stringify(appUser));
      } else {
          console.log('Usuario no autenticado');
          setUser(null);
          localStorage.removeItem('acceso7_user');
        }
      });

      // Verificar si hay una sesión guardada (fallback)
      const savedUser = localStorage.getItem('acceso7_user');
      if (savedUser && !user) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error al cargar usuario guardado:', error);
          localStorage.removeItem('acceso7_user');
        }
      }

      try {
        // Cargar usuarios desde Firestore
        const usersData = await loadUsersFromFirestore();
        setUsers(usersData);
        
        const rifasData = await loadRifasFromFirestore();
        setRifas(rifasData);
        
        const comprasData = await loadComprasFromFirestore();
        setCompras(comprasData);
        
        const ganadoresData = await loadGanadoresFromFirestore();
        setGanadores(ganadoresData);
        
        if (import.meta.env.DEV) {
          console.log('Datos Firestore:', { users: usersData.length, rifas: rifasData.length, compras: comprasData.length, ganadores: ganadoresData.length });
        }
        
        // Después de cargar los datos, manejar la ruta actual
        handleRouteChange();
      } catch (error) {
        console.error('Error al cargar datos desde Firestore:', error);
        
        // Si Firestore falla, mantener arrays vacíos
        console.log('Firestore no disponible, usando arrays vacíos');
        setUsers([]);
        setRifas([]);
        setCompras([]);
        
        // Aún así, manejar la ruta actual
        handleRouteChange();
      }
    };

    loadData();
  }, []);

  // Refrescar rifas cuando el usuario vuelve a la app (para ver números pendientes actualizados, igual que en la web)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadRifasFromFirestore().then(rifasData => setRifas(rifasData)).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // DESHABILITADO: No eliminar datos de rifas finalizadas automáticamente
  // Los ganadores deben permanecer permanentemente para evitar que se eliminen por error
  // useEffect para limpiar datos de rifas finalizadas después de 48 horas
  // useEffect(() => {
  //   const checkAndCleanExpiredRifas = async () => {
  //     const ahora = new Date();
  //     
  //     rifas.forEach(rifa => {
  //       // Solo procesar rifas finalizadas
  //       if (rifa.finalizada && rifa.ganadorId) {
  //         // Buscar el ganador para obtener la fecha de sorteo
  //         const ganador = ganadores.find(g => g.rifaId === rifa.id);
  //         
  //         if (ganador && ganador.fechaSorteo) {
  //           const fechaSorteo = new Date(ganador.fechaSorteo);
  //           const horasTranscurridas = (ahora - fechaSorteo) / (1000 * 60 * 60);
  //           
  //           console.log(`🕐 Rifa ${rifa.titulo}: ${horasTranscurridas.toFixed(2)} horas transcurridas desde finalización`);
  //           
  //           // Si han pasado más de 48 horas, limpiar los datos
  //           if (horasTranscurridas >= 48) {
  //             console.log(`🗑️ Limpiando datos de rifa finalizada: ${rifa.titulo}`);
  //             limpiarDatosDeRifa(rifa.id);
  //           }
  //         }
  //       }
  //     });
  //   };

  //   // Verificar cada minuto
  //   const interval = setInterval(checkAndCleanExpiredRifas, 60000);
  //   
  //   // Ejecutar inmediatamente
  //   checkAndCleanExpiredRifas();
  //   
  //   return () => clearInterval(interval);
  // }, [rifas, ganadores]);

  const handleRegister = async () => {
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      alert('Por favor completa todos los campos');
      return;
    }
    
    try {
      // Verificar si el email ya existe
      const existingUser = users.find(u => u.email === registerForm.email);
      if (existingUser) {
        alert('Este email ya está registrado. Por favor usa otro email o inicia sesión.');
      return;
    }

      const newUser = {
        name: registerForm.name,
        email: registerForm.email,
        password: registerForm.password
      };

      // Guardar en Firestore
      const userId = await saveUserToFirestore(newUser);
      const userWithId = { id: userId, ...newUser };

      // Actualizar estado local
      setUsers([...users, userWithId]);
      
      // Auto-login después del registro
      setUser(userWithId);
      localStorage.setItem('acceso7_user', JSON.stringify(userWithId));
      
      setRegisterForm({ name: '', email: '', password: '' });
      setShowRegister(false);
      alert(`¡Registro exitoso! Bienvenido ${newUser.name}`);
    } catch (error) {
      console.error('Error en el registro:', error);
      alert('Error al registrar usuario. Por favor intenta de nuevo.');
    }
  };

  const handleLogin = () => {
    const foundUser = users.find(u => u.email === loginForm.email && u.password === loginForm.password);
    if (foundUser) {
      setUser(foundUser);
      // Guardar sesión en localStorage
      localStorage.setItem('acceso7_user', JSON.stringify(foundUser));
      setLoginForm({ email: '', password: '' });
      setShowLogin(false);
      if (typeof window !== 'undefined' && window.location.pathname === '/login') {
        window.history.replaceState({}, '', '/inicio');
        setCurrentPage('home');
      }
      alert(`¡Bienvenido ${foundUser.name}!`);
    } else {
      alert('Credenciales incorrectas');
    }
  };

  const handleLogout = async () => {
    try {
      // Cerrar sesión en Firebase Auth
      await signOut(auth);
      
      setUser(null);
      localStorage.removeItem('acceso7_user');
      setCurrentPage('home');
      alert('Sesión cerrada correctamente');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Fallback: limpiar estado local
      setUser(null);
      localStorage.removeItem('acceso7_user');
      setCurrentPage('home');
      alert('Sesión cerrada correctamente');
    }
  };

  const handleGoogleLogin = async () => {
    try {
      console.log('🔵 Iniciando login con Google...');
      
      // Detectar si estamos en la app nativa de Capacitor
      const isNative = Capacitor.isNativePlatform();
      const platform = Capacitor.getPlatform();
      
      let result;
      
      if (isNative && platform === 'android') {
        // En Android, intentar usar el plugin nativo de Google primero
        console.log('✅ Dispositivo Android NATIVO detectado, intentando Google Auth nativo...');
        
        let nativeAuthFailed = false;
        let googleError = null;
        
        const isCancelError = (err) => {
          const c = err?.code;
          const m = (err?.message || '').toLowerCase();
          return c === 12501 || c === '12501' || m.includes('canceled') || m.includes('cancelled');
        };
        const doNativeSignIn = async () => {
          if (!GoogleAuth) throw { message: 'GoogleAuth plugin no disponible', code: 'PLUGIN_NOT_AVAILABLE' };
          await GoogleAuth.initialize({
            clientId: '743099229873-8e7b8va87adt8sn80vhjjhqovbltvd5b.apps.googleusercontent.com',
            scopes: ['profile', 'email'],
            grantOfflineAccess: true,
          });
          const googleUser = await GoogleAuth.signIn();
          if (!googleUser?.authentication?.idToken) throw { message: 'Respuesta inválida de Google Auth', code: 'INVALID_RESPONSE' };
          const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
          return await signInWithCredential(auth, credential);
        };
        try {
          result = await doNativeSignIn();
        } catch (firstError) {
          googleError = firstError;
          if (isCancelError(firstError)) {
            console.log('🔄 Primer intento 12501/cancelado, reintentando en 400ms...');
            try {
              await new Promise(r => setTimeout(r, 400));
              result = await doNativeSignIn();
              nativeAuthFailed = false;
            } catch (retryError) {
              googleError = retryError;
              nativeAuthFailed = true;
            }
          } else {
            nativeAuthFailed = true;
          }
        }
        if (nativeAuthFailed && googleError && (googleError.code === '10' || googleError.code === 10 || (googleError.message || '').toLowerCase().includes('developer_error'))) {
          console.warn('💡 SOLUCIÓN: Agrega el SHA-1 en Firebase Console.');
        }
        
        // Si el método nativo falló, mostrar error con instrucciones
        if (nativeAuthFailed) {
          const errorCode = googleError?.code;
          const errorMessage = googleError?.message || 'Error desconocido';
          
          // Registrar contexto adicional pero mostrar un mensaje genérico
          if (errorCode === '10' || errorCode === 10 ||
              (errorMessage || '').toLowerCase().includes('developer_error') ||
              (errorMessage || '').toLowerCase().includes('oauth2.0') ||
              (errorMessage || '').toLowerCase().includes('not registered')) {
            console.warn('ℹ️ Revisa la configuración OAuth en Firebase para com.acceso7.app y el SHA-1 registrado.');
          }
          if (isCancelError(googleError)) {
            alert('No se completó el inicio de sesión.\n\nToca "Continuar con Google" de nuevo.');
            return;
          }
          alert(`⚠️ Error al iniciar sesión con Google\n\nError: ${errorMessage}\nCódigo: ${errorCode || 'N/A'}\n\nSOLUCIÓN:\n1. Verifica la configuración en Firebase Console\n2. Asegúrate de que el SHA-1 de com.acceso7.app esté registrado\n3. Descarga el google-services.json actualizado\n4. Consulta: SOLUCION_ERROR_OAUTH2.md`);
          return;
        }
      } else {
        // En escritorio o web, usar popup
        result = await signInWithPopup(auth, googleProvider);
      }
      
      const user = result.user;
      console.log('✅ Usuario autenticado con Google:', user.email);
      
      const appUser = {
        id: user.uid,
        uid: user.uid,
        name: user.displayName || user.email.split('@')[0],
        email: user.email,
        avatar: user.photoURL,
        provider: 'google'
      };
      
      const existingUser = users.find(u => u.email === user.email);
      
      if (!existingUser) {
        const newUserData = {
          uid: user.uid,
          nombre: user.displayName || user.email.split('@')[0],
          username: user.displayName || user.email.split('@')[0],
          email: user.email,
          provider: 'google.com',
          rol: 'Participante',
          rifasPermitidas: 0,
          pagoRealizado: false,
          fechaRegistro: new Date().toISOString(),
          ultimoAcceso: new Date().toISOString(),
          telefono: '',
          tipo: 'email'
        };
        
        const userId = await saveUserToFirestore(newUserData);
        const userWithId = { id: userId, ...newUserData };
        appUser.id = userId;
        setUsers([...users, userWithId]);
      } else {
        appUser.id = existingUser.id;
      }
      
      setUser(appUser);
      localStorage.setItem('acceso7_user', JSON.stringify(appUser));
      setShowLogin(false);
      if (typeof window !== 'undefined' && window.location.pathname === '/login') {
        window.history.replaceState({}, '', '/inicio');
        setCurrentPage('home');
      }
      alert(`¡Bienvenido ${appUser.name}!`);
      
    } catch (error) {
      console.error('❌ Error en login con Google:', error);
      alert(`Error al iniciar sesión con Google.\n\n${error.message}`);
    }
  };


  // Función para generar token único
  const generateUniqueToken = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
  };

  // Función para validar token de sorteo
  const validateSorteoToken = async (rifaId, token) => {
    try {
      const rifa = await loadRifaByIdFromFirestore(rifaId);
      if (!rifa) {
        alert('❌ Rifa no encontrada');
        navigateTo('/inicio');
        return;
      }

      if (rifa.tipo !== 'sorteo') {
        alert('❌ Esta rifa no es un sorteo');
        navigateTo('/inicio');
        return;
      }

      // Verificar si el token es válido
      const rifaRef = doc(db, 'rifas', rifaId);
      const rifaSnap = await getDoc(rifaRef);
      const rifaData = rifaSnap.data();
      const tokensValidos = rifaData.tokensValidos || [];
      
      if (!tokensValidos.includes(token)) {
        alert('❌ Token inválido o ya utilizado. Este link ya fue usado.');
        navigateTo('/inicio');
        return;
      }

      // Token válido - INVALIDAR INMEDIATAMENTE al acceder (antes de mostrar la página)
      const tokensRestantes = tokensValidos.filter(t => t !== token);
      
      // Actualizar en Firestore removiendo el token usado INMEDIATAMENTE
      await updateDoc(rifaRef, {
        tokensValidos: tokensRestantes
      });
      
      // Esperar un momento para asegurar que la actualización se propague
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Verificar una vez más que el token fue invalidado
      const rifaSnapVerificacion = await getDoc(rifaRef);
      const rifaDataVerificacion = rifaSnapVerificacion.data();
      const tokensValidosVerificacion = rifaDataVerificacion.tokensValidos || [];
      
      if (tokensValidosVerificacion.includes(token)) {
        // Si todavía está en la lista, intentar invalidar de nuevo
        await updateDoc(rifaRef, {
          tokensValidos: tokensValidosVerificacion.filter(t => t !== token)
        });
      }
      
      // Token válido, permitir acceso (ya invalidado en Firestore)
      setSelectedRifa(rifa);
      setSorteoTokenValid(token);
      setCurrentPage('rifa-detail');
    } catch (error) {
      console.error('Error al validar token:', error);
      alert('❌ Error al validar el token');
      navigateTo('/inicio');
    }
  };

  // Función para invalidar token y generar uno nuevo
  const invalidateSorteoToken = async (rifaId, tokenUsado) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      const rifaSnap = await getDoc(rifaRef);
      
      if (!rifaSnap.exists()) {
        console.error('Rifa no encontrada');
        return null;
      }

      const rifa = rifaSnap.data();
      const tokensValidos = rifa.tokensValidos || [];
      
      // Remover token usado
      const nuevosTokens = tokensValidos.filter(t => t !== tokenUsado);
      
      // Generar nuevo token
      const nuevoToken = generateUniqueToken();
      nuevosTokens.push(nuevoToken);
      
      // Actualizar en Firestore
      await updateDoc(rifaRef, {
        tokensValidos: nuevosTokens
      });

      return nuevoToken;
    } catch (error) {
      console.error('Error al invalidar token:', error);
      return null;
    }
  };

  // Función para generar token y redirigir cuando se accede al QR fijo
  const generarTokenYRedirigir = async (rifaId) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      const rifaSnap = await getDoc(rifaRef);
      
      if (!rifaSnap.exists()) {
        alert('❌ Rifa no encontrada');
        navigateTo('/inicio');
        return;
      }

      const rifa = rifaSnap.data();
      
      if (rifa.tipo !== 'sorteo') {
        alert('❌ Esta rifa no es un sorteo');
        navigateTo('/inicio');
        return;
      }

      // Generar nuevo token único
      const nuevoToken = generateUniqueToken();
      const tokensValidos = rifa.tokensValidos || [];
      tokensValidos.push(nuevoToken);
      
      // Actualizar en Firestore
      await updateDoc(rifaRef, {
        tokensValidos: tokensValidos
      });

      // Redirigir con el nuevo token (este token funciona SOLO UNA VEZ)
      navigateTo(`/rifa/${rifaId}?token=${nuevoToken}`);
    } catch (error) {
      console.error('Error al generar token:', error);
      alert('❌ Error al generar token');
      navigateTo('/inicio');
    }
  };

  // Función para generar QR y mostrar modal - QR siempre apunta a link fijo
  const mostrarQRModal = async (rifaId) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      const rifaSnap = await getDoc(rifaRef);
      
      if (!rifaSnap.exists()) {
        alert('❌ Rifa no encontrada');
        return;
      }

      // El QR siempre apunta al mismo link fijo que genera tokens automáticamente
      const qrUrl = `${window.location.origin}/sorteo/${rifaId}/qr`;
      setCurrentQRUrl(qrUrl);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error al generar QR:', error);
      alert('❌ Error al generar QR');
    }
  };


  const handleCreateRifa = async () => {
    // Prevenir duplicados: si ya se está creando una rifa, no hacer nada
    if (isCreatingRifa) {
      console.log('⏳ Ya se está creando una rifa, ignorando click duplicado');
      return;
    }
    
    // Verificar que el usuario esté logueado para crear rifas
    if (!user) {
      alert('Debes tener una cuenta para crear rifas');
      setShowLogin(true);
      return;
    }
    
    // Verificar permisos para crear rifas
    if (!canCreateRifa()) {
      if (isAdmin()) {
        alert('❌ Error: No se pudo verificar permisos de administrador');
        return;
      } else {
        const userData = users.find(u => u.id === user.id);
        const rifasCreadas = rifas.filter(r => r.creadorId === user.id).length;
        const rifasPermitidas = userData?.rifasPermitidas || 0;
        
        if (userData && userData.pagoRealizado && rifasCreadas >= rifasPermitidas) {
          alert(`❌ Has alcanzado el límite de rifas permitidas\n\nRifas creadas: ${rifasCreadas}\nRifas permitidas: ${rifasPermitidas}\n\nContacta al administrador si necesitas crear más rifas.`);
        } else {
          alert('❌ No tienes permisos para crear rifas\n\nDebes ser aprobado por el administrador para crear rifas.\n\nContacta al administrador para más información.');
        }
        return;
      }
    }
    
    // Validar que al menos haya un premio
    const premiosValidos = rifaForm.premios.filter(p => p.trim() !== '');
    if (premiosValidos.length === 0) {
      alert('Por favor agrega al menos un premio');
      return;
    }
    
    // Validaciones diferentes según el tipo
    if (rifaForm.tipo === 'sorteo') {
      // Para sorteos, no se requiere precio, alias, cbu
      if (!rifaForm.titulo || !rifaForm.descripcion || !rifaForm.totalNumeros) {
        alert('Por favor completa todos los campos obligatorios');
        return;
      }
    } else {
      // Para rifas y rapaditas, se requiere todo
      if (!rifaForm.titulo || !rifaForm.descripcion || !rifaForm.precio || !rifaForm.totalNumeros) {
        alert('Por favor completa todos los campos obligatorios');
        return;
      }
      
      // Validar fecha solo si el tipo de finalización es por fecha
      if (rifaForm.tipoFinalizacion === 'fecha' && !rifaForm.fechaSorteo) {
        alert('Por favor selecciona una fecha de sorteo');
      return;
    }

    if (!rifaForm.alias && !rifaForm.cbu) {
      alert('Debes ingresar tu Alias o CBU para recibir transferencias');
      return;
      }
    }

    // Marcar que se está creando una rifa
    setIsCreatingRifa(true);

    try {
      console.log('=== CREANDO RIFA ===');
      console.log('Usuario actual:', user);
      console.log('Datos del formulario:', rifaForm);
      
      // Generar token inicial si es sorteo
      let tokensValidos = [];
      if (rifaForm.tipo === 'sorteo') {
        const tokenInicial = generateUniqueToken();
        tokensValidos = [tokenInicial];
      }
      
      // Email del vendedor: primero el que puso en el formulario, sino user.email o usuarios
      const emailDelForm = (rifaForm.emailVendedor && String(rifaForm.emailVendedor).trim()) ? String(rifaForm.emailVendedor).trim() : null;
      const creadorEmailVal = emailDelForm
        || ((user?.email && String(user.email).trim()) ? String(user.email).trim() : null)
        || (() => {
            const u = users.find(us => us.id === user?.id || us.uid === user?.uid);
            return (u?.email || u?.correo || u?.mail || '').trim() || null;
          })();
      const linkMp = (rifaForm.linkMercadoPago || '').trim();
      if (linkMp && !/^https?:\/\//i.test(linkMp)) {
        alert('El link de Mercado Pago debe comenzar con http:// o https://');
        setIsCreatingRifa(false);
        return;
      }

      const newRifa = {
      ...rifaForm,
        linkMercadoPago: linkMp,
        usarMercadoPago: !!linkMp,
        premios: premiosValidos, // Solo guardar premios válidos
        premio: premiosValidos.join(', '), // Mantener compatibilidad con rifa.premio
        creadorId: user.id,
        creadorEmail: creadorEmailVal,
        creadorUid: user?.uid || user?.id || null,
        creadorNombre: user.name,
        numerosVendidos: 0,
        numerosReservados: [],
        numerosPendientes: [],
        fechaCreacion: new Date().toISOString(),
        whatsapp: rifaForm.whatsapp || '', // Asegurar que se guarde el WhatsApp
        tokensValidos: tokensValidos // Tokens válidos para sorteos
      };

      console.log('Nueva rifa a crear:', newRifa);

      // Guardar en Firestore
      const rifaId = await saveRifaToFirestore(newRifa);
      console.log('Rifa guardada en Firestore con ID:', rifaId);

      const tipoRifaCreada = rifaForm.tipo;

      // Copiar archivos ANTES de limpiar estado (subida después con el snapshot)
      const archivosPremioSnapshot = imagenesPremios.map((it) => (it?.file ? it.file : null));
      const tieneAlgunaImagen = archivosPremioSnapshot.some((f) => f);

      // Cerrar modal y limpiar formulario al instante (no esperar subida de imágenes)
      imagenesPremios.forEach((it) => {
        if (it?.previewUrl) {
          try { URL.revokeObjectURL(it.previewUrl); } catch (_) {}
        }
      });
      setImagenesPremios([null, null]);
      setRifaForm({
        tipo: 'rifa',
        titulo: '',
        descripcion: '',
        premios: ['', ''],
        precio: '',
        totalNumeros: '',
        fechaSorteo: '',
        tipoFinalizacion: 'fecha',
        alias: '',
        cbu: '',
        whatsapp: '',
        emailVendedor: '',
        usarMercadoPago: false,
        linkMercadoPago: ''
      });
      setShowCreateRifa(false);
      navigateTo('/inicio');

      let rifasData = await loadRifasFromFirestore();
      setRifas(rifasData);
      console.log('Rifas recargadas desde Firestore. Total:', rifasData.length);

      // Subir imágenes después (la rifa ya está en el listado)
      if (tieneAlgunaImagen) {
        setImagenUploading(true);
        try {
          const subidas = await Promise.all(
            archivosPremioSnapshot.map((file, idx) =>
              file
                ? subirImagenPremio(rifaId, file, idx).catch((err) => {
                    console.error(`Error subiendo imagen del premio ${idx + 1}:`, err);
                    return null;
                  })
                : Promise.resolve(null)
            )
          );
          const imagenesPremiosFirestore = subidas.map((s) => (s ? { url: s.url, path: s.path } : null));
          const recortado = imagenesPremiosFirestore.slice(0, premiosValidos.length);
          await updateDoc(doc(db, 'rifas', rifaId), {
            imagenesPremios: recortado,
            updatedAt: new Date().toISOString()
          });
          rifasData = await loadRifasFromFirestore();
          setRifas(rifasData);
        } catch (errImg) {
          console.error('Error al subir imágenes de premios:', errImg);
          alert('La rifa se creó, pero no se pudieron subir todas las imágenes de los premios.');
        } finally {
          setImagenUploading(false);
        }
      }

      setIsCreatingRifa(false);

      if (tipoRifaCreada === 'sorteo') {
        await mostrarQRModal(rifaId);
      } else {
        const rifaUrl = `${window.location.origin}/rifa/${rifaId}`;
        window.setTimeout(() => {
          const compartirRifa = window.confirm(
            `¡Rifa creada exitosamente!\n\n¿Quieres copiar la URL para compartir?\n\nURL: ${rifaUrl}`
          );
          if (compartirRifa) {
            (async () => {
              try {
                await navigator.clipboard.writeText(rifaUrl);
                alert('✅ URL copiada al portapapeles.\n\nPuedes compartir esta rifa con el enlace copiado.');
              } catch (error) {
                const textArea = document.createElement('textarea');
                textArea.value = rifaUrl;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                alert('✅ URL copiada al portapapeles.\n\nPuedes compartir esta rifa con el enlace copiado.');
              }
            })();
          }
        }, 150);
      }
    } catch (error) {
      console.error('Error al crear rifa:', error);
      setIsCreatingRifa(false); // Limpiar estado en caso de error
      alert('Error al crear la rifa. Por favor intenta de nuevo.');
    }
  };

  const handleVerRifa = (rifa) => {
    setSelectedRifa(rifa);
    setSelectedNumbers([]);
    navigateTo(`/rifa/${rifa.id}`);
  };

  const copiarTexto = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = texto;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const urlRifa = (rifa) => `${window.location.origin}/rifa/${rifa.id}`;

  const handleCompartirLinkRifa = async (rifa) => {
    const rifaUrl = urlRifa(rifa);
    const texto = `🎫 ${rifa.titulo}\nParticipá acá: ${rifaUrl}`;
    await copiarTexto(texto);
    alert(`✅ Enlace copiado al portapapeles:\n\n${rifaUrl}\n\nPegalo en WhatsApp o donde quieras compartir la rifa.`);
  };

  const handleDescargarImagenRifa = async (rifa) => {
    if (!esCreadorDeRifa(rifa, user)) {
      alert('Solo el creador de la rifa puede descargar la imagen del flyer.');
      return;
    }
    if (generandoFlyerId) return;
    setGenerandoFlyerId(rifa.id);
    try {
      const blob = await generarImagenCompartirRifa(rifa, ganadores);
      const res = await descargarOCompartirImagenRifa(blob, rifa);
      alert(`✅ Imagen descargada: ${res.nombre || 'rifa.png'}\n\nAdjuntala en WhatsApp para promocionar tu rifa.`);
    } catch (error) {
      console.error('Error al generar flyer:', error);
      alert(`No se pudo generar la imagen.\n\n${error.message || 'Intentá de nuevo en unos segundos.'}`);
    } finally {
      setGenerandoFlyerId(null);
    }
  };

  const renderBotonesCompartirRifa = (rifa, { compact = false, fullWidth = false } = {}) => {
    if (!MOSTRAR_COMPARTIR_FLYER) return null;
    const puedeDescargarImagen = esCreadorDeRifa(rifa, user);
    const generando = generandoFlyerId === rifa.id;
    const btn = compact
      ? 'px-3 py-2 sm:py-3 text-xs sm:text-sm'
      : 'px-4 py-2 sm:py-3 text-sm';
    const wrap = fullWidth ? 'flex flex-col sm:flex-row gap-2 w-full' : 'flex gap-2 flex-wrap';

    return (
      <div className={wrap}>
        <button
          type="button"
          onClick={() => handleCompartirLinkRifa(rifa)}
          className={`${btn} bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-semibold flex items-center justify-center gap-1.5 ${fullWidth ? 'flex-1' : ''}`}
          title="Copiar enlace de la rifa"
        >
          <Share2 className="w-4 h-4 shrink-0" />
          <span className={compact ? 'hidden sm:inline' : ''}>Compartir link</span>
          {compact && <span className="sm:hidden">Link</span>}
        </button>
        {puedeDescargarImagen && (
          <button
            type="button"
            onClick={() => handleDescargarImagenRifa(rifa)}
            disabled={generando}
            className={`${btn} bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg transition font-semibold flex items-center justify-center gap-1.5 ${fullWidth ? 'flex-1' : ''}`}
            title="Descargar imagen del flyer (solo creador)"
          >
            <ImageIcon className="w-4 h-4 shrink-0" />
            {generando ? 'Generando…' : compact ? (
              <>
                <span className="hidden sm:inline">Descargar imagen</span>
                <span className="sm:hidden">Imagen</span>
              </>
            ) : (
              'Descargar imagen'
            )}
          </button>
        )}
      </div>
    );
  };

  const handleToggleNumber = async (numero) => {
    const rifa = rifas.find(r => r.id === selectedRifa.id);
    
    // Si es sorteo con token, verificar que el token siga siendo válido ANTES de seleccionar
    if (rifa.tipo === 'sorteo' && sorteoTokenValid) {
      try {
        const rifaRef = doc(db, 'rifas', rifa.id);
        const rifaSnap = await getDoc(rifaRef);
        
        if (rifaSnap.exists()) {
          const rifaData = rifaSnap.data();
          const tokensValidos = rifaData.tokensValidos || [];
          
          // Si el token ya no está en la lista, fue invalidado
          if (!tokensValidos.includes(sorteoTokenValid)) {
            alert('❌ Este link ya fue usado. Por favor, escanea el QR nuevamente para obtener un link válido.');
            setSorteoTokenValid(null);
            setSelectedNumbers([]);
            navigateTo('/inicio');
            return;
          }
        }
      } catch (error) {
        console.error('Error al verificar token:', error);
        alert('❌ Error al verificar el link. Por favor, intenta de nuevo.');
        return;
      }
    }
    
    // Si el número está vendido, mostrar información del comprador
    if (rifa.numerosReservados.includes(numero)) {
      console.log('🔴 Número vendido clicado:', numero);
      console.log('🔍 Buscando comprador...');
      console.log('Rifa ID:', rifa.id);
      console.log('Compras disponibles:', compras.length);
      console.log('Compras:', compras);
      
      // Buscar comprador - buscar en todas las compras sin filtrar por estado
      let comprador = compras.find(c => 
        c.rifaId === rifa.id && 
        c.numeros && Array.isArray(c.numeros) && c.numeros.includes(numero)
      );
      
      // Si no se encuentra, buscar también por número como string
      if (!comprador) {
        comprador = compras.find(c => 
          c.rifaId === rifa.id && 
          c.numeros && Array.isArray(c.numeros) && c.numeros.includes(String(numero))
        );
      }
      
      console.log('🔍 Buscando comprador para número:', numero);
      console.log('📋 Rifa ID:', rifa.id);
      console.log('📦 Total compras:', compras.length);
      console.log('📋 Compras de esta rifa:', compras.filter(c => c.rifaId === rifa.id));
      console.log('🎯 Comprador encontrado:', comprador);
      
      if (comprador) {
        setCompradorInfo({
          numero,
          nombre: comprador.compradorNombre || comprador.nombre || comprador.compradorNombre || 'Sin nombre',
          direccion: comprador.compradorDireccion || comprador.direccion || 'Sin dirección',
          email: comprador.compradorEmail || comprador.email || 'Sin email',
          telefono: comprador.compradorTelefono || comprador.telefono || 'Sin teléfono'
        });
        setShowCompradorModal(true);
        console.log('✅ Modal abierto con información del comprador');
      } else {
        console.log('❌ No se encontró comprador para este número');
        alert(`No se encontró información del comprador para el número ${numero}.\n\nEsto puede suceder si:\n- El número fue marcado como vendido pero no hay registro de compra\n- La compra fue eliminada`);
      }
      return;
    }

    if ((rifa.numerosPendientes || []).includes(numero)) {
      return; // Ya está pendiente
    }
    
    // Si es sorteo con token, solo permitir seleccionar UN número (siempre reemplazar)
    if (rifa.tipo === 'sorteo' && sorteoTokenValid) {
      // INVALIDAR EL TOKEN INMEDIATAMENTE AL SELECCIONAR EL PRIMER NÚMERO
      if (selectedNumbers.length === 0) {
        // Primer número seleccionado - invalidar token AHORA
        try {
          const rifaRef = doc(db, 'rifas', rifa.id);
          const rifaSnap = await getDoc(rifaRef);
          
          if (rifaSnap.exists()) {
            const rifaData = rifaSnap.data();
            const tokensValidos = rifaData.tokensValidos || [];
            
            // Remover el token de la lista (invalidarlo)
            const tokensRestantes = tokensValidos.filter(t => t !== sorteoTokenValid);
            
            await updateDoc(rifaRef, {
              tokensValidos: tokensRestantes
            });
            
            console.log('🔒 Token invalidado al seleccionar primer número');
          }
        } catch (error) {
          console.error('Error al invalidar token:', error);
        }
      }
      
      // Si ya está seleccionado, deseleccionar
      if (selectedNumbers.includes(numero) && selectedNumbers.length === 1) {
        setSelectedNumbers([]);
      } else {
        // Siempre reemplazar con solo este número (no agregar a la selección)
        setSelectedNumbers([numero]);
      }
    } else {
      // Para rifas normales, permitir múltiples selecciones
    if (selectedNumbers.includes(numero)) {
      setSelectedNumbers(selectedNumbers.filter(n => n !== numero));
      } else {
      setSelectedNumbers([...selectedNumbers, numero]);
      }
    }
  };

  const handleProceedToPayment = () => {
    if (selectedNumbers.length === 0) {
      alert('Debes seleccionar al menos un número');
      return;
    }
    // Limpiar formulario del comprador
    setBuyerForm({ nombre: '', apellido: '', email: '', telefono: '', direccion: '' });
    setShowDatosModal(true);
  };

  const handleProceedToConfirmation = () => {
    if (!buyerForm.nombre || !buyerForm.apellido || !buyerForm.email || !buyerForm.telefono || !buyerForm.direccion) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    // Advertencia: Verificar datos antes de continuar
    const confirmacion = confirm(
      '⚠️ IMPORTANTE: Verifica que tus datos sean correctos\n\n' +
      `Nombre: ${buyerForm.nombre} ${buyerForm.apellido}\n` +
      `Email: ${buyerForm.email}\n` +
      `Teléfono: ${buyerForm.telefono}\n` +
      `Dirección: ${buyerForm.direccion}\n\n` +
      '¿Confirmas que estos datos son correctos?'
    );
    
    if (!confirmacion) {
      return;
    }
    
    setShowDatosModal(false);
    navigateTo('/confirmar-compra');
  };

  const handleConfirmPayment = async (metodoPago = 'transferencia') => {
    try {
      console.log('=== CONFIRMANDO PAGO ===');
      console.log('Método de pago seleccionado:', metodoPago);
      console.log('Números seleccionados:', selectedNumbers);
      console.log('Datos del comprador:', buyerForm);
      console.log('Link MercadoPago:', selectedRifa.linkMercadoPago);

      const rifaRef = doc(db, 'rifas', selectedRifa.id);
      const rifaSnap = await getDoc(rifaRef);
      if (!rifaSnap.exists()) {
        alert('Rifa no encontrada. Actualizá la página.');
        return;
      }
      const rifaFresca = { id: rifaSnap.id, ...rifaSnap.data() };
      const ocupados = new Set([
        ...(rifaFresca.numerosReservados || []),
        ...(rifaFresca.numerosPendientes || []),
      ]);
      const numerosParaReservar = normalizarNumerosLista(
        selectedNumbers.filter(n => !ocupados.has(n))
      );
      const yaNoDisponibles = selectedNumbers.filter(n => ocupados.has(n));

      if (yaNoDisponibles.length > 0) {
        alert(
          `Estos números ya no están disponibles: ${yaNoDisponibles.sort((a, b) => a - b).join(', ')}.` +
          (numerosParaReservar.length
            ? ` Solo se reservarán: ${numerosParaReservar.join(', ')}.`
            : ' Elegí otros números.')
        );
        if (numerosParaReservar.length === 0) {
          setSelectedNumbers([]);
          return;
        }
      }

    // Determinar si es sorteo (gratis), MercadoPago o transferencia
    const esSorteo = metodoPago === 'sorteo' || selectedRifa.tipo === 'sorteo';
    const esPagoMercadoPago = metodoPago === 'mercadopago';

    if (esPagoMercadoPago && selectedNumbers.length !== 1) {
      alert('Mercado Pago solo está disponible para comprar un número a la vez. Para varios números, usá transferencia.');
      return;
    }

    const updatedRifas = rifas.map(r => {
      if (r.id === selectedRifa.id) {
        if (esSorteo) {
          // Sorteo - marcar directamente como VENDIDOS (ROJO) sin pago
          console.log('🎲 Sorteo - Números marcados como VENDIDOS (gratis)');
          return {
            ...r,
            numerosReservados: agregarNumerosSinDuplicar(r.numerosReservados, numerosParaReservar),
            numerosVendidos: r.numerosVendidos + numerosParaReservar.length
          };
        } else {
          // Transferencia o Mercado Pago — pendientes (naranja) hasta que el creador confirme
          console.log(esPagoMercadoPago ? '💳 Mercado Pago - Números PENDIENTES' : '🏦 Transferencia - Números PENDIENTES');
          return {
            ...r,
            numerosPendientes: agregarNumerosSinDuplicar(r.numerosPendientes, numerosParaReservar)
          };
        }
      }
      return r;
    });
    
    const nuevaCompra = {
      rifaId: selectedRifa.id,
      rifaTitulo: selectedRifa.titulo,
      compradorNombre: esSorteo ? 'Cliente Sorteo' : `${buyerForm.nombre} ${buyerForm.apellido}`,
      compradorEmail: esSorteo ? 'sorteo@acceso7.com' : buyerForm.email,
      compradorTelefono: esSorteo ? '' : buyerForm.telefono,
      compradorDireccion: esSorteo ? '' : buyerForm.direccion,
      numeros: [...numerosParaReservar],
      total: esSorteo ? 0 : (numerosParaReservar.length * parseFloat(selectedRifa.precio)),
        fecha: new Date().toISOString(),
      estado: esSorteo ? 'confirmado' : 'pendiente',
      metodoPago: esSorteo ? 'sorteo' : (esPagoMercadoPago ? 'mercadopago' : 'transferencia')
      };
      
      // Guardar compra en Firestore
      const compraId = await saveCompraToFirestore(nuevaCompra);
      const compraWithId = { id: compraId, ...nuevaCompra };
      
      // Actualizar rifa en Firestore
      if (esSorteo) {
        await updateRifaInFirestore(selectedRifa.id, {
          numerosReservados: agregarNumerosSinDuplicar(rifaFresca.numerosReservados, numerosParaReservar),
          numerosVendidos: (rifaFresca.numerosVendidos || 0) + numerosParaReservar.length
        });
      } else {
        const numerosPendientesActuales = rifaFresca.numerosPendientes || [];
        const numerosPendientesNuevos = agregarNumerosSinDuplicar(numerosPendientesActuales, numerosParaReservar);
        
        await updateRifaInFirestore(selectedRifa.id, {
          numerosPendientes: numerosPendientesNuevos
        });
        
        notificarPagoPendiente({
          rifaId: selectedRifa.id,
          compradorNombre: nuevaCompra.compradorNombre,
          numeros: nuevaCompra.numeros,
          total: nuevaCompra.total,
          rifaTitulo: nuevaCompra.rifaTitulo
        });
      }
      
      setCompras([...compras, compraWithId]);
      setRifas(updatedRifas);
      
      // Simular envío de email (mantener para compatibilidad)
      enviarEmailConfirmacion(compraWithId);
      
      if (esSorteo) {
        // Mensaje para sorteos
        alert(`¡Números seleccionados exitosamente! 🎉\n\n✅ Sorteo Gratuito\n🎫 Tus números: ${numerosParaReservar.sort((a, b) => a - b).join(', ')}\n\n🔴 Tus números ya están RESERVADOS\n\n¡Mucha suerte en el sorteo!`);
      } else if (esPagoMercadoPago) {
        alert(`¡Reserva registrada!\n\n✅ Pagaste con Mercado Pago\n🎫 Tu número: ${numerosParaReservar.join(', ')}\n💰 Total: $${nuevaCompra.total}\n\n🟠 Quedó en estado PENDIENTE (naranja) hasta que el creador de la rifa confirme que recibió el pago.\n\n📧 Aviso enviado a: ${buyerForm.email}\n\n¡Mucha suerte!`);
      } else {
      alert(`¡Reserva realizada exitosamente!\n\n✅ Confirmación enviada a: ${buyerForm.email}\n🎫 Tus números: ${numerosParaReservar.sort((a, b) => a - b).join(', ')}\n💰 Total: $${nuevaCompra.total}\n\n⏳ Los números están en estado "Pendiente" hasta que el creador confirme tu transferencia.\n\n¡Mucha suerte!`);
      }
      
      const numerosSeleccionados = [...numerosParaReservar];
    setSelectedNumbers([]);
      setBuyerForm({ nombre: '', apellido: '', email: '', telefono: '', direccion: '' });
      
      // Si es sorteo con token, el token ya fue invalidado cuando se validó por primera vez
      // Solo mostramos mensaje al usuario
      if (selectedRifa.tipo === 'sorteo' && sorteoTokenValid) {
        if (user && user.id === selectedRifa.creadorId) {
          alert(`✅ Número seleccionado exitosamente! 🎉\n\n🎫 Número: ${numerosSeleccionados[0]}\n\n📱 El QR físico sigue funcionando. Cada escaneo genera un nuevo link automáticamente.`);
        } else {
          alert(`✅ Número seleccionado exitosamente! 🎉\n\n🎫 Tu número: ${numerosSeleccionados[0]}\n\n⚠️ Este link ya no es válido y no se puede usar de nuevo.`);
        }
        setSorteoTokenValid(null);
      }
      
      navigateTo('/inicio');
      
    } catch (error) {
      console.error('Error al confirmar pago:', error);
      alert('Error al procesar el pago. Por favor intenta de nuevo.');
    }
  };

  // Push al celular (Android/iOS) o navegador cuando el usuario inicia sesión
  useEffect(() => {
    let cleanup = () => {};
    if (user) {
      iniciarNotificacionesPush(user, navigateTo).then((fn) => {
        cleanup = typeof fn === 'function' ? fn : () => {};
      });
    }
    return () => cleanup();
  }, [user]);

  // Función para enviar notificación push al creador cuando hay pago pendiente
  const notificarPagoPendiente = async (datos) => {
    try {
      const { rifaId, compradorNombre, rifaTitulo } = datos;
      // Normalizar total y numeros para no fallar con toFixed/length (si faltan, la notificación se crea igual)
      const totalNum = typeof datos.total === 'number' ? datos.total : (parseFloat(datos.total) || 0);
      const numerosArr = Array.isArray(datos.numeros) ? datos.numeros : [];
      const tituloRifa = rifaTitulo || 'Rifa';
      const numsTexto = numerosArr.length ? numerosArr.join(', ') : '—';
      const mensaje = `${compradorNombre || 'Cliente'} reservó el/los número(s) ${numsTexto} en "${tituloRifa}" ($${totalNum.toFixed(2)}). Confirmá el pago en Mis Rifas.`;

      // Obtener el creador de la rifa
      const rifaDoc = await getDoc(doc(db, 'rifas', rifaId));
      if (!rifaDoc.exists()) {
        console.error('Rifa no encontrada');
        return;
      }

      const rifaData = rifaDoc.data();
      const creadorId = rifaData.creadorId || rifaData.creadorUid || rifaData.creadorUID;
      if (!creadorId) {
        console.error('❌ notificarPagoPendiente: la rifa no tiene creadorId/creadorUid. rifaId:', rifaId);
        return;
      }

      // Obtener email del creador para que la Cloud Function pueda enviar el correo
      // Prioridad: si la rifa ya lo tiene guardado, usarlo
      let creadorEmail = (rifaData.creadorEmail && String(rifaData.creadorEmail).trim())
        ? String(rifaData.creadorEmail).trim()
        : null;
      try {
        if (!creadorEmail) {
          const creadorDoc = await getDoc(doc(db, 'usuarios', creadorId));
          if (creadorDoc.exists()) {
            const d = creadorDoc.data();
            creadorEmail = (d.email || d.correo || d.mail || null);
          }
        }
      } catch (e) { /* ignorar */ }

      // Guardar notificación en Firestore para que el creador la reciba (incluye creadorEmail para el envío de correo)
      const notificacionRef = doc(collection(db, 'notificaciones'));
      await setDoc(notificacionRef, {
        userId: creadorId,
        // Firestore no acepta `undefined`; usar null cuando no haya email
        creadorEmail: (creadorEmail && String(creadorEmail).trim()) ? String(creadorEmail).trim() : null,
        tipo: 'pago_pendiente',
        titulo: '⏳ Número pendiente de confirmar',
        mensaje,
        rifaId: rifaId,
        compradorNombre: compradorNombre || 'Cliente',
        numeros: numerosArr,
        total: totalNum,
        rifaTitulo: tituloRifa,
        leida: false,
        createdAt: new Date().toISOString()
      });

      console.log('✅ Notificación guardada en Firestore para el creador');

      // También mostrar notificación local si el usuario actual es el creador
      const esCreador = user && (user.id === creadorId || user.uid === creadorId);
      if (esCreador && 'Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('🔔 Nuevo Pago Pendiente', {
          body: mensaje,
          icon: '/logo.png',
          badge: '/logo.png',
          tag: `pago-pendiente-${rifaId}`,
          requireInteraction: true,
          data: {
            rifaId: rifaId,
            tipo: 'pago_pendiente'
          }
        });

        notification.onclick = () => {
          window.focus();
          navigateTo('/mis-rifas');
          notification.close();
        };
      }
    } catch (error) {
      console.error('Error al enviar notificación:', error);
    }
  };

  // Función para enviar notificación push al cliente cuando se confirma el pago
  const notificarPagoConfirmado = async (datos) => {
    try {
      const emailCliente = (datos.compradorEmail || datos.email || '').trim();
      if (!emailCliente) {
        console.error('❌ notificarPagoConfirmado: falta email del comprador');
        return;
      }
      const { compradorNombre, rifaTitulo, rifaId, compraId } = datos;
      // Normalizar total y numeros para no fallar con toFixed/join (si faltan, la notificación se crea igual)
      const totalNum = typeof datos.total === 'number' ? datos.total : (parseFloat(datos.total) || 0);
      const numerosArr = Array.isArray(datos.numeros) ? datos.numeros : [];
      const numerosTexto = numerosArr.length ? numerosArr.join(', ') : '—';
      const tituloRifa = rifaTitulo || 'Rifa';

      // Obtener el ID del comprador desde la colección de usuarios
      let compradorId = null;
      try {
        const usuariosQuery = query(collection(db, 'usuarios'), where('email', '==', emailCliente));
        const usuariosSnapshot = await getDocs(usuariosQuery);
        if (!usuariosSnapshot.empty) {
          compradorId = usuariosSnapshot.docs[0].id;
        }
      } catch (error) {
        console.error('Error al buscar comprador:', error);
      }

      // Guardar notificación en Firestore (Cloud Function enviará el correo al comprador)
      const notificacionRef = doc(collection(db, 'notificaciones'));
      const mensaje = `Tu pago de $${totalNum.toFixed(2)} fue confirmado. Números: ${numerosTexto} en "${tituloRifa}"`;
      await setDoc(notificacionRef, {
        userId: compradorId || emailCliente,
        email: emailCliente,
        compradorEmail: emailCliente,
        clienteEmail: emailCliente,
        compradorNombre: compradorNombre || 'Cliente',
        compraId: compraId || null,
        tipo: 'pago_confirmado',
        titulo: '✅ Pago Confirmado',
        mensaje,
        rifaId: rifaId,
        rifaTitulo: tituloRifa,
        numeros: numerosArr,
        total: totalNum,
        leida: false,
        createdAt: new Date().toISOString()
      });

      // También mostrar notificación local si el usuario actual es el comprador
      const esComprador = user && (
        user.email === emailCliente ||
        user.id === emailCliente ||
        (compradorNombre && user.name && compradorNombre.toLowerCase().includes(user.name.toLowerCase()))
      );

      if (esComprador && 'Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('✅ Pago Confirmado', {
          body: mensaje,
          icon: '/logo.png',
          badge: '/logo.png',
          tag: `pago-confirmado-${rifaId}`,
          requireInteraction: true,
          data: {
            rifaId: rifaId,
            tipo: 'pago_confirmado'
          }
        });

        notification.onclick = () => {
          window.focus();
          navigateTo(`/rifa/${rifaId}`);
          notification.close();
        };
      }
    } catch (error) {
      console.error('Error al enviar notificación:', error);
    }
  };

  const enviarEmailConfirmacion = (compra) => {
    // Función legacy - mantener para compatibilidad
    console.log('📧 EMAIL DE CONFIRMACIÓN ENVIADO:');
    console.log(`Para: ${compra.compradorEmail}`);
    console.log(`Asunto: Confirmación de compra - ${compra.rifaTitulo}`);
    console.log(`Números: ${compra.numeros.join(', ')}`);
    console.log(`Total: ${compra.total}`);
    console.log('---');
  };

  const realizarSorteo = async (premioIndex) => {
    // IMPORTANTE: Limpiar selectedNumbers para asegurar que no se use en el sorteo
    setSelectedNumbers([]);
    
    if (selectedRifa.numerosReservados.length === 0) {
      alert('No hay números vendidos para realizar el sorteo');
      return;
    }

    // Obtener premios
    const premios = selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : []);
    const premioSeleccionado = premios[premioIndex];
    
    if (!premioSeleccionado) {
      alert('No hay más premios para sortear');
      return;
    }
    
    // Guardar el premio específico que se está sorteando para mostrarlo en el modal
    setPremioActualSorteo(premioSeleccionado);

    // Obtener números que ya ganaron (excluir del sorteo)
    const numerosGanadores = ganadores
      .filter(g => g.rifaId === selectedRifa.id)
      .map(g => g.numeroGanador);
    
    // Números disponibles para sortear (excluyendo ganadores)
    // IMPORTANTE: Solo usar numerosReservados (vendidos), NUNCA usar selectedNumbers
    const numerosDisponibles = selectedRifa.numerosReservados.filter(num => !numerosGanadores.includes(num));
    
    console.log('🎲 INICIANDO SORTEO:', {
      premio: premioSeleccionado,
      premioIndex: premioIndex,
      totalNumerosReservados: selectedRifa.numerosReservados.length,
      numerosGanadores: numerosGanadores,
      numerosDisponibles: numerosDisponibles,
      selectedNumbers: selectedNumbers, // Debe estar vacío
      advertencia: 'El sorteo es COMPLETAMENTE ALEATORIO de numerosDisponibles'
    });
    
    if (numerosDisponibles.length === 0) {
      alert('No hay números disponibles para sortear (todos ya ganaron)');
      return;
    }

    setShowSorteoModal(true);
    setSorteoEnCurso(true);
    setNumeroGanador(null);
    // NO limpiar premioActualSorteo aquí, ya se estableció arriba

    // Animación de sorteo mejorada - más lenta y con mejor mezcla
    let contador = 0;
    const maxIteraciones = 80; // Aumentado de 20 a 80 iteraciones para más duración
    
    // Función para animar con velocidad progresiva
    const animarSorteo = () => {
      // Mezclar todos los números disponibles de forma aleatoria para mejor visualización
      const numerosMezclados = [...numerosDisponibles].sort(() => Math.random() - 0.5);
      const numeroAleatorio = numerosMezclados[
        Math.floor(Math.random() * numerosMezclados.length)
      ];
      setNumeroGanador(numeroAleatorio);
      contador++;

      if (contador >= maxIteraciones) {
        setSorteoEnCurso(false);
        
        // Seleccionar número ganador final - COMPLETAMENTE ALEATORIO
        // Usar crypto.getRandomValues para mejor aleatoriedad si está disponible
        let numeroFinalGanador;
        if (window.crypto && window.crypto.getRandomValues) {
          // Método más seguro: usar crypto.getRandomValues
          const randomArray = new Uint32Array(1);
          window.crypto.getRandomValues(randomArray);
          const randomIndex = randomArray[0] % numerosDisponibles.length;
          numeroFinalGanador = numerosDisponibles[randomIndex];
        } else {
          // Fallback: Math.random (menos seguro pero funcional)
          numeroFinalGanador = numerosDisponibles[
            Math.floor(Math.random() * numerosDisponibles.length)
          ];
        }
        
        console.log('🎯 NÚMERO GANADOR SELECCIONADO (ALEATORIO):', {
          numeroGanador: numeroFinalGanador,
          premio: premioSeleccionado,
          premioIndex: premioIndex,
          totalDisponibles: numerosDisponibles.length,
          metodo: window.crypto ? 'crypto.getRandomValues' : 'Math.random',
          advertencia: 'Este número fue seleccionado ALEATORIAMENTE, NO por selección manual'
        });
        
        setNumeroGanador(numeroFinalGanador);
        
        // Continuar con el guardado del ganador
        const compraGanadora = compras.find(c => 
          c.rifaId === selectedRifa.id && c.numeros.includes(numeroFinalGanador)
        );

        const ahora = new Date();
        // NO agregar fecha de expiración - los ganadores son permanentes
        
        const nuevoGanador = {
          id: Date.now(),
          rifaId: selectedRifa.id,
          rifaTitulo: selectedRifa.titulo,
          rifaPremio: premioSeleccionado.trim(),
          premioIndex: premioIndex,
          numeroGanador: numeroFinalGanador,
          ganadorNombre: compraGanadora?.compradorNombre || 'Desconocido',
          ganadorEmail: compraGanadora?.compradorEmail || '',
          ganadorTelefono: compraGanadora?.compradorTelefono || '',
          fechaSorteo: ahora.toISOString(),
          // Sin fechaExpiracion - ganador permanente
          creadorId: selectedRifa.creadorId
        };

        saveGanadorToFirestore(nuevoGanador).then(ganadorId => {
          nuevoGanador.firestoreId = ganadorId;
        }).catch(error => {
          console.error('Error al guardar ganador en Firestore:', error);
        });

        setGanadores([...ganadores, nuevoGanador]);

        const premiosSorteados = ganadores.filter(g => g.rifaId === selectedRifa.id).length + 1;
        const todosLosPremiosSorteados = premiosSorteados >= premios.length;

        if (todosLosPremiosSorteados) {
          const rifasActualizadas = rifas.map(r => {
            if (r.id === selectedRifa.id) {
              return { ...r, finalizada: true, ganadorId: nuevoGanador.id };
            }
            return r;
          });
          setRifas(rifasActualizadas);

          updateRifaInFirestore(selectedRifa.id, {
            finalizada: true,
            fechaFinalizacion: new Date().toISOString(),
            ganadorId: nuevoGanador.id
          }).catch(error => {
            console.error('Error al actualizar rifa en Firestore:', error);
          });
        } else {
          if (selectedRifa.finalizada) {
            console.log('⚠️ La rifa estaba finalizada pero aún faltan premios por sortear. Desfinalizando...');
            updateRifaInFirestore(selectedRifa.id, {
              finalizada: false
            }).catch(error => {
              console.error('Error al desfinalizar rifa:', error);
            });
          }
        }

        enviarNotificacionGanador(nuevoGanador);
        return;
      }
      
      // Velocidad progresiva: empieza rápido (50ms) y se hace más lento (300ms)
      const velocidad = Math.min(50 + (contador * 3), 300);
      setTimeout(animarSorteo, velocidad);
    };
    
    // Iniciar animación
    animarSorteo();
  };

  const enviarNotificacionGanador = (ganador) => {
    console.log('🎉 NOTIFICACIÓN AL GANADOR:');
    console.log(`Para: ${ganador.ganadorEmail}`);
    console.log(`¡FELICIDADES ${ganador.ganadorNombre}!`);
    console.log(`Has ganado: ${ganador.rifaPremio}`);
    console.log(`Número ganador: ${ganador.numeroGanador}`);
    console.log('---');
  };

  // Función para agregar ganador manualmente (solo admin)
  const agregarGanadorManual = async (rifaId, numeroGanador, premioTexto, premioIndex) => {
    if (!isAdmin()) {
      alert('❌ Solo el administrador puede agregar ganadores manualmente');
      return;
    }

    try {
      const rifa = rifas.find(r => r.id === rifaId);
      if (!rifa) {
        alert('❌ Rifa no encontrada');
        return;
      }

      // Buscar la compra del número
      const compraGanadora = compras.find(c => 
        c.rifaId === rifaId && c.numeros.includes(numeroGanador)
      );

      if (!compraGanadora) {
        alert(`❌ No se encontró información del comprador para el número ${numeroGanador}`);
        return;
      }

      const ahora = new Date();
      const nuevoGanador = {
        id: Date.now(),
        rifaId: rifaId,
        rifaTitulo: rifa.titulo,
        rifaPremio: premioTexto,
        premioIndex: premioIndex,
        numeroGanador: numeroGanador,
        ganadorNombre: compraGanadora.compradorNombre || 'Desconocido',
        ganadorEmail: compraGanadora.compradorEmail || '',
        ganadorTelefono: compraGanadora.compradorTelefono || '',
        fechaSorteo: ahora.toISOString(),
        creadorId: rifa.creadorId
      };

      const ganadorId = await saveGanadorToFirestore(nuevoGanador);
      nuevoGanador.firestoreId = ganadorId;
      
      setGanadores([...ganadores, nuevoGanador]);
      
      alert(`✅ Ganador agregado exitosamente!\n\nNúmero: ${numeroGanador}\nPremio: ${premioTexto}\nGanador: ${nuevoGanador.ganadorNombre}`);
      
      console.log('🎉 Ganador manual agregado:', nuevoGanador);
    } catch (error) {
      console.error('Error al agregar ganador manual:', error);
      alert('❌ Error al agregar ganador. Por favor intenta de nuevo.');
    }
  };


  // Header Component
  const Header = () => (
    <header className="bg-black bg-opacity-40 backdrop-blur-md border-b border-purple-500 w-full overflow-hidden">
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <div 
            className="flex items-center gap-2 cursor-pointer flex-shrink-0"
            onClick={() => navigateTo('/inicio')}
          >
            <Ticket className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-yellow-400" />
            <h1 className="text-xl sm:text-2xl md:text-4xl font-bold text-white tracking-wider">ACCESO7</h1>
          </div>
          
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 w-full sm:w-auto">
            {!user ? (
              <>
                <button
                  onClick={() => navigateTo('/login')}
                  className="px-4 sm:px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-sm sm:text-base transition"
                >
                  Iniciar Sesión
                </button>
                <button
                  onClick={() => navigateTo('/registro')}
                  className="px-4 sm:px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black rounded-lg font-semibold text-sm sm:text-base transition"
                >
                  Registro
                </button>
              </>
                    ) : (
                      <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                        <button
                          onClick={() => {
                            openCrearRifaModal();
                          }}
                          className="px-3 py-1.5 md:px-6 md:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                        >
                          <Plus className="w-4 h-4 md:w-5 md:h-5" />
                          <span className="hidden md:inline">Crear Rifa</span>
                        </button>
                        {!isAdmin() && (
                        <button
                            onClick={() => navigateTo('/habilitar-rifa')}
                            className="px-3 py-1.5 md:px-6 md:py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                        >
                            <User className="w-4 h-4 md:w-5 md:h-5" />
                            <span className="hidden md:inline">Habilitar Rifa</span>
                        </button>
                        )}
                        <button
                          onClick={() => navigateTo('/mis-rifas')}
                          className="px-3 py-1.5 md:px-6 md:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm md:text-base font-semibold transition"
                        >
                          <span className="hidden md:inline">Mis Rifas</span>
                          <span className="md:hidden">Rifas</span>
                        </button>
                        {isAdmin() && (
                          <>
                          <button
                            onClick={() => navigateTo('/todas-las-rifas')}
                            className="px-3 py-1.5 md:px-6 md:py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                          >
                            <Ticket className="w-4 h-4 md:w-5 md:h-5" />
                            <span className="hidden lg:inline">Todas las Rifas</span>
                          </button>
                          <button
                            onClick={() => navigateTo('/admin')}
                            className="px-3 py-1.5 md:px-6 md:py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                          >
                            <User className="w-4 h-4 md:w-5 md:h-5" />
                            <span className="hidden md:inline">Admin</span>
                          </button>
                          </>
                        )}
                        <button
                          onClick={() => navigateTo('/ganadores')}
                          className="px-3 py-1.5 md:px-6 md:py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm md:text-base font-semibold transition"
                        >
                          <span className="hidden md:inline">Ganadores</span>
                          <span className="md:hidden">🏆</span>
                        </button>
                        <div className="hidden md:flex items-center gap-2 text-white">
                          <User className="w-5 h-5" />
                  <span className="font-semibold">{user.name}</span>
                        </div>
                        <button
                          onClick={handleLogout}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                        >
                          <LogOut className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                      </div>
                    )}
          </div>
        </div>
      </div>
    </header>
  );

  // Home Page - Lista de Rifas
  const HomePage = () => {
    const [showFinalizadas, setShowFinalizadas] = useState(false);
    const [busquedaRifas, setBusquedaRifas] = useState('');
    const [busquedaAbierta, setBusquedaAbierta] = useState(false);
    const inputBusquedaRef = useRef(null);

    useEffect(() => {
      if (busquedaAbierta && inputBusquedaRef.current) {
        inputBusquedaRef.current.focus();
      }
    }, [busquedaAbierta]);

    const rifasActivas = rifas.filter(r => !r.finalizada);
    const rifasFinalizadas = rifas.filter(r => r.finalizada);
    const rifasActivasFiltradas = filtrarRifasLista(rifasActivas, busquedaRifas);
    const rifasFinalizadasFiltradas = filtrarRifasLista(rifasFinalizadas, busquedaRifas);
    const hayBusqueda = busquedaRifas.trim().length > 0;

    const cerrarBusqueda = () => {
      setBusquedaRifas('');
      setBusquedaAbierta(false);
    };
    
    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 text-center px-2">Todas las Rifas, Sorteo, Raspaditas Disponibles</h2>

        {/* Buscador de rifas */}
        <div className="flex justify-center mb-6 sm:mb-8 px-2">
          <div
            className={`relative transition-all duration-300 ease-out ${
              busquedaAbierta || hayBusqueda ? 'w-full max-w-xl' : 'w-auto'
            }`}
          >
            {!busquedaAbierta && !hayBusqueda ? (
              <button
                type="button"
                onClick={() => setBusquedaAbierta(true)}
                className="group flex items-center gap-2.5 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-purple-400/50 hover:border-yellow-400/60 text-purple-100 hover:text-white shadow-lg shadow-purple-900/30 backdrop-blur-md transition-all"
                aria-label="Buscar rifas"
              >
                <Search className="w-5 h-5 text-yellow-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                <span className="font-medium text-sm sm:text-base">Buscar rifas</span>
              </button>
            ) : (
              <div className="flex items-center w-full rounded-full bg-black/50 border-2 border-purple-400/80 focus-within:border-yellow-400 shadow-xl shadow-purple-900/40 backdrop-blur-md overflow-hidden">
                <Search className="w-5 h-5 text-yellow-400 ml-4 flex-shrink-0 pointer-events-none" aria-hidden />
                <input
                  ref={inputBusquedaRef}
                  type="search"
                  value={busquedaRifas}
                  onChange={(e) => setBusquedaRifas(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && cerrarBusqueda()}
                  placeholder="Buscar por título, premio o creador..."
                  className="flex-1 min-w-0 bg-transparent px-3 py-3.5 text-white placeholder:text-purple-300/60 outline-none text-sm sm:text-base"
                  aria-label="Buscar rifas"
                />
                <button
                  type="button"
                  onClick={cerrarBusqueda}
                  className="p-2 mr-2 text-purple-300 hover:text-white rounded-full hover:bg-white/10 transition flex-shrink-0"
                  aria-label="Cerrar búsqueda"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {hayBusqueda && (
          <p className="text-center text-purple-200 text-sm mb-4 -mt-2">
            {showFinalizadas
              ? `${rifasFinalizadasFiltradas.length} rifa(s) finalizada(s) encontrada(s)`
              : `${rifasActivasFiltradas.length} rifa(s) activa(s) encontrada(s)`}
            {' '}para &quot;{busquedaRifas.trim()}&quot;
          </p>
        )}
            
        {/* Botón para ver rifas finalizadas - Visible para todos */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <button
            onClick={() => setShowFinalizadas(!showFinalizadas)}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition flex items-center gap-2 text-sm sm:text-base"
          >
            {showFinalizadas ? 'Ver Rifas Activas' : 'Ver Rifas Finalizadas'}
            <span className="bg-gray-500 text-white px-2 py-0.5 sm:py-1 rounded-full text-xs">
              {rifasFinalizadas.length}
            </span>
          </button>
        </div>
            
            {showFinalizadas ? (
              // Mostrar rifas finalizadas
              rifasFinalizadasFiltradas.length === 0 ? (
              <div className="text-center py-16">
                  {hayBusqueda ? (
                    <>
                      <Search className="w-20 h-20 text-purple-400 mx-auto mb-4 opacity-60" />
                      <p className="text-white text-xl">No hay rifas finalizadas que coincidan</p>
                      <p className="text-purple-300 mt-2">Probá con otro término o limpiá la búsqueda</p>
                    </>
                  ) : (
                    <>
                      <Ticket className="w-24 h-24 text-gray-400 mx-auto mb-4 opacity-50" />
                      <p className="text-white text-xl">No hay rifas finalizadas aún</p>
                    </>
                  )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {rifasFinalizadasFiltradas.map(rifa => {
            const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
            return (
                      <div key={rifa.id} className="bg-gray-600 bg-opacity-20 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-gray-500 opacity-75">
                        <div className="flex items-start gap-2 sm:gap-3 mb-2">
                          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-400 flex-1">{rifa.titulo}</h3>
                          <span className="px-2 sm:px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full flex-shrink-0">
                            ✅ Finalizada
                          </span>
                        </div>
                        <p className="text-gray-300 mb-3 sm:mb-4 text-sm sm:text-base">{rifa.descripcion}</p>
                
                <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-gray-300">
                            <Ticket className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold">Premio:</span> {rifa.premio}
                  </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold">Precio:</span> ${rifa.precio}
                  </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <Users className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold">Números:</span> {rifa.numerosVendidos}/{rifa.totalNumeros}
                  </div>
                </div>

                        <div className="border-t border-gray-500 pt-3 mb-4">
                          <div className="flex items-center justify-between">
                          <p className="text-gray-400 text-sm">Creado por: <span className="font-semibold text-gray-300">{rifa.creadorNombre}</span></p>
                            <CopiableAliasCbu alias={rifa.alias} cbu={rifa.cbu} className="flex items-center gap-2" />
                          </div>
                        </div>
                
                  <div className="flex gap-2">
                  <button 
                    onClick={() => handleVerRifa(rifa)}
                      className="flex-1 py-3 bg-gray-700 hover:bg-gray-800 text-white font-bold rounded-lg transition"
                        >
                          Ver Números y Ganadores
                  </button>
                    {renderBotonesCompartirRifa(rifa, { compact: true })}
                  </div>
              </div>
            );
          })}
        </div>
              )
            ) : rifasActivasFiltradas.length === 0 ? (
              <div className="text-center py-16">
                {hayBusqueda ? (
                  <>
                    <Search className="w-20 h-20 text-yellow-400/80 mx-auto mb-4" />
                    <p className="text-white text-xl">No encontramos rifas activas</p>
                    <p className="text-purple-300 mt-2">Probá con otro término o tocá la X para ver todas</p>
                  </>
                ) : (
                  <>
                    <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
                    <p className="text-white text-xl">No hay rifas activas en este momento</p>
                  </>
                )}
              </div>
            ) : (
              // Mostrar rifas activas
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {rifasActivasFiltradas.map(rifa => {
                  const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
                  return (
                    <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-purple-400 hover:border-yellow-400 transition">
                      {(() => {
                        const imgsPremios = Array.isArray(rifa.imagenesPremios) ? rifa.imagenesPremios : [];
                        const portadaUrl = imgsPremios.find(i => i?.url)?.url || rifa.imagenUrl || null;
                        return portadaUrl ? (
                          <img
                            src={portadaUrl}
                            alt={rifa.titulo}
                            className="w-full h-40 sm:h-48 object-cover rounded-lg mb-3 border border-purple-400/50"
                            loading="lazy"
                          />
                        ) : null;
                      })()}
                      <div className="flex items-start gap-2 sm:gap-3 mb-2">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-400 flex-1">{rifa.titulo}</h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                          rifa.tipo === 'rapadita' 
                            ? 'bg-orange-500 text-white' 
                            : rifa.tipo === 'sorteo'
                            ? 'bg-blue-500 text-white'
                            : 'bg-purple-500 text-white'
                        }`}>
                          {rifa.tipo === 'rapadita' ? '⚡ Rapadita' : rifa.tipo === 'sorteo' ? '🎲 Sorteo' : '🎫 Rifa'}
                        </span>
                      </div>
                      <p className="text-white mb-3 sm:mb-4 text-sm sm:text-base">{rifa.descripcion}</p>
                      
                      <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 text-sm sm:text-base">
                        <div className="flex items-center gap-2 text-white">
                          <Ticket className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-400 flex-shrink-0" />
                          <span className="font-semibold">Premio:</span> <span className="truncate">{rifa.premio}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400 flex-shrink-0" />
                          <span className="font-semibold">Precio:</span> ${rifa.precio}
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400 flex-shrink-0" />
                          <span className="font-semibold">Números:</span> {rifa.numerosVendidos}/{rifa.totalNumeros}
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-400 flex-shrink-0" />
                          <span className="font-semibold">
                            {rifa.tipoFinalizacion === 'fecha' ? 'Sorteo:' : 'Finaliza:'}
                          </span> 
                          <span className="truncate">
                            {rifa.tipoFinalizacion === 'fecha' 
                              ? new Date(rifa.fechaSorteo).toLocaleDateString()
                              : 'Cuando se vendan todos'
                            }
                          </span>
                        </div>
                      </div>

                      {/* Barra de progreso */}
                      <div className="mb-4">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-yellow-500 h-2 rounded-full transition-all"
                            style={{ width: `${porcentajeVendido}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-purple-300 mt-1">{porcentajeVendido.toFixed(1)}% vendido</p>
                      </div>
                      
                      <div className="border-t border-purple-400 pt-2 sm:pt-3 mb-3 sm:mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-2">
                          <p className="text-purple-300 text-xs sm:text-sm">Creado por: <span className="font-semibold text-white">{rifa.creadorNombre}</span></p>
                          <CopiableAliasCbu
                            alias={rifa.alias}
                            cbu={rifa.cbu}
                            soloValor
                            className="text-xs sm:text-sm truncate max-w-full px-2 sm:px-3"
                          />
                        </div>
                        {rifa.whatsapp && (
                          <p className="text-purple-300 text-xs sm:text-sm mt-1">
                            📱 WhatsApp: <span className="font-semibold text-white">{rifa.whatsapp}</span>
                          </p>
                        )}
                      </div>
                      
                      {rifa.whatsapp && (
                        <a
                          href={`https://wa.me/${rifa.whatsapp.replace(/[^0-9]/g, '')}?text=Hola! Vi tu rifa "${rifa.titulo}" y me interesa participar. ¿Podrías darme más información?`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full mb-2 sm:mb-3 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg transition flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                          <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="hidden sm:inline">Contactar por WhatsApp</span>
                          <span className="sm:hidden">WhatsApp</span>
                        </a>
                      )}
                      
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          {rifa.tipo === 'sorteo' ? (
                            <button 
                              onClick={() => handleVerRifa(rifa)}
                              className="w-full py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-lg transition relative text-sm sm:text-base"
                            >
                              👁️ <span className="hidden sm:inline">Ver Números Disponibles</span><span className="sm:hidden">Ver Números</span>
                            </button>
                          ) : (
                            <>
                              <button 
                                onClick={() => handleVerRifa(rifa)}
                                className="w-full py-2 sm:py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold rounded-lg transition relative text-sm sm:text-base"
                              >
                                <span className="hidden sm:inline">Ver Números Disponibles</span><span className="sm:hidden">Ver Números</span>
                              </button>
                              {/* Tooltip animado - abajo del botón naranja - oculto en móvil */}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 z-10 mt-2 animate-bounce hidden sm:block">
                                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg shadow-xl font-bold text-sm whitespace-nowrap border-2 border-yellow-400 relative">
                                  🎯 ¡Haz clic en los números para seleccionarlos!
                                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gradient-to-r from-purple-600 to-pink-600 rotate-45 border-l-2 border-t-2 border-yellow-400"></div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        {renderBotonesCompartirRifa(rifa, { compact: true })}
                      </div>
                    </div>
                  );
                })}
                
                {/* Rifas finalizadas (intercaladas) */}
                {rifasFinalizadas.map(rifa => {
                  const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
                  return (
                    <div key={rifa.id} className="bg-gray-600 bg-opacity-20 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-gray-500 opacity-75">
                      <div className="flex items-start gap-2 sm:gap-3 mb-2">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-400 flex-1">{rifa.titulo}</h3>
                        <span className="px-2 sm:px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full flex-shrink-0">
                          ✅ Finalizada
                        </span>
                      </div>
                      <p className="text-gray-300 mb-3 sm:mb-4 text-sm sm:text-base">{rifa.descripcion}</p>
                      
                      <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 text-sm sm:text-base">
                        <div className="flex items-center gap-2 text-gray-300">
                          <Ticket className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-semibold">Premio:</span> <span className="truncate">{rifa.premio}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-semibold">Precio:</span> ${rifa.precio}
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-semibold">Números:</span> {rifa.numerosVendidos}/{rifa.totalNumeros}
                        </div>
                      </div>
                      
                      <div className="border-t border-gray-500 pt-2 sm:pt-3 mb-3 sm:mb-4">
                        <p className="text-gray-400 text-xs sm:text-sm">Creado por: <span className="font-semibold text-gray-300">{rifa.creadorNombre}</span></p>
                      </div>
                      
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleVerRifa(rifa)}
                          className="flex-1 py-2 sm:py-3 bg-gray-700 hover:bg-gray-800 text-white font-bold rounded-lg transition text-sm sm:text-base"
                        >
                          <span className="hidden sm:inline">Ver Números y Ganadores</span>
                          <span className="sm:hidden">Ver Ganadores</span>
                        </button>
                        {renderBotonesCompartirRifa(rifa, { compact: true })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {!showFinalizadas && !hayBusqueda && rifasActivas.length === 0 && rifasFinalizadas.length === 0 && (
              <div className="text-center py-16">
                <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
                <p className="text-white text-xl">No hay rifas disponibles aún</p>
                <p className="text-purple-300 mt-2">¡Sé el primero en crear una!</p>
              </div>
            )}
            
            {/* Espacio antes del footer global */}
            <div className="mt-8" />
                  
      </main>
    );
  };

  // Rifa Loading Page - Página de carga específica para rifas
  const RifaLoadingPage = () => {
    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-400 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-white mb-4">Cargando rifa...</h2>
            <p className="text-purple-300 mb-4">ID: {rifaIdFromUrl}</p>
            <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-6 border border-purple-400">
              <p className="text-white text-sm">
                Estamos cargando la información de la rifa. 
                <br />
                Esto puede tomar unos segundos...
              </p>
            </div>
          </div>
        </div>
      </main>
    );
  };

  // Rifa Detail Page - Selección de números
  const RifaDetailPage = () => {
    const totalNumeros = parseInt(selectedRifa.totalNumeros);
    const numeros = Array.from({ length: totalNumeros }, (_, i) => i + 1);
    
    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <button
            onClick={() => navigateTo('/inicio')}
            className="flex items-center gap-2 text-white hover:text-yellow-400 transition text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            Volver a las rifas
          </button>
          <button
            onClick={handleActualizarRifa}
            disabled={refreshingRifa}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition text-sm sm:text-base"
            title="Actualizar números vendidos, pendientes y compras"
          >
            <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${refreshingRifa ? 'animate-spin' : ''}`} />
            {refreshingRifa ? 'Actualizando…' : 'Actualizar'}
          </button>
          {esCreadorDeRifa(selectedRifa, user) && (
            <button
              type="button"
              onClick={() => handleAbrirEditarRifa(selectedRifa)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition text-sm sm:text-base"
              title="Editar nombre, descripción, alias o link de Mercado Pago"
            >
              <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
              Editar rifa
            </button>
          )}
          {renderBotonesCompartirRifa(selectedRifa)}
        </div>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-4 sm:p-6 md:p-8 border border-purple-400 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4 mb-4">
            <div className="flex-1 w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-2">{selectedRifa.titulo}</h2>
              <p className="text-white text-sm sm:text-base md:text-lg mb-3 sm:mb-4">{selectedRifa.descripcion}</p>
              <p className="text-purple-300 text-xs sm:text-sm">Creado por: <span className="font-semibold text-white">{selectedRifa.creadorNombre}</span></p>
            </div>
            <CopiableAliasCbu
              alias={selectedRifa.alias}
              cbu={selectedRifa.cbu}
              className="text-sm sm:text-base md:text-lg w-full sm:w-auto sm:ml-4 break-all px-3 sm:px-4 py-2"
            />
          </div>

          {/* Premios con su foto (si la tienen) */}
          <div className="bg-black bg-opacity-30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-purple-300 text-sm mb-3">Premios</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => {
                const imgsPremios = Array.isArray(selectedRifa.imagenesPremios) ? selectedRifa.imagenesPremios : [];
                const imgPremio = imgsPremios[index];
                const imgUrl = imgPremio?.url || (index === 0 ? selectedRifa.imagenUrl : null);
                return (
                  <div key={index} className="flex gap-3 items-center bg-white/5 rounded-lg p-2 border border-purple-500/30">
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt={`Foto del ${index + 1}° premio`}
                        className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md border border-yellow-400/60 shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-md border border-purple-500/40 bg-black/40 shrink-0 flex items-center justify-center text-purple-300 text-xs">
                        Sin foto
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-yellow-400 font-bold text-base sm:text-lg leading-tight">{index + 1}° Premio</p>
                      <p className="text-white font-semibold text-sm sm:text-base break-words">{premio}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-4 sm:mb-6">
            {renderBotonesCompartirRifa(selectedRifa, { fullWidth: true })}
          </div>

          <div className={`grid gap-2 sm:gap-4 mb-4 sm:mb-6 ${
            selectedRifa.tipo === 'sorteo'
              ? 'grid-cols-2'
              : 'grid-cols-2 md:grid-cols-3'
          }`}>
            {selectedRifa.tipo !== 'sorteo' && (
              <div className="bg-black bg-opacity-30 rounded-lg p-4">
                <p className="text-purple-300 text-sm">Precio</p>
                <p className="text-white font-bold">${selectedRifa.precio}</p>
              </div>
            )}
            <div className="bg-black bg-opacity-30 rounded-lg p-4">
              <p className="text-purple-300 text-sm">Disponibles</p>
              <p className="text-white font-bold">{totalNumeros - selectedRifa.numerosVendidos}</p>
            </div>
            <div className="bg-black bg-opacity-30 rounded-lg p-4">
              <p className="text-purple-300 text-sm">
                {selectedRifa.tipoFinalizacion === 'fecha' ? 'Sorteo' : 'Finaliza'}
              </p>
              <p className="text-white font-bold">
                {selectedRifa.tipoFinalizacion === 'fecha'
                  ? new Date(selectedRifa.fechaSorteo).toLocaleDateString()
                  : 'Cuando se vendan todos'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Sección de Gestión de Pagos Pendientes (solo para el creador) */}
        {(user && (selectedRifa.creadorId === user.id || selectedRifa.creadorId === user.uid)) && (selectedRifa.numerosPendientes || []).length > 0 && (
          <div className="bg-orange-900 bg-opacity-50 rounded-xl p-6 border border-orange-400 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-orange-400">⏳</span>
              Gestionar Pagos Pendientes
            </h3>
            <p className="text-orange-200 mb-4">
              Tenés <span className="font-bold text-yellow-400">{(selectedRifa.numerosPendientes || []).length}</span> número(s) pendiente(s). Podés confirmar uno por uno, todos juntos, o liberar un número (vuelve a verde/disponible) si la reserva fue un error.
            </p>

            <div className="flex flex-col gap-2 mb-4">
              {(selectedRifa.numerosPendientes || []).map((num, idx) => (
                <div
                  key={`pendiente-${num}-${idx}`}
                  className="flex flex-wrap items-center justify-between gap-2 bg-black/30 rounded-lg px-3 py-2 border border-orange-500/40"
                >
                  <span className="bg-orange-500 text-white px-3 py-1 rounded-lg font-bold min-w-[3rem] text-center">
                    {num}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`¿Confirmar solo el número ${num}?\n\nSe marcará como vendido y se avisará al comprador.`)) {
                          handleConfirmarPago(selectedRifa.id, [num]);
                        }
                      }}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold"
                    >
                      Confirmar este
                    </button>
                    <button
                      type="button"
                      onClick={() => handleLiberarNumeroPendiente(selectedRifa.id, num)}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-semibold border border-orange-400/50"
                    >
                      Liberar (volver disponible)
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-orange-200 mb-4">
              <p className="text-sm">Total si confirmás todos: <span className="font-bold text-yellow-400">${((selectedRifa.numerosPendientes?.length || 0) * parseFloat(selectedRifa.precio)).toFixed(2)}</span></p>
            </div>

            <button
              type="button"
              onClick={() => {
                if (confirm(`¿Confirmar TODOS los pagos pendientes?\n\nNúmeros: ${(selectedRifa.numerosPendientes || []).join(', ')}\nTotal: $${((selectedRifa.numerosPendientes?.length || 0) * parseFloat(selectedRifa.precio)).toFixed(2)}`)) {
                  handleConfirmarPago(selectedRifa.id, selectedRifa.numerosPendientes || []);
                }
              }}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Confirmar todos los pagos
            </button>
          </div>
        )}

        {/* Mostrar ganadores siempre que haya alguno - MOVER ESTO ARRIBA */}
        {(() => {
          // NO filtrar por expiración - mostrar TODOS los ganadores permanentemente
          const ganadoresValidos = ganadores.filter(g => {
            return g.rifaId === selectedRifa.id;
          });
          
          // Ordenar ganadores por premioIndex para mostrar en orden
          const ganadoresOrdenados = ganadoresValidos.sort((a, b) => {
            if (a.premioIndex !== undefined && b.premioIndex !== undefined) {
              return a.premioIndex - b.premioIndex;
            }
            return 0;
          });
          
          const premiosRifa = selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : []);
          
          return ganadoresOrdenados.length > 0 ? (
            <div className="bg-yellow-900 bg-opacity-50 rounded-xl p-6 border border-yellow-400 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
                <span>👑</span>
                  Ganadores {selectedRifa.finalizada ? '(Rifa Finalizada)' : `(${ganadoresOrdenados.length} de ${premiosRifa.length} sorteos)`}
              </h4>
              </div>
              <div className="space-y-3">
                {ganadoresOrdenados.map(ganador => {
                // Buscar la compra para obtener más datos
                const compraGanadora = compras.find(c => 
                  c.rifaId === ganador.rifaId && 
                  c.numeros.includes(ganador.numeroGanador)
                );
                
                return (
                  <div key={ganador.id} className="bg-yellow-800 bg-opacity-30 rounded-lg p-4 border border-yellow-500">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">👑</span>
                        <p className="text-white font-bold text-xl">
                          Número Ganador: <span className="text-yellow-400">{ganador.numeroGanador}</span>
                        </p>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 bg-black bg-opacity-30 rounded-lg p-3">
                <div>
                          <p className="text-yellow-200 text-sm mb-1">Ganador/a</p>
                          <p className="text-white font-bold text-lg">{ganador.ganadorNombre}</p>
                </div>
                <div>
                          <p className="text-yellow-200 text-sm mb-1">Premio</p>
                          <p className="text-white font-bold text-lg">{ganador.rifaPremio}</p>
                </div>
                        {compraGanadora && (
                          <>
                            {compraGanadora.compradorDireccion && (
                              <div className="md:col-span-2">
                                <p className="text-yellow-200 text-sm mb-1">Dirección</p>
                                <p className="text-white font-semibold">{compraGanadora.compradorDireccion}</p>
                              </div>
                            )}
                          </>
                        )}
                        {/* WhatsApp y correo solo visibles para el creador de la rifa */}
                        {user && selectedRifa && user.id === selectedRifa.creadorId && (ganador.ganadorEmail || ganador.ganadorTelefono || compraGanadora?.compradorEmail || compraGanadora?.compradorTelefono) && (
                          <div className="md:col-span-2 pt-2 mt-2 border-t border-yellow-600 border-opacity-50">
                            <p className="text-yellow-200 text-sm mb-2">Contactar al ganador</p>
                            <div className="flex flex-wrap gap-3">
                              {(ganador.ganadorEmail || compraGanadora?.compradorEmail) && (
                                <a
                                  href={`mailto:${ganador.ganadorEmail || compraGanadora?.compradorEmail}`}
                                  className="inline-flex items-center gap-1 text-white hover:text-yellow-300 transition text-sm"
                                >
                                  <Mail className="w-4 h-4" />
                                  {ganador.ganadorEmail || compraGanadora?.compradorEmail}
                                </a>
                              )}
                              {(ganador.ganadorTelefono || compraGanadora?.compradorTelefono) && (
                                <a
                                  href={`https://wa.me/${(ganador.ganadorTelefono || compraGanadora?.compradorTelefono || '').replace(/[^0-9]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-white hover:text-yellow-300 transition text-sm"
                                >
                                  <Phone className="w-4 h-4" />
                                  WhatsApp
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          ) : null;
        })()}

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-purple-400">
          <h3 className="text-2xl font-bold text-white mb-6">
            {selectedRifa.finalizada ? 'Números y Ganadores' : 'Selecciona tus números'}
          </h3>
          {/* En rifa finalizada: mostrar número ganador y nombre arriba del tablero */}
          {selectedRifa.finalizada && (() => {
            const gans = (ganadores || []).filter(g => g.rifaId === selectedRifa.id);
            return gans.length > 0 ? (
            <div className="mb-6 p-4 bg-yellow-900 bg-opacity-40 rounded-lg border border-yellow-500">
              <p className="text-yellow-200 text-sm mb-2 font-semibold">Ganadores</p>
              <div className="flex flex-wrap gap-4">
                {gans.sort((a, b) => (a.premioIndex ?? 0) - (b.premioIndex ?? 0)).map(g => (
                  <div key={g.id} className="flex items-center gap-2">
                    <span className="text-2xl">👑</span>
                    <span className="text-white font-bold">Nº {g.numeroGanador}</span>
                    <span className="text-yellow-200">—</span>
                    <span className="text-white">{g.ganadorNombre}</span>
                  </div>
                ))}
              </div>
            </div>
            ) : null;
          })()}
          
            <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      <span className="text-white text-sm">Disponible</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded"></div>
                      <span className="text-white text-sm">Vendido</span>
                    </div>
                    <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-white text-sm">Seleccionado</span>
                      </div>
                    <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span className="text-white text-sm">Pendiente</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">👑</span>
                      <span className="text-white text-sm">Ganador</span>
                    </div>
                  </div>

                  {/* Solo mostrar instrucciones y tooltip si NO es sorteo sin token */}
                  {!(selectedRifa.tipo === 'sorteo' && !sorteoTokenValid) && (
                    <>
                      {/* Botón de instrucciones */}
                      <div className="mb-6 text-center">
                        <button
                          onClick={iniciarInstrucciones}
                          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg font-bold text-sm md:text-lg shadow-lg transition-all duration-300 transform hover:scale-105"
                        >
                          📚 Ver Instrucciones de Compra
                        </button>
                      </div>

                      {/* Tooltip animado para selección de números */}
                      <div className="mb-6 text-center">
                        <div className="inline-block animate-bounce">
                          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg shadow-xl font-bold text-sm md:text-lg border-2 border-yellow-400">
                            🎯 ¡Haz clic en los números para seleccionarlos!
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Mensaje para sorteos sin token (solo vista) */}
                  {selectedRifa.tipo === 'sorteo' && !sorteoTokenValid && (
                    <div className="mb-6 text-center">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-lg shadow-xl font-bold text-lg border-2 border-blue-400 inline-block">
                        👁️ Modo Vista - Solo puedes ver los números disponibles
                        <p className="text-sm font-normal mt-2">Escanea el QR del sorteo para seleccionar un número</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-1.5 sm:gap-2 mb-6 sm:mb-8 w-full px-1">
                    {numeros.map(numero => {
              const isVendido = selectedRifa.numerosReservados.includes(numero);
              const isPendiente = (selectedRifa.numerosPendientes || []).includes(numero);
                      const isSelected = selectedNumbers.includes(numero);
                      
                      // Verificar si este número es ganador
                      const numeroGanador = ganadores.find(g => g.rifaId === selectedRifa.id && g.numeroGanador === numero);
                      const isGanador = !!numeroGanador;
                      
                      // Si es sorteo sin token, modo solo lectura
                      const esSoloLectura = selectedRifa.tipo === 'sorteo' && !sorteoTokenValid;
                      
                      return (
                        <div
                          key={numero}
                          onClick={() => {
                            // Si está vendido, mostrar información del comprador
                            if (isVendido) {
                              handleToggleNumber(numero);
                            }
                          }}
                          className={`
                    aspect-square rounded-md sm:rounded-lg font-bold text-sm sm:text-base md:text-sm transition-all relative overflow-hidden flex items-center justify-center
                    ${isGanador
                      ? 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-white border-2 border-yellow-300 shadow-lg ring-2 ring-yellow-200'
                      : isVendido 
                        ? 'bg-red-500 opacity-70 cursor-pointer hover:opacity-90' 
                        : isPendiente
                          ? 'bg-orange-500 opacity-70'
                          : isSelected && !esSoloLectura
                            ? 'bg-yellow-500 text-black scale-105 sm:scale-110 shadow-lg' 
                            : 'bg-green-500 text-white'
                    }
                    ${esSoloLectura ? 'cursor-default' : (isVendido ? 'cursor-pointer' : 'cursor-pointer')}
                  `}
                >
                          {isGanador && (
                            <span className="absolute top-0 left-0 right-0 text-center text-2xl sm:text-3xl leading-none z-10 drop-shadow-md" title="Número ganador">👑</span>
                          )}
                          <span className={isGanador ? 'mt-5 sm:mt-6 block text-sm font-bold' : ''}>{numero}</span>
                          {!esSoloLectura && !isPendiente && !isGanador && !isVendido && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Si es sorteo con token, siempre reemplazar la selección
                                if (selectedRifa.tipo === 'sorteo' && sorteoTokenValid) {
                                  setSelectedNumbers([numero]);
                                } else {
                                  handleToggleNumber(numero);
                                }
                              }}
                              disabled={isPendiente || isGanador || isVendido}
                              className="absolute inset-0 w-full h-full"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

          {!selectedRifa.finalizada && selectedNumbers.length > 0 && !(selectedRifa.tipo === 'sorteo' && !sorteoTokenValid) && (
            <div className="bg-black bg-opacity-40 rounded-lg p-6 mb-6">
              <h4 className="text-white font-bold mb-3">Números seleccionados ({selectedNumbers.length}):</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedNumbers.sort((a, b) => a - b).map(num => (
                  <span key={num} className="bg-yellow-500 text-black px-3 py-1 rounded-full font-bold">
                    {num}
                  </span>
                ))}
              </div>
              <div className="flex justify-between items-center">
                {selectedRifa.tipo !== 'sorteo' && (
                <div className="text-white">
                  <p className="text-sm text-purple-300">Total a pagar:</p>
                  <p className="text-3xl font-bold text-yellow-400">
                    ${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}
                  </p>
                </div>
                )}
                {selectedRifa.tipo === 'sorteo' && sorteoTokenValid && (
                  <div className="text-white">
                    <p className="text-sm text-purple-300">Sorteo Gratuito</p>
                    <p className="text-lg font-bold text-green-400">
                      Selecciona UN número
                    </p>
                    {selectedNumbers.length > 0 && (
                      <p className="text-xs text-yellow-300 mt-1">
                        Número seleccionado: {selectedNumbers[0]}
                      </p>
                    )}
                  </div>
                )}
                {selectedRifa.tipo === 'sorteo' && sorteoTokenValid ? (
                  <button
                    onClick={async () => {
                      // Verificar que el token siga siendo válido antes de confirmar
                      try {
                        const rifaRef = doc(db, 'rifas', selectedRifa.id);
                        const rifaSnap = await getDoc(rifaRef);
                        
                        if (rifaSnap.exists()) {
                          const rifaData = rifaSnap.data();
                          const tokensValidos = rifaData.tokensValidos || [];
                          
                          // Si el token ya no está en la lista, fue invalidado
                          if (!tokensValidos.includes(sorteoTokenValid)) {
                            alert('❌ Este link ya fue usado. Por favor, escanea el QR nuevamente para obtener un link válido.');
                            setSorteoTokenValid(null);
                            navigateTo('/inicio');
                            return;
                          }
                        }
                      } catch (error) {
                        console.error('Error al verificar token:', error);
                        alert('❌ Error al verificar el link. Por favor, intenta de nuevo.');
                        return;
                      }
                      
                      if (selectedNumbers.length === 0) {
                        alert('Debes seleccionar un número');
                        return;
                      }
                      
                      if (selectedNumbers.length > 1) {
                        alert('❌ Solo puedes seleccionar UN número en sorteos');
                        return;
                      }
                      
                      // Para sorteos con token, confirmar directamente sin pago
                      const confirmacion = confirm(
                        `¿Confirmar la selección de este número?\n\n` +
                        `Número: ${selectedNumbers[0]}\n\n` +
                        `⚠️ Este link solo funciona UNA vez. Después de confirmar, no podrás usarlo nuevamente.`
                      );
                      
                      if (!confirmacion) return;
                      
                      // Marcar números como reservados directamente (gratis)
                      await handleConfirmPayment('sorteo');
                    }}
                    className="px-5 py-3 md:px-8 md:py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg flex items-center gap-2 transition text-sm md:text-base"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Confirmar Selección
                  </button>
                ) : (
                <button
                  onClick={handleProceedToPayment}
                    className="px-5 py-3 md:px-8 md:py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg flex items-center gap-2 transition text-sm md:text-base"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Proceder al Pago
                </button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    );
  };

  // Datos Personales Page
  const DatosPersonalesPage = () => {
    return (
      <main className="w-full max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <button
          onClick={() => setCurrentPage('rifa-detail')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-4 sm:mb-6 transition text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          Volver a la selección
        </button>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-4 sm:p-6 md:p-8 border border-purple-400">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-4 sm:mb-6 md:mb-8 text-center">Tus Datos Personales</h2>
          
          <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-white mb-4">Resumen de tu Compra</h3>
            <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-purple-300 text-sm">Rifa</p>
                <p className="text-white font-bold text-lg">{selectedRifa.titulo}</p>
                <p className="text-purple-300 text-sm mt-2">Premio</p>
                  <p className="text-white font-bold">{selectedRifa.premio}</p>
                </div>
                <div>
                <p className="text-purple-300 text-sm">Números seleccionados</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedNumbers.sort((a, b) => a - b).map(num => (
                    <span key={num} className="bg-yellow-500 text-black px-3 py-1 rounded-lg font-bold">
                      {num}
                    </span>
                  ))}
                </div>
                <p className="text-purple-300 text-sm">Total a pagar</p>
                <p className="text-yellow-400 font-bold text-2xl">${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}</p>
                  </div>
            </div>
          </div>

          <div className="bg-black bg-opacity-30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-6">Información de Contacto</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 font-semibold">Nombre *</label>
                <input
                  type="text"
                  value={buyerForm.nombre}
                  onChange={(e) => setBuyerForm({...buyerForm, nombre: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Juan"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold">Apellido *</label>
                <input
                  type="text"
                  value={buyerForm.apellido}
                  onChange={(e) => setBuyerForm({...buyerForm, apellido: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Pérez"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold">Correo Electrónico *</label>
                <input
                  type="email"
                  value={buyerForm.email}
                  onChange={(e) => setBuyerForm({...buyerForm, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="juan@ejemplo.com"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold">Número de Teléfono *</label>
                <input
                  type="tel"
                  value={buyerForm.telefono}
                  onChange={(e) => setBuyerForm({...buyerForm, telefono: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="+54 9 11 1234-5678"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white mb-2 font-semibold">Dirección *</label>
                <input
                  type="text"
                  value={buyerForm.direccion}
                  onChange={(e) => setBuyerForm({...buyerForm, direccion: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Av. Corrientes 1234, CABA"
                  required
                />
            </div>
          </div>
            <p className="text-purple-300 text-sm mt-4">* Campos obligatorios</p>
                  </div>

            <button
            onClick={handleProceedToConfirmation}
              className="w-full mt-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-xl transition"
            >
              <CheckCircle className="w-6 h-6" />
            Continuar a Confirmación
            </button>
        </div>
      </main>
    );
  };

  // Payment Page
  const PaymentPage = () => {
    const total = selectedNumbers.length * parseFloat(selectedRifa.precio);
    
    // Debug: Verificar datos de la rifa
    console.log('=== DEBUG PAYMENT PAGE ===');
    console.log('Rifa seleccionada:', selectedRifa);
    console.log('Alias:', selectedRifa.alias);
    console.log('CBU:', selectedRifa.cbu);
    console.log('Creador:', selectedRifa.creadorNombre);
    console.log('========================');
    
    return (
      <main className="w-full max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <button
          onClick={() => navigateTo(`/rifa/${selectedRifa.id}`)}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-4 sm:mb-6 transition text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          Volver a la selección
        </button>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-4 sm:p-6 md:p-8 border border-purple-400">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-4 sm:mb-6 md:mb-8 text-center">Confirmar Compra</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Detalles de la Rifa</h3>
              <div className="bg-black bg-opacity-30 rounded-lg p-6 space-y-3">
                <div>
                  <p className="text-purple-300 text-sm">Rifa</p>
                  <p className="text-white font-bold">{selectedRifa.titulo}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Premio</p>
                  <p className="text-white font-bold">{selectedRifa.premio}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Precio por número</p>
                  <p className="text-white font-bold">${selectedRifa.precio}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Sorteo</p>
                  <p className="text-white font-bold">{new Date(selectedRifa.fechaSorteo).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-4">Tus Números</h3>
              <div className="bg-black bg-opacity-30 rounded-lg p-6">
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedNumbers.sort((a, b) => a - b).map(num => (
                    <span key={num} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold text-lg">
                      {num}
                    </span>
                  ))}
                </div>
                <div className="border-t border-purple-400 pt-4">
                  <div className="flex justify-between text-white mb-2">
                    <span>Cantidad de números:</span>
                    <span className="font-bold">{selectedNumbers.length}</span>
                  </div>
                  <div className="flex justify-between text-white mb-4">
                    <span>Precio unitario:</span>
                    <span className="font-bold">${selectedRifa.precio}</span>
                  </div>
                  <div className="flex justify-between items-center bg-yellow-500 bg-opacity-20 rounded-lg p-4">
                    <span className="text-yellow-400 font-bold text-lg">Total a pagar:</span>
                    <span className="text-yellow-400 font-bold text-3xl">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

            <div>
            <h3 className="text-xl font-bold text-white mb-4">Datos del Comprador</h3>
              <div className="bg-black bg-opacity-30 rounded-lg p-6 space-y-3">
              <div>
                  <p className="text-purple-300 text-sm">Nombre Completo</p>
                  <p className="text-white font-bold">{buyerForm.nombre} {buyerForm.apellido}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm">Email</p>
                  <p className="text-white font-bold">{buyerForm.email}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm">Teléfono</p>
                  <p className="text-white font-bold">{buyerForm.telefono}</p>
          </div>
                <div>
                  <p className="text-purple-300 text-sm">Dirección</p>
                  <p className="text-white font-bold">{buyerForm.direccion}</p>
                </div>
              </div>
            </div>
          </div>


          <div className="bg-black bg-opacity-30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              💳 Datos para Transferencia
            </h3>
            <div className="bg-purple-900 bg-opacity-50 rounded-lg p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏦</span>
              </div>
                <div>
                  <h4 className="text-white font-bold text-lg">Transferencia Bancaria</h4>
                  <p className="text-purple-300 text-sm">Realiza la transferencia a los siguientes datos:</p>
            </div>
          </div>

              <div className="bg-green-900 bg-opacity-50 rounded-lg p-4 mb-4">
                <h5 className="text-green-400 font-bold mb-3">📋 Datos del Creador de la Rifa</h5>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-green-200">Creador:</span>
                    <span className="text-white font-bold">{selectedRifa.creadorNombre}</span>
                  </div>
                  
                  {/* WhatsApp */}
                  {selectedRifa.whatsapp && (
                    <div className="bg-green-800 bg-opacity-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-green-200 font-semibold">📱 WhatsApp:</span>
                        <span className="text-white font-bold text-lg">{selectedRifa.whatsapp}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Alias */}
                  {selectedRifa.alias && (
                    <div className="bg-green-800 bg-opacity-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-green-200 font-semibold">Alias:</span>
                        <div className="flex items-center gap-2">
                          <TextoCopiable
                            texto={selectedRifa.alias}
                            etiqueta="Alias"
                            className="text-white font-bold text-xl bg-green-700 px-3 py-1 rounded underline decoration-dotted"
                          />
                        </div>
                      </div>
                </div>
              )}
                  
                  {/* CBU */}
                  {selectedRifa.cbu && (
                    <div className="bg-green-800 bg-opacity-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-green-200 font-semibold">CBU:</span>
                        <div className="flex items-center gap-2">
<TextoCopiable
                            texto={selectedRifa.cbu}
                            etiqueta="CBU"
                            className="text-white font-bold text-lg bg-green-700 px-3 py-1 rounded underline decoration-dotted"
                          />
                        </div>
            </div>
          </div>
                  )}
                  
                  {/* Verificar que al menos uno esté disponible */}
                  {!selectedRifa.alias && !selectedRifa.cbu && (
                    <div className="bg-red-900 bg-opacity-50 rounded-lg p-3">
                      <p className="text-red-200 text-center font-semibold">
                        ⚠️ El creador no ha proporcionado datos de transferencia
                      </p>
            </div>
                  )}
                  
                  <div className="flex justify-between items-center bg-yellow-900 bg-opacity-50 rounded-lg p-3">
                    <span className="text-yellow-200 font-semibold">Monto a transferir:</span>
                    <span className="text-yellow-400 font-bold text-2xl">${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}</span>
          </div>
                </div>
              </div>

              {/* Botón de WhatsApp para contactar al creador */}
              {selectedRifa.whatsapp && (
                <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 mb-4 border-2 border-green-400">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">📱</div>
                      <div>
                        <p className="text-white font-bold">¿Tienes dudas?</p>
                        <p className="text-green-200 text-sm">Contacta al creador por WhatsApp</p>
                      </div>
                    </div>
                    <a
                      href={`https://wa.me/${selectedRifa.whatsapp.replace(/[^0-9]/g, '')}?text=Hola! Vi tu rifa "${selectedRifa.titulo}" y tengo algunas dudas sobre la compra. ¿Podrías ayudarme?`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-white text-green-700 font-bold rounded-lg hover:bg-green-100 transition flex items-center gap-2"
                    >
                      📱 WhatsApp
                    </a>
                  </div>
                </div>
              )}

              {/* Título de Métodos de Pago */}
              <div className="bg-gradient-to-r from-purple-700 to-indigo-700 rounded-xl p-4 mb-6 border-2 border-purple-400">
                <h3 className="text-white font-bold text-2xl text-center mb-2">
                  💰 Métodos de Pago
                </h3>
                <p className="text-purple-200 text-center text-sm">
                  Elige cómo quieres pagar tus números
                </p>
            </div>

              {/* Mercado Pago: solo 1 número (el link fijo cobra un precio unitario) */}
              {selectedRifa.linkMercadoPago && selectedNumbers.length > 1 && (
                <div className="bg-sky-900/50 border-2 border-sky-400 rounded-xl p-4 mb-6">
                  <p className="text-sky-100 text-sm">
                    <span className="font-bold">💳 Mercado Pago no está disponible para varios números.</span>{' '}
                    El link de pago es por <span className="font-semibold text-white">un solo número</span> (${parseFloat(selectedRifa.precio).toFixed(2)}).
                    Elegiste <span className="font-semibold text-white">{selectedNumbers.length} números</span>
                    {' '}(${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}) — usá <span className="font-semibold text-white">transferencia</span> abajo.
                  </p>
                </div>
              )}

              {selectedRifa.linkMercadoPago && selectedNumbers.length === 1 && (
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 mb-6 border-2 border-blue-400">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="text-4xl">💳</div>
                      <div>
                        <h3 className="text-white font-bold text-xl">
                          Opción 1: Mercado Pago
                        </h3>
                        <p className="text-blue-200 text-sm">
                          1 número — el creador confirma el pago
                        </p>
                      </div>
                    </div>
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      REQUIERE CONFIRMACIÓN
                    </span>
                  </div>
                  
                  <div className="bg-blue-800 bg-opacity-50 rounded-lg p-4 mb-4">
                    <p className="text-blue-200 text-sm mb-2">Monto a pagar (1 número):</p>
                    <p className="text-yellow-400 font-bold text-3xl">
                      ${parseFloat(selectedRifa.precio).toFixed(2)}
                    </p>
                    <p className="text-blue-300 text-xs mt-2">
                      Número: {selectedNumbers[0]}
                    </p>
                  </div>

                  <div className="bg-amber-900/50 border border-amber-500 rounded-lg p-3 mb-4">
                    <p className="text-amber-100 text-xs sm:text-sm font-semibold mb-1">⚠️ Aviso Mercado Pago</p>
                    <p className="text-amber-50/95 text-xs">
                      Mercado Pago puede aplicar <span className="font-semibold">comisión</span> al vendedor e <span className="font-semibold">intereses</span> al comprador si pagás en cuotas. Los costos dependen del medio de pago elegido.
                    </p>
                  </div>
                  
                  <a
                    href={selectedRifa.linkMercadoPago}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 bg-white text-blue-700 font-bold rounded-lg hover:bg-blue-50 transition text-center text-lg shadow-lg mb-3"
                  >
                    💳 Abrir pago en Mercado Pago
                  </a>

                  <div className="bg-yellow-900/50 border-2 border-yellow-500 rounded-lg p-3 mb-3">
                    <p className="text-yellow-200 text-sm font-semibold flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      Después de pagar, tocá el botón de abajo. El número queda PENDIENTE (naranja) hasta que el creador confirme.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleConfirmPayment('mercadopago')}
                    className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-xl transition shadow-lg"
                  >
                    <CheckCircle className="w-6 h-6" />
                    Ya pagué — reservar número
                  </button>
                </div>
              )}

              {/* Opción 2: Transferencia Manual (SIEMPRE DISPONIBLE) */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-6 mb-4 border-2 border-purple-400">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">🏦</div>
                    <div>
                      {selectedRifa.linkMercadoPago && selectedNumbers.length === 1 ? (
                        <>
                          <h3 className="text-white font-bold text-xl">Opción 2: Transferencia</h3>
                          <p className="text-purple-200 text-sm">Transferencia bancaria a Alias/CBU</p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-white font-bold text-xl">Transferencia</h3>
                          <p className="text-purple-200 text-sm">Transferencia bancaria a Alias/CBU</p>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    REQUIERE CONFIRMACIÓN
                  </span>
                </div>

                <div className="bg-purple-800 bg-opacity-50 rounded-lg p-4 mb-4">
                  <p className="text-purple-200 text-sm mb-2">Monto a transferir:</p>
                  <p className="text-yellow-400 font-bold text-3xl">
                    ${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}
                  </p>
                </div>

                <div className="bg-yellow-900 bg-opacity-50 rounded-lg p-4 mb-4 border-2 border-yellow-500">
                  <p className="text-yellow-200 text-sm font-semibold flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Los números quedarán en estado "Pendiente" (NARANJA) hasta que el creador confirme tu pago
                  </p>
          </div>

                      <button 
                  onClick={() => handleConfirmPayment('transferencia')}
                  className="w-full py-4 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-xl transition shadow-lg"
            >
              <CheckCircle className="w-6 h-6" />
              Reservar con Transferencia
                      </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  };

  // Mis Rifas Page - Ver rifas creadas por el usuario (estado Finalizadas en parent para que no se pierda al re-renderizar)
  const MisRifasPage = () => {
    const showFinalizadas = misRifasShowFinalizadas;
    const setShowFinalizadas = setMisRifasShowFinalizadas;
    // Filtrar rifas por creadorId - comparar tanto con user.id como con user.uid
    const misRifas = rifas.filter(r => 
      r.creadorId === user?.id || 
      r.creadorId === user?.uid || 
      (r.creadorNombre && user?.name && r.creadorNombre.toLowerCase() === user.name.toLowerCase())
    );
    const misRifasActivas = misRifas.filter(r => !r.finalizada);
    const misRifasFinalizadas = misRifas.filter(r => r.finalizada);
    
    if (!user) {
    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
          <div className="text-center py-8 sm:py-16 bg-white bg-opacity-10 backdrop-blur-md rounded-xl border border-purple-400 mx-2 sm:mx-0">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4">🔐 Acceso Requerido</h2>
            <p className="text-purple-300 mb-4 sm:mb-6 text-sm sm:text-base px-4">Necesitas iniciar sesión para ver tus rifas</p>
            <button 
              onClick={() => navigateTo('/login')}
              className="px-6 sm:px-8 py-2 sm:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold transition text-sm sm:text-base"
            >
              Iniciar Sesión
            </button>
          </div>
        </main>
      );
    }

    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <button 
            onClick={() => navigateTo('/inicio')}
            className="flex items-center gap-2 text-white hover:text-yellow-400 transition text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            Volver al inicio
          </button>
          <button
            onClick={handleActualizarMisRifas}
            disabled={refreshingMisRifas}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition text-sm sm:text-base"
            title="Actualizar rifas, pagos pendientes y compras"
          >
            <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${refreshingMisRifas ? 'animate-spin' : ''}`} />
            {refreshingMisRifas ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>

        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
            <div className="w-full sm:w-auto">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
                Mis Rifas
              </h2>
              <p className="text-purple-300 text-sm sm:text-base md:text-lg">
                Hola <span className="text-yellow-400 font-bold">{user?.name || 'Usuario'}</span>, aquí puedes ver todas tus rifas activas y finalizadas
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={() => navigateTo('/gestionar-rifas')}
                className="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-bold flex items-center gap-2 sm:gap-3 transition shadow-lg text-sm sm:text-base"
              >
                <User className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Ver Compradores</span>
                <span className="sm:hidden">Compradores</span>
              </button>
            </div>
          </div>
        </div>
                  
        {/* Sección de Pagos Pendientes Global */}
        {(() => {
          const rifasConPendientes = misRifasActivas.filter(rifa => (rifa.numerosPendientes || []).length > 0);
          const totalPendientes = rifasConPendientes.reduce((total, rifa) => total + (rifa.numerosPendientes?.length || 0), 0);
          
          if (totalPendientes > 0) {
            return (
              <div className="bg-orange-900 bg-opacity-50 rounded-xl p-6 border border-orange-400 mb-8">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <span className="text-orange-400">⏳</span>
                    Pagos Pendientes de Confirmación
                  </h3>
                <p className="text-orange-200 mb-4">
                  Tienes <span className="font-bold text-yellow-400">{totalPendientes}</span> números pendientes de confirmación en {rifasConPendientes.length} rifa(s)
                </p>
                
                <div className="grid gap-4">
                  {rifasConPendientes.map(rifa => {
                    const precioNum = parseFloat(rifa.precio) || 0;
                    const totalPendRifa = (rifa.numerosPendientes?.length || 0) * precioNum;

                    return (
                      <div key={rifa.id} className="bg-orange-800 bg-opacity-50 rounded-lg p-4 border border-orange-500/30">
                        <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                          <div>
                            <h4 className="text-white font-bold text-lg">{rifa.titulo}</h4>
                            <p className="text-orange-200 text-sm">
                              {(rifa.numerosPendientes?.length || 0)} números esperando confirmación
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-orange-200 text-xs uppercase tracking-wide">Total si confirmás todos</p>
                            <p className="text-yellow-300 font-bold text-lg">${totalPendRifa.toFixed(2)}</p>
                          </div>
                        </div>

                        <p className="text-orange-100/90 text-sm mb-3">
                          Podés confirmar cada número por separado, liberar uno si hubo error, o confirmar todos de una vez.
                        </p>

                        <div className="flex flex-col gap-2 mb-4">
                          {(rifa.numerosPendientes || []).map((num, idx) => (
                            <div
                              key={`pendiente-${num}-${idx}`}
                              className="flex flex-wrap items-center justify-between gap-2 bg-black/30 rounded-lg px-3 py-2.5 border border-orange-500/35"
                            >
                              <span className="bg-orange-500 text-white px-3 py-1.5 rounded-lg font-bold min-w-[2.75rem] text-center">
                                {num}
                              </span>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `¿Confirmar el pago del número ${num}?\n\nSe marcará como vendido y se avisará al comprador por email si figura.`
                                      )
                                    ) {
                                      handleConfirmarPago(rifa.id, [num]);
                                    }
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition"
                                >
                                  <CheckCircle className="w-4 h-4 shrink-0" />
                                  Confirmar este
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleLiberarNumeroPendiente(rifa.id, num)}
                                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold border border-orange-400/45 transition"
                                >
                                  Liberar
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `¿Confirmar TODOS los pagos pendientes de esta rifa?\n\nNúmeros: ${(rifa.numerosPendientes || []).join(', ')}\nTotal: $${totalPendRifa.toFixed(2)}`
                                )
                              ) {
                                handleConfirmarPago(rifa.id, rifa.numerosPendientes || []);
                              }
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition"
                          >
                            <CheckCircle className="w-5 h-5" />
                            Confirmar todos
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVerRifa(rifa)}
                            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
                          >
                            Ver detalles
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
            );
          }
          return null;
        })()}

        {/* Rifas Activas */}
        {misRifasActivas.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2 bg-white/5 rounded-lg px-4 py-2">
              <span className="w-3 h-3 bg-green-400 rounded-full inline-block"></span>
              Activas ({misRifasActivas.length})
            </h3>
            <div className="space-y-6">
              {misRifasActivas.map(rifa => {
                const comprasRifa = compras.filter(c => c.rifaId === rifa.id);
                const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
                const totalRecaudado = rifa.numerosVendidos * parseFloat(rifa.precio);
                
                return (
                  <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-6 border border-purple-400">
                    <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-2xl font-bold text-yellow-400">{rifa.titulo}</h3>
                          <CopiableAliasCbu alias={rifa.alias} cbu={rifa.cbu} className="ml-4" />
                        </div>
                        <p className="text-white mb-2">{rifa.descripcion}</p>
                        <p className="text-purple-300 text-sm">
                          Creada el {new Date(rifa.fechaCreacion).toLocaleDateString()}
                        </p>
                        {/* Botones de sorteo debajo de la fecha */}
                        {(() => { const premiosList = rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : []); const totalSorteados = ganadores.filter(g => g.rifaId === rifa.id).length; return !rifa.finalizada || totalSorteados < premiosList.length; })() && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full my-3">
                            {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => {
                              const ganadoresRifa = ganadores.filter(g => g.rifaId === rifa.id);
                              const premioYaSorteado = ganadoresRifa.some(g => {
                                if (g.premioIndex !== undefined && g.premioIndex !== null) {
                                  return g.premioIndex === index;
                                }
                                const premioNormalizado = premio.trim().toLowerCase();
                                const ganadorPremio = (g.rifaPremio || '').trim().toLowerCase();
                                const hayOtroGanadorConEsteIndice = ganadoresRifa.some(otroG => 
                                  otroG.premioIndex === index && otroG.id !== g.id
                                );
                                return ganadorPremio === premioNormalizado && !hayOtroGanadorConEsteIndice;
                              });
                              
                              return (
                                <button
                                  key={index}
                                  onClick={async () => {
                                    const ganadoresRifa = ganadores.filter(g => g.rifaId === rifa.id);
                                    const yaSeorteado = ganadoresRifa.some(g => 
                                      g.premioIndex !== undefined && g.premioIndex === index
                                    );
                                    
                                    if (yaSeorteado) {
                                      const ganadorActual = ganadoresRifa.find(g => g.premioIndex === index);
                                      alert(
                                        `✅ El Premio ${index + 1} (${premio}) ya fue sorteado.\n\n` +
                                        `Ganador: ${ganadorActual?.ganadorNombre || 'Desconocido'}\n` +
                                        `Número ganador: ${ganadorActual?.numeroGanador || '?'}\n\n` +
                                        `Este premio ya no puede volver a sortearse.`
                                      );
                                      return;
                                    }
                                    
                                    setSelectedRifa(rifa);
                                    await realizarSorteo(index);
                                  }}
                                  disabled={premioYaSorteado}
                                  className={`w-full px-3 py-2 md:px-4 md:py-2 rounded-lg font-semibold text-sm md:text-base transition flex items-center justify-center gap-2 ${
                                    premioYaSorteado
                                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                      : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                  }`}
                                >
                                  {premioYaSorteado ? '✓ Premio Sorteado' : `🎲 Sortear Premio ${index + 1}`}
                                </button>
                              );
                            })}
                      </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap mt-2">
                      <button
                        type="button"
                        onClick={() => handleVerRifa(rifa)}
                          className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition"
                      >
                        Ver Detalles
                      </button>
                        {esCreadorDeRifa(rifa, user) && (
                          <button
                            type="button"
                            onClick={() => handleAbrirEditarRifa(rifa)}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                            title="Editar nombre, descripción, alias o Mercado Pago"
                          >
                            <Pencil className="w-4 h-4" />
                            Editar
                          </button>
                        )}
                        {rifa.tipo === 'sorteo' && (
                          <button
                            onClick={async () => {
                              await mostrarQRModal(rifa.id);
                            }}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                            title="Ver QR para sorteo"
                          >
                            📱 Ver QR
                          </button>
                        )}
                        {renderBotonesCompartirRifa(rifa, { compact: true })}
                      </div>
                      
                      {false && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full">
                          {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => {
                            const ganadoresRifa = ganadores.filter(g => g.rifaId === rifa.id);
                            const premioYaSorteado = ganadoresRifa.some(g => g.rifaPremio === premio);
                            
                            return (
                              <button
                                key={index}
                                onClick={() => {
                                  setSelectedRifa(rifa);
                                  setSelectedPremioForSorteo({ premio, index });
                                  setShowSeleccionarPremioModal(true);
                                }}
                                disabled={premioYaSorteado}
                                className={`w-full px-3 py-2 md:px-4 md:py-2 rounded-lg font-semibold text-sm md:text-base transition flex items-center justify-center gap-2 ${
                                  premioYaSorteado
                                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                                    : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                }`}
                              >
                                🎲 {premioYaSorteado ? `✓ Premio ${index + 1} Sortado` : `Sortear Premio ${index + 1}`}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      
                      {rifa.finalizada && (
                        <span className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold">
                          ✅ Finalizada
                      </span>
                      )}
                      
                      {isAdmin() && (
                        <button
                          onClick={() => handleEliminarRifa(rifa.id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                        >
                          🗑️ Eliminar
                        </button>
                      )}
                  </div>
                    
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-black bg-opacity-30 rounded-lg p-4 relative">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-purple-300 text-sm">Premio</p>
                    {isAdmin() && !rifa.finalizada && (
                      <button
                        onClick={() => handleEditarPremios(rifa)}
                        className="text-purple-400 hover:text-purple-300 transition p-1"
                        title="Editar premios"
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                  <div className="text-white font-bold space-y-1">
                    {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => (
                      <div key={index} className="text-sm">{index + 1}° {premio}</div>
                    ))}
                  </div>
                    </div>
                <div className="bg-black bg-opacity-30 rounded-lg p-4">
                  <p className="text-purple-300 text-sm mb-1">Precio</p>
                  <p className="text-white font-bold">${rifa.precio}</p>
                    </div>
                <div className="bg-black bg-opacity-30 rounded-lg p-4">
                  <p className="text-purple-300 text-sm mb-1">Vendidos</p>
                  <p className="text-white font-bold">{rifa.numerosVendidos}/{rifa.totalNumeros}</p>
                    </div>
                <div className="bg-black bg-opacity-30 rounded-lg p-4">
                  <p className="text-purple-300 text-sm mb-1">Recaudado</p>
                  <p className="text-green-400 font-bold">${totalRecaudado}</p>
                  </div>
                </div>

              {/* Estados de Números */}
              <div className="bg-black bg-opacity-30 rounded-lg p-4 mb-6">
                <h4 className="text-white font-bold mb-3">Estado de Números</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-white text-sm">Disponibles: {parseInt(rifa.totalNumeros) - rifa.numerosVendidos - (rifa.numerosPendientes?.length || 0)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span className="text-white text-sm">Vendidos: {rifa.numerosVendidos}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-500 rounded"></div>
                    <span className="text-white text-sm">Pendientes: {rifa.numerosPendientes?.length || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                    <span className="text-white text-sm">Total: {rifa.totalNumeros}</span>
                  </div>
              </div>
            </div>

                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-white mb-2">
                        <span>Progreso de ventas</span>
                        <span className="font-bold">{porcentajeVendido.toFixed(1)}%</span>
                </div>
                      <div className="w-full bg-gray-700 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-yellow-500 h-3 rounded-full transition-all"
                          style={{ width: `${porcentajeVendido}%` }}
                        ></div>
              </div>
            </div>

              <div className="flex items-center justify-between pt-4 border-t border-purple-400">
                <div className="text-white">
                  <p className="text-sm text-purple-300">Sorteo programado para:</p>
                  <p className="font-bold text-lg">
                    {rifa.tipoFinalizacion === 'fecha' &&
                    rifa.fechaSorteo &&
                    !Number.isNaN(new Date(rifa.fechaSorteo).getTime())
                      ? new Date(rifa.fechaSorteo).toLocaleDateString()
                      : rifa.tipoFinalizacion === 'fecha'
                        ? 'Sin fecha'
                        : 'Cuando se vendan todos'}
                  </p>
                  </div>
                <div className="text-white text-right">
                  <p className="text-sm text-purple-300">Compradores:</p>
                  <p className="font-bold text-lg">{comprasRifa.length}</p>
              </div>
            </div>

              {/* Botones de Acción Rápida */}
              {(rifa.numerosPendientes?.length || 0) > 0 && (
                <div className="mt-4 pt-4 border-t border-orange-400">
                  <div className="bg-orange-900 bg-opacity-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-orange-200 font-bold">⏳ Números Pendientes</p>
                        <p className="text-orange-300 text-sm">
                          {rifa.numerosPendientes?.length || 0} números esperando confirmación
                        </p>
              </div>
                      <div className="text-right">
                        <p className="text-orange-200 font-bold">
                          ${(rifa.numerosPendientes?.length || 0) * parseFloat(rifa.precio)}
                        </p>
            </div>
          </div>

                    <div className="flex flex-col gap-2 mb-3">
                      {(rifa.numerosPendientes || []).map((num, idx) => (
                        <div
                          key={`pendiente-${num}-${idx}`}
                          className="flex flex-wrap items-center justify-between gap-2 bg-black/30 rounded-lg px-3 py-2.5 border border-orange-500/30"
                        >
                          <span className="bg-orange-500 text-white px-3 py-1.5 rounded-lg font-bold min-w-[2.75rem] text-center">
                            {num}
                          </span>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `¿Confirmar el pago del número ${num}?\n\nSe marcará como vendido y se avisará al comprador por email si figura.`
                                  )
                                ) {
                                  handleConfirmarPago(rifa.id, [num]);
                                }
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition"
                            >
                              <CheckCircle className="w-4 h-4 shrink-0" />
                              Confirmar este
                            </button>
                            <button
                              type="button"
                              onClick={() => handleLiberarNumeroPendiente(rifa.id, num)}
                              className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold border border-orange-400/45 transition"
                            >
                              Liberar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          const totalP = ((rifa.numerosPendientes?.length || 0) * parseFloat(rifa.precio)).toFixed(2);
                          if (
                            window.confirm(
                              `¿Confirmar TODOS los pagos pendientes?\n\nNúmeros: ${(rifa.numerosPendientes || []).join(', ')}\nTotal: $${totalP}`
                            )
                          ) {
                            handleConfirmarPago(rifa.id, rifa.numerosPendientes || []);
                          }
                        }}
                        className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition inline-flex items-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Confirmar todos
                      </button>

                      <button
                        type="button"
                        onClick={() => handleVerRifa(rifa)}
                        className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
                      >
                        Ver detalles
                      </button>
                    </div>
                  </div>
                </div>
              )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Rifas Finalizadas */}
        {misRifasFinalizadas.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2 bg-white/5 rounded-lg px-4 py-2">
              <span className="w-3 h-3 bg-gray-400 rounded-full inline-block"></span>
              Finalizadas ({misRifasFinalizadas.length})
            </h3>
            <div className="space-y-6">
              {misRifasFinalizadas.map(rifa => {
                const comprasRifa = compras.filter(c => c.rifaId === rifa.id);
                const totalRecaudado = rifa.numerosVendidos * parseFloat(rifa.precio);
                
                return (
                  <div key={rifa.id} className="bg-gray-600 bg-opacity-20 backdrop-blur-md rounded-xl p-6 border border-gray-500 opacity-75">
                    <div className="flex justify-between items-start mb-6">
                <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-400">{rifa.titulo}</h3>
                          <span className="px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full">
                            ✅ Finalizada
                          </span>
                </div>
                        <p className="text-gray-300 mb-2">{rifa.descripcion}</p>
                        <p className="text-gray-400 text-sm">
                          Creada el {new Date(rifa.fechaCreacion).toLocaleDateString()}
                        </p>
              </div>
            </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-2">Premio</p>
                        <div className="text-gray-300 font-bold space-y-1">
                          {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => (
                            <div key={index} className="text-sm">{index + 1}° {premio}</div>
                          ))}
                        </div>
                      </div>
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-1">Precio</p>
                        <p className="text-gray-300 font-bold">${rifa.precio}</p>
                      </div>
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-1">Vendidos</p>
                        <p className="text-gray-300 font-bold">{rifa.numerosVendidos}/{rifa.totalNumeros}</p>
                      </div>
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-1">Recaudado</p>
                        <p className="text-green-300 font-bold">${totalRecaudado}</p>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-500 pt-4">
                    {(() => { const premiosList = rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : []); const totalSorteados = (ganadores || []).filter(g => g.rifaId === rifa.id).length; return totalSorteados < premiosList.length; })() && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full mb-4">
                        {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => {
                          const ganadoresRifa = (ganadores || []).filter(g => g.rifaId === rifa.id);
                          const premioYaSorteado = ganadoresRifa.some(g => {
                            if (g.premioIndex !== undefined && g.premioIndex !== null) {
                              return g.premioIndex === index;
                            }
                            const premioNormalizado = premio.trim().toLowerCase();
                            const ganadorPremio = (g.rifaPremio || '').trim().toLowerCase();
                            const hayOtroGanadorConEsteIndice = ganadoresRifa.some(otroG => 
                              otroG.premioIndex === index && otroG.id !== g.id
                            );
                            return ganadorPremio === premioNormalizado && !hayOtroGanadorConEsteIndice;
                          });
                          
                          return (
                            <button
                              key={index}
                              onClick={async () => {
                                const ganadoresRifa = (ganadores || []).filter(g => g.rifaId === rifa.id);
                                const yaSeorteado = ganadoresRifa.some(g => 
                                  g.premioIndex !== undefined && g.premioIndex === index
                                );
                                
                                if (yaSeorteado) {
                                  const ganadorActual = ganadoresRifa.find(g => g.premioIndex === index);
                                  alert(
                                    `✅ El Premio ${index + 1} (${premio}) ya fue sorteado.\n\n` +
                                    `Ganador: ${ganadorActual?.ganadorNombre || 'Desconocido'}\n` +
                                    `Número ganador: ${ganadorActual?.numeroGanador || '?'}\n\n` +
                                    `Este premio ya no puede volver a sortearse.`
                                  );
                                  return;
                                }
                                
                                setSelectedRifa(rifa);
                                await realizarSorteo(index);
                              }}
                              disabled={premioYaSorteado}
                              className={`w-full px-3 py-2 md:px-4 md:py-2 rounded-lg font-semibold text-sm md:text-base transition flex items-center justify-center gap-2 ${
                                premioYaSorteado
                                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                              }`}
                            >
                              {premioYaSorteado ? '✓ Premio Sorteado' : `🎲 Sortear Premio ${index + 1}`}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex gap-3 flex-wrap">
                      <button
                        onClick={() => handleVerRifa(rifa)}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition"
                      >
                        👁️ Ver Detalles
                      </button>
                      {esCreadorDeRifa(rifa, user) && (
                        <button
                          type="button"
                          onClick={() => handleAbrirEditarRifa(rifa)}
                          className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                        >
                          <Pencil className="w-4 h-4" />
                          Editar
                        </button>
                      )}
                      <button
                        onClick={() => {
                          navigateTo('/ganadores');
                        }}
                        className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition"
                      >
                        🏆 Ver Ganadores
                      </button>
                      {isAdmin() ? (
                        <button
                          onClick={async () => {
                            const confirmacion = confirm(
                              `¿Deseas reactivar esta rifa?\n\n` +
                              `Esto permitirá que puedas sortear los premios restantes.\n\n` +
                              `Rifa: ${rifa.titulo}`
                            );
                            
                            if (confirmacion) {
                              try {
                                await updateRifaInFirestore(rifa.id, {
                                  finalizada: false
                                });
                                
                                const rifasActualizadas = rifas.map(r => 
                                  r.id === rifa.id ? { ...r, finalizada: false } : r
                                );
                                setRifas(rifasActualizadas);
                                
                                alert('✅ Rifa reactivada exitosamente. Ahora puedes sortear los premios restantes.');
                              } catch (error) {
                                console.error('Error al reactivar rifa:', error);
                                alert('❌ Error al reactivar la rifa. Por favor intenta de nuevo.');
                              }
                            }
                          }}
                          className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition"
                        >
                          🔄 Reactivar Rifa
                        </button>
                      ) : (
                        <div className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold text-center text-sm">
                          ⚠️ Contacta al admin para reactivar
                        </div>
                      )}
                      <span className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg font-semibold cursor-not-allowed inline-flex items-center">
                        ✅ Finalizada
                      </span>
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Sin rifas */}
        {misRifasActivas.length === 0 && misRifasFinalizadas.length === 0 && (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-blur-md rounded-xl border border-purple-400">
            <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-white text-xl mb-2">¡Hola {user?.name || 'Usuario'}! No has creado ninguna rifa aún</p>
            <p className="text-purple-300 mb-6">¡Crea tu primera rifa y comienza a vender números!</p>
            <button
              onClick={() => {
                const emailSugerido = user?.email || (users.find(u => u.id === user?.id || u.uid === user?.uid)?.email) || (users.find(u => u.id === user?.id)?.correo) || '';
                setRifaForm(prev => ({ ...prev, emailVendedor: (prev.emailVendedor && prev.emailVendedor.trim()) ? prev.emailVendedor : emailSugerido }));
                setShowCreateRifa(true);
              }}
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold inline-flex items-center gap-2 transition"
            >
              <Plus className="w-5 h-5" />
              Crear Mi Primera Rifa
            </button>
          </div>
        )}
      </main>
    );
  };

  // Admin Panel - Gestión de usuarios y rifas
  const AdminPanelPage = () => {
    // Excluir al admin de la lista de usuarios (comparar por id en lugar de uid)
    const regularUsers = users.filter(u => u.id !== 'Trz2xMBhVjc9pnOOGkTPegeNGGD2');
    
    // Eliminar duplicados por email, manteniendo solo el primer registro
    const uniqueUsers = regularUsers.filter((user, index, self) => 
      index === self.findIndex(u => u.email === user.email)
    );
    
    const pendingUsers = uniqueUsers.filter(u => !u.pagoRealizado);
    const approvedUsers = uniqueUsers.filter(u => u.pagoRealizado);
    
    console.log('👥 Admin Panel Debug:', {
      totalUsers: users.length,
      regularUsers: regularUsers.length,
      pendingUsers: pendingUsers.length,
      approvedUsers: approvedUsers.length,
      usersList: users.map(u => ({ 
        name: u.nombre || u.username, 
        email: u.email, 
        uid: u.uid,
        pagoRealizado: u.pagoRealizado 
      }))
    });
    
    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
          <button
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-4 sm:mb-6 transition text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            Volver al inicio
          </button>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-4 sm:p-6 md:p-8 border border-purple-400 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-4 sm:mb-6">🔐 Panel de Administración</h2>
          <p className="text-white text-sm sm:text-base md:text-lg mb-4 sm:mb-6">Gestiona usuarios y sus permisos para crear rifas</p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Usuarios Pendientes</p>
              <p className="text-orange-400 font-bold text-3xl">{pendingUsers.length}</p>
          </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Usuarios Aprobados</p>
              <p className="text-green-400 font-bold text-3xl">{approvedUsers.length}</p>
                  </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Total Usuarios</p>
              <p className="text-white font-bold text-3xl">{uniqueUsers.length}</p>
                  </div>
                  </div>
                  </div>
                  
        {/* Sección de Usuarios Pendientes */}
        {pendingUsers.length > 0 && (
          <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-orange-400 mb-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="text-orange-400">⏳</span>
              Usuarios Pendientes de Aprobación ({pendingUsers.length})
            </h3>
            
            <div className="space-y-4">
              {pendingUsers.map(pendingUser => (
                <div key={pendingUser.id} className="bg-orange-900 bg-opacity-50 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-white font-bold text-lg">{pendingUser.nombre || pendingUser.username}</h4>
                      <p className="text-orange-200 text-sm">{pendingUser.email}</p>
                      <p className="text-orange-300 text-sm">Registrado: {new Date(pendingUser.fechaRegistro).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-orange-200 text-sm">Estado</p>
                      <p className="text-orange-400 font-bold">Pendiente</p>
                    </div>
                </div>
                
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        console.log('🔍 Clicked approve for user:', pendingUser);
                        const rifasPermitidas = prompt('¿Cuántas rifas puede crear este usuario? (0 = ilimitado)');
                        if (rifasPermitidas !== null) {
                          const cantidad = rifasPermitidas === '0' ? 999 : parseInt(rifasPermitidas);
                          if (!isNaN(cantidad) && cantidad > 0) {
                            handleApproveUser(pendingUser, cantidad);
                          } else {
                            alert('Por favor ingresa un número válido');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aprobar Usuario
                    </button>
                    
                    <button
                      onClick={() => handleRejectUser(pendingUser)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Rechazar
                    </button>
                </div>
                </div>
                        ))}
                      </div>
                    </div>
        )}

        {/* Sección de Usuarios Aprobados */}
        {approvedUsers.length > 0 && (
          <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-8 border border-green-400 mb-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="text-green-400">✅</span>
              Usuarios Aprobados ({approvedUsers.length})
            </h3>
            
                        <div className="space-y-4">
              {approvedUsers.map(approvedUser => (
                <div key={approvedUser.id} className="bg-green-900 bg-opacity-50 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-white font-bold text-lg">{approvedUser.nombre || approvedUser.username}</h4>
                      <p className="text-green-200 text-sm">{approvedUser.email}</p>
                      <p className="text-green-300 text-sm">
                        Rifas permitidas: {approvedUser.rifasPermitidas === 999 ? 'Ilimitadas' : approvedUser.rifasPermitidas}
                      </p>
                      <p className="text-green-300 text-sm">
                        Aprobado: {new Date(approvedUser.fechaPago).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-200 text-sm">Estado</p>
                      <p className="text-green-400 font-bold">Aprobado</p>
                  </div>
                    </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const nuevasRifas = prompt(`Rifas permitidas actuales: ${approvedUser.rifasPermitidas}\n\n¿Cuántas rifas puede crear? (0 = ilimitado)`);
                        if (nuevasRifas !== null) {
                          const cantidad = nuevasRifas === '0' ? 999 : parseInt(nuevasRifas);
                          if (!isNaN(cantidad) && cantidad >= 0) {
                            handleApproveUser(approvedUser, cantidad);
                          } else {
                            alert('Por favor ingresa un número válido');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Modificar Permisos
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mensaje si no hay usuarios */}
        {regularUsers.length === 0 && (
          <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-8 border border-purple-400 text-center">
            <div className="text-6xl mb-4">👥</div>
            <p className="text-white text-xl mb-2">No hay usuarios registrados</p>
            <p className="text-purple-300">Los usuarios aparecerán aquí cuando se registren</p>
          </div>
        )}
      </main>
    );
  };

  // Gestionar Rifas Page - Ver compradores de cada rifa
  const GestionarRifasPage = () => {
    const [selectedRifaId, setSelectedRifaId] = useState(null);
    // Filtrar rifas por creadorId - comparar tanto con user.id como con user.uid
    const misRifas = rifas.filter(r => 
      r.creadorId === user?.id || 
      r.creadorId === user?.uid || 
      (r.creadorNombre && user?.name && r.creadorNombre.toLowerCase() === user.name.toLowerCase())
    );
    
    // Solo mostrar rifas que tienen compras
    const rifasConCompras = misRifas.filter(rifa => {
      const comprasRifa = compras.filter(c => c.rifaId === rifa.id);
      return comprasRifa.length > 0;
    });

    const toggleRifa = (rifaId) => {
      setSelectedRifaId(selectedRifaId === rifaId ? null : rifaId);
    };

    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <button
          onClick={() => navigateTo('/mis-rifas')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-4 sm:mb-6 transition text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          Volver a Mis Rifas
        </button>

        <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-4 sm:p-6 md:p-8 border border-purple-400 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-2">👥 Gestionar Rifas y Compradores</h2>
          <p className="text-white text-sm sm:text-base md:text-lg">
            Revisa los compradores de cada rifa
          </p>
        </div>

        {rifasConCompras.length === 0 ? (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400">
            <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-white text-xl mb-2">No hay rifas con compradores aún</p>
            <p className="text-purple-300">Las rifas con compras aparecerán aquí</p>
          </div>
        ) : (
          <div className="space-y-6">
            {rifasConCompras.map(rifa => {
              const compradoresRifa = compras.filter(c => c.rifaId === rifa.id);
              console.log('🔍 Rifa:', rifa.titulo);
              console.log('📊 Compras encontradas:', compradoresRifa);
              console.log('📂 Todas las compras:', compras);
              
              const compradoresConfirmados = compradoresRifa.filter(c => c.estado === 'confirmado');
              const compradoresPendientes = compradoresRifa.filter(c => c.estado === 'pendiente');
              const isExpanded = selectedRifaId === rifa.id;
              
              return (
                <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400 overflow-hidden">
                  {/* Header clickeable */}
                  <div 
                    onClick={() => toggleRifa(rifa.id)}
                    className="p-6 cursor-pointer hover:bg-white hover:bg-opacity-5 transition"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-yellow-400">{rifa.titulo}</h3>
                          <span className="px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
                            {rifa.tipo === 'rapadita' ? '⚡ Rapadita' : rifa.tipo === 'sorteo' ? '🎲 Sorteo' : '🎫 Rifa'}
                          </span>
                          <span className={`px-3 py-1 ${isExpanded ? 'bg-green-600' : 'bg-blue-600'} text-white text-xs font-semibold rounded-full`}>
                            {isExpanded ? '▼ Ver Menos' : '▶ Ver Compradores'}
                          </span>
                        </div>
                        <p className="text-white text-sm mb-3">{rifa.descripcion}</p>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-black bg-opacity-40 rounded-lg p-3">
                            <p className="text-purple-300 text-xs mb-1">Total Compradores</p>
                            <p className="text-white font-bold text-xl">{compradoresRifa.length}</p>
                          </div>
                          <div className="bg-green-900 bg-opacity-40 rounded-lg p-3">
                            <p className="text-green-300 text-xs mb-1">Confirmados</p>
                            <p className="text-green-400 font-bold text-xl">{compradoresConfirmados.length}</p>
                          </div>
                          <div className="bg-orange-900 bg-opacity-40 rounded-lg p-3">
                            <p className="text-orange-300 text-xs mb-1">Pendientes</p>
                            <p className="text-orange-400 font-bold text-xl">{compradoresPendientes.length}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contenido expandible */}
                  {isExpanded && compradoresRifa.length > 0 && (
                    <div className="border-t border-purple-400 bg-black bg-opacity-20 p-6">
                      <h4 className="text-lg font-bold text-white mb-4">📋 Lista de Compradores</h4>
                      
                      {compradoresConfirmados.length > 0 && (
                        <div className="mb-6">
                          <h5 className="text-md font-semibold text-green-400 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Compradores Confirmados ({compradoresConfirmados.length})
                          </h5>
                          <div className="space-y-3">
                            {compradoresConfirmados.map(compra => (
                              <div key={compra.id} className="bg-green-900 bg-opacity-30 rounded-lg p-4 border border-green-500">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg">{compra.compradorNombre}</p>
                                    <p className="text-green-200 text-xs">ID Compra: {compra.id}</p>
                                    <p className="text-green-200 text-sm">📍 {compra.compradorDireccion}</p>
                                    <p className="text-green-200 text-sm">📧 {compra.compradorEmail}</p>
                                    <p className="text-green-200 text-sm">📱 {compra.compradorTelefono}</p>
                                  </div>
                                  <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                                    Confirmado
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                  <span className="text-purple-300 text-sm font-semibold">Números:</span>
                                  {compra.numeros.sort((a, b) => a - b).map(num => (
                                    <span key={num} className="bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">
                                      {num}
                                    </span>
                                  ))}
                                </div>
                                <div className="mt-2 text-right">
                                  <span className="text-green-300 font-bold">Total: ${compra.total}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {compradoresPendientes.length > 0 && (
                        <div>
                          <h5 className="text-md font-semibold text-orange-400 mb-3 flex items-center gap-2">
                            <span className="text-orange-400">⏳</span>
                            Compradores Pendientes ({compradoresPendientes.length})
                          </h5>
                          <div className="space-y-3">
                            {compradoresPendientes.map(compra => (
                              <div key={compra.id} className="bg-orange-900 bg-opacity-30 rounded-lg p-4 border border-orange-500">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg">{compra.compradorNombre}</p>
                                    <p className="text-orange-200 text-xs">ID Compra: {compra.id}</p>
                                    <p className="text-orange-200 text-sm">📍 {compra.compradorDireccion}</p>
                                    <p className="text-orange-200 text-sm">📧 {compra.compradorEmail}</p>
                                    <p className="text-orange-200 text-sm">📱 {compra.compradorTelefono}</p>
                                  </div>
                                  <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded-full">
                                    Pendiente
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                  <span className="text-purple-300 text-sm font-semibold">Números:</span>
                                  {compra.numeros.sort((a, b) => a - b).map(num => (
                                    <span key={num} className="bg-orange-500 text-white px-2 py-1 rounded text-sm font-bold">
                                      {num}
                                    </span>
                                  ))}
                                </div>
                                <div className="mt-2 text-right">
                                  <span className="text-orange-300 font-bold">Total: ${compra.total}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    );
  };

  // Todas las Rifas Page - Solo para admin
  const TodasLasRifasPage = () => {
    // Mostrar TODAS las rifas (admin puede verlas todas)
    const allRifas = rifas;
    
    return (
    <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <button
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-4 sm:mb-6 transition text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          Volver al inicio
        </button>

        <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-4 sm:p-6 md:p-8 border border-purple-400 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-4 sm:mb-6">🎫 Todas las Rifas Creadas</h2>
          <p className="text-white text-sm sm:text-base md:text-lg mb-4 sm:mb-6">Revisa y gestiona todas las rifas creadas por usuarios autorizados</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Total de Rifas</p>
              <p className="text-purple-400 font-bold text-3xl">{allRifas.length}</p>
            </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Rifas Activas</p>
              <p className="text-green-400 font-bold text-3xl">{allRifas.filter(r => !r.finalizada).length}</p>
            </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Rifas Finalizadas</p>
              <p className="text-orange-400 font-bold text-3xl">{allRifas.filter(r => r.finalizada).length}</p>
            </div>
          </div>
      </div>
      
        {allRifas.length === 0 ? (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400">
          <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-white text-xl mb-2">No hay rifas creadas</p>
            <p className="text-purple-300">Los usuarios aún no han creado ninguna rifa</p>
        </div>
      ) : (
          <div className="space-y-6">
            {allRifas.map(rifa => {
              const creador = users.find(u => u.id === rifa.creadorId);
              const isAutorizada = creador && creador.pagoRealizado === true;
            const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
              const totalRecaudado = rifa.numerosVendidos * parseFloat(rifa.precio);
              
            return (
                <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-md rounded-xl p-6 border border-purple-400">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-2xl font-bold text-yellow-400">{rifa.titulo}</h3>
                        {isAutorizada ? (
                          <span className="px-3 py-1 bg-green-700 text-white text-sm font-semibold rounded-full">
                            ✅ Autorizada
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-orange-600 text-white text-sm font-semibold rounded-full">
                            ⚠️ Pendiente de Autorización
                          </span>
                        )}
                        {rifa.finalizada && (
                          <span className="px-3 py-1 bg-green-600 text-white text-sm font-semibold rounded-full">
                            ✅ Finalizada
                          </span>
                        )}
                      </div>
                <p className="text-white mb-4">{rifa.descripcion}</p>
                      <div className="flex items-center justify-between text-sm text-purple-300">
                        <div className="flex items-center gap-4">
                        <span>Creada por: <span className="text-white font-semibold">{creador?.nombre || creador?.username || 'Usuario desconocido'}</span></span>
                        <span>•</span>
                        <span>{new Date(rifa.fechaCreacion).toLocaleDateString()}</span>
                        </div>
                        <CopiableAliasCbu alias={rifa.alias} cbu={rifa.cbu} />
                      </div>
                    </div>
                    <button
                      onClick={() => handleVerRifa(rifa)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
                    >
                      Ver Detalles
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-2">Premio</p>
                      <div className="text-white font-bold space-y-1">
                        {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => (
                          <div key={index} className="text-sm">{index + 1}° {premio}</div>
                        ))}
                  </div>
                  </div>
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-1">Precio</p>
                      <p className="text-white font-bold">${rifa.precio}</p>
                  </div>
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-1">Vendidos</p>
                      <p className="text-white font-bold">{rifa.numerosVendidos}/{rifa.totalNumeros}</p>
                    </div>
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-1">Recaudado</p>
                      <p className="text-green-400 font-bold">${totalRecaudado}</p>
                  </div>
                </div>

                  <div className="mb-6">
                    <div className="flex justify-between text-sm text-white mb-2">
                      <span>Progreso de ventas</span>
                      <span className="font-bold">{porcentajeVendido.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-yellow-500 h-3 rounded-full transition-all" 
                      style={{ width: `${porcentajeVendido}%` }}
                    ></div>
                  </div>
                </div>
                
                  <div className="flex gap-3 pt-4 border-t border-purple-400">
                <button 
                      onClick={() => handleEliminarRifa(rifa.id)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                >
                      🗑️ Eliminar Rifa
                </button>
                    {creador && (
                      <div className="flex items-center gap-2 text-white ml-auto">
                        <User className="w-4 h-4" />
                        <span className="text-sm">{creador.email}</span>
                      </div>
                    )}
                  </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
  };

  // Habilitar Rifa Page
  const HabilitarRifaPage = () => {
    // Recargar automáticamente cada 10 segundos
    useEffect(() => {
      const interval = setInterval(async () => {
        try {
          const usersData = await loadUsersFromFirestore();
          setUsers(usersData);
          console.log('🔄 Usuarios recargados automáticamente');
        } catch (error) {
          console.error('Error al recargar estado:', error);
        }
      }, 10000); // 10 segundos

      return () => clearInterval(interval);
    }, []);

    const handleRecargarEstado = async () => {
      try {
        const usersData = await loadUsersFromFirestore();
        setUsers(usersData);
        alert('✅ Estado actualizado');
      } catch (error) {
        console.error('Error al recargar estado:', error);
        alert('❌ Error al recargar estado');
      }
    };

    const handleSolicitarHabilitacion = async () => {
      if (!user) {
        alert('❌ Debes iniciar sesión para solicitar habilitación');
        navigateTo('/login');
        return;
      }

      try {
        // Verificar si el usuario ya existe en la base de datos
        const userData = users.find(
          (u) => u.uid === user.uid || u.uid === user.id || u.id === user.id || u.email === user.email
        );
        
        if (userData && userData.pagoRealizado) {
          alert('✅ Ya estás habilitado para crear rifas');
          return;
        }

        if (userData && userData.solicitudPendiente) {
          alert('⏳ Ya tienes una solicitud pendiente. Espere a que el administrador la apruebe.');
          return;
        }

        // Guardar solicitud en Firestore
        const docRef = await addDoc(collection(db, 'solicitudes_rifas'), {
          userId: user.id,
          nombreUsuario: user.name,
          emailUsuario: user.email,
          estado: 'pendiente',
          fechaSolicitud: new Date().toISOString(),
          rifasSolicitadas: 1
        });

        // Actualizar el usuario en la colección usuarios
        if (userData) {
          const userRef = doc(db, 'usuarios', userData.id);
          await updateDoc(userRef, {
            solicitudPendiente: true,
            fechaSolicitud: new Date().toISOString()
          });
        } else {
          const uid = user.uid || user.id;
          const newUserData = {
            uid,
            nombre: user.name,
            username: user.name,
            email: user.email,
            fechaRegistro: new Date().toISOString(),
            solicitudPendiente: true,
            fechaSolicitud: new Date().toISOString(),
            pagoRealizado: false,
            rifasPermitidas: 0,
          };
          if (user.photoURL) newUserData.foto = user.photoURL;
          await setDoc(doc(db, 'usuarios', uid), newUserData, { merge: true });
        }

        // Recargar usuarios
        const usersData = await loadUsersFromFirestore();
        setUsers(usersData);

        alert('✅ Solicitud enviada exitosamente.\n\nEl administrador revisará tu solicitud y te habilitará para crear rifas.');
        
      } catch (error) {
        console.error('Error al enviar solicitud:', error);
        alert('❌ Error al enviar la solicitud. Por favor intenta de nuevo.');
      }
    };

    // Buscar usuario por ID, UID o email
    const userData = users.find(u => 
      u.id === user?.id || 
      u.uid === user?.uid || 
      u.email === user?.email
    );
    
    const yaHabilitado = userData && userData.pagoRealizado;
    const solicitudPendiente = userData && userData.solicitudPendiente;

    // Debug logs
    console.log('🔍 HABILITAR RIFA DEBUG:', {
      currentUser: user,
      userData: userData,
      usersList: users.map(u => ({
        id: u.id,
        uid: u.uid,
        email: u.email,
        nombre: u.nombre,
        pagoRealizado: u.pagoRealizado,
        solicitudPendiente: u.solicitudPendiente
      })),
      yaHabilitado,
      solicitudPendiente
    });

    return (
      <main className="w-full max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <button
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-4 sm:mb-6 transition text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          Volver al inicio
        </button>

        <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-4 sm:p-6 md:p-8 border border-purple-400 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-4">💳 Solicitud de Habilitación para Crear Rifas</h2>
          <p className="text-white text-sm sm:text-base md:text-lg mb-4 sm:mb-6">Solicita tu habilitación para crear rifas de forma gratuita</p>
          
          <div className="bg-green-900 bg-opacity-30 border-2 border-green-500 rounded-lg p-4 mb-6">
            <p className="text-green-200 text-center font-semibold">
              ✅ Habilitación gratuita
            </p>
        </div>
      </div>

        {false && (
              <div>
            {/* Información de Pago - Ocultada porque ahora es gratuito */}
              </div>
        )}

        {/* Estado del Usuario */}
        {yaHabilitado ? (
          <div className="bg-green-900 bg-opacity-50 border-2 border-green-500 rounded-xl p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-2xl font-bold text-green-400 mb-4">¡Ya estás habilitado!</h3>
            <p className="text-white mb-2">Puedes crear rifas sin restricciones.</p>
            {userData.rifasPermitidas && (
              <p className="text-green-300">
                Rifas permitidas: {userData.rifasPermitidas === 999 ? 'Ilimitadas' : userData.rifasPermitidas}
              </p>
            )}
          </div>
        ) : solicitudPendiente ? (
          <div className="bg-orange-900 bg-opacity-50 border-2 border-orange-500 rounded-xl p-8 text-center">
            <div className="text-6xl mb-4">⏳</div>
            <h3 className="text-2xl font-bold text-orange-400 mb-4">Solicitud Pendiente</h3>
            <p className="text-white mb-2">Tu solicitud está siendo revisada por el administrador.</p>
            <p className="text-orange-300 mb-4">Recibirás una notificación cuando sea aprobada.</p>
                <button
              onClick={handleRecargarEstado}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition flex items-center gap-2 mx-auto"
                >
              🔄 Recargar Estado
                </button>
            <p className="text-orange-200 text-sm mt-2">Haz clic para verificar si tu solicitud fue aprobada</p>
          </div>
        ) : (
          <div className="text-center">
                <button
              onClick={handleSolicitarHabilitacion}
              className="px-12 py-5 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold rounded-xl transition text-xl shadow-2xl flex items-center gap-3 mx-auto"
            >
              <CheckCircle className="w-7 h-7" />
              Solicitar Habilitación para Crear Rifas
                </button>
            <p className="text-purple-300 text-sm mt-4">
              * Al hacer clic, se registra tu solicitud y aparecerá como pendiente en el panel de administración.
            </p>
              </div>
        )}
      </main>
    );
  };

  // Ganadores Page
  const GanadoresPage = () => {
    return (
    <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-8 text-center">🏆 Ganadores de Rifas</h2>
        
        {ganadores.length === 0 ? (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400">
            <div className="text-6xl mb-4">🎉</div>
            <p className="text-white text-xl mb-2">Aún no hay ganadores</p>
            <p className="text-purple-300">Los ganadores aparecerán aquí una vez se realicen los sorteos</p>
            </div>
      ) : (
          <div className="space-y-6">
            {ganadores.map(ganador => {
              const rifa = rifas.find(r => r.id === ganador.rifaId);
            return (
                <div key={ganador.id} className="bg-gradient-to-r from-yellow-600 to-yellow-700 rounded-xl p-8 border-4 border-yellow-400 shadow-2xl">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-3xl font-bold text-white mb-2">🎊 {ganador.rifaTitulo}</h3>
                      <p className="text-yellow-100 text-lg">Premio: {ganador.rifaPremio}</p>
          </div>
                    <div className="text-right">
                      <p className="text-yellow-100 text-sm">Fecha del sorteo</p>
                      <p className="text-white font-bold">{new Date(ganador.fechaSorteo).toLocaleString()}</p>
        </div>
                  </div>

                  <div className="bg-white bg-opacity-20 rounded-lg p-6 mb-6">
                    <div className="text-center mb-4">
                      <p className="text-yellow-100 text-lg mb-2">Número Ganador</p>
                      <div className="inline-block bg-white text-yellow-700 text-6xl font-bold px-8 py-4 rounded-xl shadow-lg">
                        {ganador.numeroGanador}
                      </div>
                  </div>
                </div>

                  <div className="bg-white bg-opacity-20 rounded-lg p-6">
                    <p className="text-yellow-100 text-lg mb-3 flex items-center gap-2">
                      <Trophy className="w-6 h-6" />
                      Ganador/a:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-white font-bold text-2xl mb-3">{ganador.ganadorNombre}</p>
                        {/* Solo el creador de la rifa ve WhatsApp y correo del ganador (solo en esta vista Ganadores) */}
                        {user && (user.id === ganador.creadorId || (rifa && user.id === rifa.creadorId)) && (ganador.ganadorEmail || ganador.ganadorTelefono) && (
                          <div className="mt-3 pt-3 border-t border-white border-opacity-30 space-y-2">
                            <p className="text-yellow-100 text-sm font-semibold">Contacto del ganador</p>
                            {ganador.ganadorEmail && (
                              <a href={`mailto:${ganador.ganadorEmail}`} className="flex items-center gap-2 text-white hover:text-yellow-200 transition text-sm">
                                <Mail className="w-4 h-4 flex-shrink-0" />
                                {ganador.ganadorEmail}
                              </a>
                            )}
                            {ganador.ganadorTelefono && (
                              <a href={`https://wa.me/${(ganador.ganadorTelefono || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white hover:text-yellow-200 transition text-sm">
                                <Phone className="w-4 h-4 flex-shrink-0" />
                                WhatsApp: {ganador.ganadorTelefono}
                              </a>
                            )}
                          </div>
                        )}
                        {(() => {
                          const compraGanadora = compras.find(c => c.rifaId === ganador.rifaId && c.numeros.includes(ganador.numeroGanador));
                          if (compraGanadora && user && (user.id === ganador.creadorId || (rifa && user.id === rifa.creadorId)) && compraGanadora.compradorDireccion) {
                            return (
                              <div className="mt-3 pt-3 border-t border-white border-opacity-30">
                                <p className="text-yellow-100 text-sm">Dirección:</p>
                                <p className="text-white">{compraGanadora.compradorDireccion}</p>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                
                    {user && (user.id === ganador.creadorId || (rifa && user.id === rifa.creadorId)) && (ganador.ganadorEmail || ganador.ganadorTelefono) && (
                      <div className="mt-4 pt-4 border-t border-white border-opacity-20 flex gap-2">
                        <a
                          href={`mailto:${ganador.ganadorEmail}?subject=¡Felicitaciones! Ganaste: ${ganador.rifaPremio}&body=Hola ${ganador.ganadorNombre},%0D%0A%0D%0A¡Felicitaciones! Has ganado la rifa "${ganador.rifaTitulo}".%0D%0APremio: ${ganador.rifaPremio}%0D%0ANúmero ganador: ${ganador.numeroGanador}%0D%0A%0D%0APor favor contáctame para coordinar la entrega del premio.`}
                          className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition text-center"
                        >
                          📧 Notificar por Email
                        </a>
                        <a
                          href={`https://wa.me/${(ganador.ganadorTelefono || '').replace(/[^0-9]/g, '')}?text=🎉 ¡Felicitaciones ${ganador.ganadorNombre}! Has ganado la rifa "${ganador.rifaTitulo}". Premio: ${ganador.rifaPremio}. Número ganador: ${ganador.numeroGanador}. Contáctame para coordinar la entrega.`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition text-center"
                        >
                          💬 Notificar por WhatsApp
                        </a>
                      </div>
                    )}
                </div>
            );
          })}
        </div>
      )}
    </main>
  );
  };

  // Modal de Sorteo
  const SorteoModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-12 max-w-2xl w-full border-4 border-yellow-400 text-center">
        <h2 className="text-4xl font-bold text-white mb-8">
          {sorteoEnCurso ? '🎲 Sorteando...' : '🎉 ¡Tenemos un Ganador!'}
        </h2>
        
        <div className="bg-white bg-opacity-20 rounded-xl p-12 mb-8">
          <p className="text-yellow-400 text-2xl mb-4">Número Ganador</p>
          <div className={`text-9xl font-bold text-white transition-all duration-200 ${sorteoEnCurso ? 'animate-pulse scale-105' : 'scale-110'}`}>
            {numeroGanador || (sorteoEnCurso ? '🎲' : '?')}
          </div>
          {sorteoEnCurso && (
            <p className="text-yellow-300 text-lg mt-4 animate-bounce">
              Mezclando números...
            </p>
          )}
        </div>

        {!sorteoEnCurso && numeroGanador && (
          <>
            <div className="mb-6">
              <p className="text-white text-xl mb-2">Premio Sorteado:</p>
              <p className="text-yellow-400 text-3xl font-bold">
                {premioActualSorteo}
              </p>
            </div>
            
            <button
              onClick={() => {
                setShowSorteoModal(false);
                setCurrentPage('ganadores');
              }}
              className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white text-xl font-bold rounded-lg transition"
            >
              Ver Ganadores
            </button>
          </>
        )}
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <Header />

      {/* WhatsApp flotante: solo con sesión iniciada; derecha, centrado vertical, tamaño medio */}
      {user && (
        <a
          href={`https://wa.me/543872198309?text=${encodeURIComponent(
            'Hola Acceso7, soy usuario registrado en la plataforma y quisiera hacer una consulta.'
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group fixed right-4 z-[48] top-1/2 -translate-y-1/2 focus:outline-none sm:right-6"
          title="Contactar a Acceso7 por WhatsApp"
          aria-label="Abrir WhatsApp de Acceso7"
        >
          <span className="pointer-events-none absolute bottom-full right-0 z-[1] mb-2 max-w-[11rem] rounded-2xl rounded-br-md border border-white/25 bg-[#075E54] px-3 py-2 text-right text-[11px] font-medium leading-tight text-white shadow-lg opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 sm:text-xs">
            ¿Necesitás ayuda? Escríbenos
          </span>
          <span className="relative flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_4px_20px_rgba(37,211,102,0.45)] ring-2 ring-white/30 transition duration-200 hover:scale-105 hover:shadow-[0_6px_28px_rgba(37,211,102,0.55)] focus-visible:ring-4 focus-visible:ring-green-300/60 sm:h-14 sm:w-14">
            <svg
              className="relative z-[1] h-[1.65rem] w-[1.65rem] sm:h-7 sm:w-7"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </span>
        </a>
      )}
      
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'rifa-loading' && <RifaLoadingPage />}
      {currentPage === 'rifa-detail' && <RifaDetailPage />}
      {currentPage === 'datos-personales' && <DatosPersonalesPage />}
      {currentPage === 'payment' && <PaymentPage />}
      {currentPage === 'mis-rifas' && user && <MisRifasPage />}
      {currentPage === 'admin-panel' && user && <AdminPanelPage />}
      {currentPage === 'todas-las-rifas' && user && <TodasLasRifasPage />}
      {currentPage === 'gestionar-rifas' && user && <GestionarRifasPage />}
      {currentPage === 'habilitar-rifa' && <HabilitarRifaPage />}
      {currentPage === 'ganadores' && <GanadoresPage />}

      {/* Footer global: Contáctanos + Política de Privacidad */}
      <footer className="mt-16 pt-8 pb-8 border-t border-purple-500 text-center">
        <div className="flex flex-col items-center gap-5">
          <p className="text-white font-semibold text-lg">Contáctanos</p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a
              href="https://wa.me/543872198309"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition"
              aria-label="WhatsApp"
            >
              <MessageCircle className="w-5 h-5" />
              <span>+54 387 219 8309</span>
            </a>
            <a
              href="mailto:acceso7rifas@gmail.com"
              className="flex items-center gap-2 px-5 py-3 bg-purple-700 hover:bg-purple-800 text-white rounded-lg font-medium transition"
              aria-label="Email"
            >
              <Mail className="w-5 h-5" />
              <span>acceso7rifas@gmail.com</span>
            </a>
          </div>
          <a
            href="https://acceso7-32ad3.web.app/politica-privacidad"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 bg-purple-700 hover:bg-purple-800 text-white rounded-lg font-semibold transition inline-flex items-center gap-2"
          >
            🔒 Política de Privacidad
          </a>
          <p className="text-purple-300 text-sm">
            © 2026 Acceso7 Rifas - Todos los derechos reservados
          </p>
        </div>
      </footer>

      {showSorteoModal && <SorteoModal />}

      {/* Modal de Selección de Premio */}
      {showSeleccionarPremioModal && selectedPremioForSorteo && selectedRifa && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-yellow-400">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">🎲 Confirmar Sorteo</h2>
              <p className="text-purple-300">¿Estás seguro de que quieres sortear este premio?</p>
            </div>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              <div>
                  <p className="text-purple-300 text-sm mb-1">Rifa</p>
                  <p className="text-white font-bold text-lg">{selectedRifa.titulo}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm mb-2">Premio a Sortear</p>
                  <select
                    value={selectedPremioForSorteo.premio}
                    onChange={(e) => {
                      const premios = selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : []);
                      const selectedIndex = premios.indexOf(e.target.value);
                      setSelectedPremioForSorteo({ premio: e.target.value, index: selectedIndex });
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-yellow-400 focus:border-yellow-400 focus:outline-none font-semibold text-lg"
                  >
                    {(selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => {
                      const ganadoresRifa = ganadores.filter(g => g.rifaId === selectedRifa.id);
                      // SIEMPRE comparar por índice PRIMERO - esto evita confusión cuando hay premios con el mismo valor
                      const premioYaSorteado = ganadoresRifa.some(g => {
                        // Prioridad 1: Comparar por premioIndex (MÁS PRECISO)
                        if (g.premioIndex !== undefined && g.premioIndex !== null) {
                          // Si el ganador tiene premioIndex, SOLO coincidir si es exactamente el mismo índice
                          return g.premioIndex === index;
                        }
                        // Prioridad 2: Si el ganador NO tiene premioIndex pero estamos buscando por índice,
                        // verificar que el texto coincida Y que no haya otro ganador con ese índice
                        const premioNormalizado = premio.trim().toLowerCase();
                        const ganadorPremio = (g.rifaPremio || '').trim().toLowerCase();
                        // Solo coincidir por texto si:
                        // 1. El texto coincide
                        // 2. NO hay otro ganador con premioIndex igual a este índice (para evitar duplicados)
                        const hayOtroGanadorConEsteIndice = ganadoresRifa.some(otroG => 
                          otroG.premioIndex === index && otroG.id !== g.id
                        );
                        return ganadorPremio === premioNormalizado && !hayOtroGanadorConEsteIndice;
                      });
                      
                      return (
                        <option 
                          key={index} 
                          value={premio}
                          disabled={premioYaSorteado}
                          className={premioYaSorteado ? 'bg-gray-600 text-gray-400' : 'bg-purple-800 text-white'}
                        >
                          {premioYaSorteado ? `✓ ${premio} (Ya sorteado)` : `${index + 1}. ${premio}`}
                        </option>
                      );
                    })}
                  </select>
              </div>
              <div>
                  <p className="text-purple-300 text-sm mb-1">Números Disponibles</p>
                  <p className="text-white font-semibold">{selectedRifa.numerosReservados.length}</p>
              </div>
                </div>
              </div>
            
            <div className="bg-yellow-900 bg-opacity-30 rounded-lg p-4 mb-6 border border-yellow-500">
              <p className="text-yellow-200 text-sm text-center">
                ⚠️ Al confirmar, se realizará el sorteo y se seleccionará un número ganador automáticamente.
              </p>
            </div>
            
            <div className="flex gap-3 flex-col sm:flex-row">
              {(() => {
                const ganadoresRifa = ganadores.filter(g => g.rifaId === selectedRifa.id);
                const premioNormalizado = selectedPremioForSorteo.premio.trim().toLowerCase();
                
                // Buscar el ganador EXACTO del premio seleccionado (SOLO por índice para evitar confusión)
                const ganadorExistente = ganadoresRifa.find(g => {
                  // PRIORIDAD ABSOLUTA: Si tenemos el índice del premio seleccionado, SOLO buscar por índice
                  if (selectedPremioForSorteo.index !== undefined) {
                    // Si el ganador tiene premioIndex, comparar SOLO por índice (evita confusión con premios del mismo valor)
                    if (g.premioIndex !== undefined && g.premioIndex !== null) {
                      return g.premioIndex === selectedPremioForSorteo.index;
                    }
                    // Si el ganador NO tiene premioIndex pero estamos buscando por índice específico,
                    // NO coincidir (para evitar confundir premios con el mismo valor)
                    return false;
                  }
                  
                  // Solo si NO tenemos índice del premio seleccionado, comparar por texto
                  // (esto es un caso de fallback raro)
                  const ganadorPremio = (g.rifaPremio || '').trim().toLowerCase();
                  return ganadorPremio === premioNormalizado;
                });
                
                // Debug para verificar
                console.log('🔍 Verificando ganador para premio:', {
                  premioSeleccionado: selectedPremioForSorteo.premio,
                  premioIndex: selectedPremioForSorteo.index,
                  premioNormalizado,
                  ganadoresRifa: ganadoresRifa.map(g => ({
                    rifaPremio: g.rifaPremio,
                    premioIndex: g.premioIndex,
                    numeroGanador: g.numeroGanador
                  })),
                  ganadorExistente: ganadorExistente ? {
                    rifaPremio: ganadorExistente.rifaPremio,
                    premioIndex: ganadorExistente.premioIndex,
                    numeroGanador: ganadorExistente.numeroGanador
                  } : null
                });
                
                if (ganadorExistente) {
                  return (
                    <>
                      <button
                        onClick={async () => {
                          const confirmacion = confirm(
                            `⚠️ Este premio ya fue sorteado.\n\n` +
                            `Premio: ${selectedPremioForSorteo.premio}\n` +
                            `Ganador actual: ${ganadorExistente.ganadorNombre}\n` +
                            `Número ganador: ${ganadorExistente.numeroGanador}\n\n` +
                            `¿Deseas volver a sortear este premio?\n\n` +
                            `Esto eliminará el ganador anterior y sorteará un nuevo número.`
                          );
                          
                          if (confirmacion) {
                            try {
                              // Eliminar SOLO el ganador del premio específico seleccionado
                              const ganadoresQuery = await getDocs(collection(db, 'ganadores'));
                              const deletePromises = [];
                              ganadoresQuery.forEach((docSnapshot) => {
                                const ganadorData = docSnapshot.data();
                                
                                // Verificar que sea de la misma rifa
                                if (ganadorData.rifaId !== selectedRifa.id) return;
                                
                                // Comparación estricta por premioIndex primero
                                let coincide = false;
                                if (ganadorExistente.premioIndex !== undefined && ganadorData.premioIndex !== undefined) {
                                  coincide = ganadorData.premioIndex === ganadorExistente.premioIndex &&
                                           ganadorData.premioIndex === selectedPremioForSorteo.index;
                                } else {
                                  // Si no hay premioIndex, comparar por texto del premio exacto
                                  const ganadorPremio = (ganadorData.rifaPremio || '').trim().toLowerCase();
                                  coincide = ganadorPremio === premioNormalizado;
                                }
                                
                                if (coincide) {
                                  deletePromises.push(deleteDoc(docSnapshot.ref));
                                }
                              });
                              
                              await Promise.all(deletePromises);
                              
                              // Eliminar de estado local
                              const ganadoresActualizados = ganadores.filter(g => 
                                !(g.rifaId === selectedRifa.id && (
                                  (ganadorExistente.premioIndex !== undefined && g.premioIndex === ganadorExistente.premioIndex) ||
                                  (g.rifaPremio || '').trim().toLowerCase() === premioNormalizado
                                ))
                              );
                              setGanadores(ganadoresActualizados);
                              
                              // Guardar el índice antes de limpiar el estado
                              const indicePremio = selectedPremioForSorteo.index;
                              
                              // Cerrar modal
                              setShowSeleccionarPremioModal(false);
                              setSelectedPremioForSorteo(null);
                              
                              // Pequeño delay para asegurar que el estado se actualice, luego realizar sorteo
                              setTimeout(() => {
                                realizarSorteo(indicePremio);
                              }, 300);
                              
                              alert('✅ Ganador anterior eliminado. Realizando nuevo sorteo...');
                            } catch (error) {
                              console.error('Error al eliminar ganador:', error);
                              alert('❌ Error al volver a sortear. Por favor intenta de nuevo.');
                            }
                          }
                        }}
                        className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
                      >
                        🔄 Volver a Sortear
                      </button>
                      <button
                        onClick={() => {
                          setShowSeleccionarPremioModal(false);
                          setSelectedPremioForSorteo(null);
                        }}
                        className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-lg transition"
                      >
                        Cancelar
                      </button>
                    </>
                  );
                }
                
                return (
                  <>
              <button
                onClick={() => {
                  realizarSorteo(selectedPremioForSorteo.index);
                  setShowSeleccionarPremioModal(false);
                  setSelectedPremioForSorteo(null);
                }}
                className="flex-1 py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition"
              >
                🎲 Confirmar y Sortear
              </button>
              <button
                onClick={() => {
                  setShowSeleccionarPremioModal(false);
                  setSelectedPremioForSorteo(null);
                }}
                className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-lg transition"
              >
                Cancelar
              </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Información del Comprador */}
      {showCompradorModal && compradorInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-yellow-400">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">📋 Información del Comprador</h2>
              <div className="bg-yellow-500 text-black px-4 py-2 rounded-full inline-block font-bold text-xl">
                Número: {compradorInfo.numero}
              </div>
            </div>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
              <div className="space-y-4">
              <div>
                  <p className="text-purple-300 text-sm mb-1">Nombre Completo</p>
                  <p className="text-white font-bold text-lg">{compradorInfo.nombre}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm mb-1">Dirección</p>
                  <p className="text-white font-semibold">{compradorInfo.direccion}</p>
              </div>
              </div>
            </div>
            
                <button
              onClick={() => setShowCompradorModal(false)}
              className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition"
                >
              Cerrar
                </button>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-purple-400">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Iniciar Sesión</h2>
            
            {/* Botón de Google */}
                <button
              onClick={handleGoogleLogin}
              className="w-full py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-semibold flex items-center justify-center gap-3 mb-6 transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </button>

            {/* Enlace a Política de Privacidad */}
            <div className="text-center mb-6">
              <p className="text-purple-200 text-sm">
                Al continuar, aceptas nuestra{' '}
                <a 
                  href="https://acceso7-32ad3.web.app/politica-privacidad" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:text-yellow-300 underline font-semibold"
                >
                  Política de Privacidad
                </a>
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowLogin(false);
                  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
                    window.history.replaceState({}, '', '/inicio');
                    setCurrentPage('home');
                  }
                }}
                className="w-full py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
                >
                  Cancelar
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-purple-400">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Registro</h2>
            
            {/* Botón de Google */}
            <button
              onClick={handleGoogleLogin}
              className="w-full py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-semibold flex items-center justify-center gap-3 mb-6 transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </button>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowRegister(false)}
                className="w-full py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Rifa Modal */}
      {showCreateRifa && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-4 sm:p-8 max-w-2xl w-full border-2 border-purple-400 my-4 sm:my-8 max-h-[95vh] overflow-y-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6 text-center">Crear Nueva Rifa</h2>
            {/* Email del vendedor: se usa para enviar notificaciones cuando compran números */}
            {(() => {
              const emailVendedor = (user?.email && String(user.email).trim()) ? String(user.email).trim() : (users.find(u => u.id === user?.id || u.uid === user?.uid)?.email || users.find(u => u.id === user?.id)?.correo || users.find(u => u.id === user?.id)?.mail || '').trim();
              return emailVendedor ? (
                <p className="mb-4 px-3 py-2 rounded-lg bg-green-900/40 text-green-200 text-sm border border-green-500/50">
                  📧 Las notificaciones de ventas se enviarán a: <strong>{emailVendedor}</strong>
                </p>
              ) : (
                <p className="mb-4 px-3 py-2 rounded-lg bg-amber-900/50 text-amber-200 text-sm border border-amber-500/50">
                  ⚠️ No tenemos tu email. Cierra sesión e inicia con <strong>Google</strong> para que te lleguen los avisos cuando alguien reserve números.
                </p>
              );
            })()}
            <div className="space-y-3 sm:space-y-4">
              {/* Selector de Tipo */}
              <div>
                <label className="block text-white mb-2 font-semibold">Tipo de Sorteo</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setRifaForm({...rifaForm, tipo: 'rifa'})}
                    className={`flex-1 py-3 rounded-lg font-semibold transition ${
                      rifaForm.tipo === 'rifa'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white bg-opacity-20 text-white border border-purple-400'
                    }`}
                  >
                    🎫 Rifa
                  </button>
                  <button
                    type="button"
                    onClick={() => setRifaForm({...rifaForm, tipo: 'rapadita'})}
                    className={`flex-1 py-3 rounded-lg font-semibold transition ${
                      rifaForm.tipo === 'rapadita'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white bg-opacity-20 text-white border border-purple-400'
                    }`}
                  >
                    ⚡ Rapadita
                  </button>
                  <button
                    type="button"
                    onClick={() => setRifaForm({...rifaForm, tipo: 'sorteo'})}
                    className={`flex-1 py-3 rounded-lg font-semibold transition ${
                      rifaForm.tipo === 'sorteo'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white bg-opacity-20 text-white border border-purple-400'
                    }`}
                  >
                    🎲 Sorteo
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Título</label>
                <input
                  type="text"
                  value={rifaForm.titulo}
                  onChange={(e) => setRifaForm({...rifaForm, titulo: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Descripción</label>
                <textarea
                  value={rifaForm.descripcion}
                  onChange={(e) => setRifaForm({...rifaForm, descripcion: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                  rows="3"
                />
              </div>
              {/* Premios: cada uno con su nombre y su propia foto opcional */}
              <div>
                <label className="block text-white mb-2 font-semibold">Premios</label>
                <p className="text-purple-200 text-xs sm:text-sm mb-3">
                  Cada premio puede tener su propia foto (opcional, máx. 5 MB).
                </p>

                {rifaForm.premios.map((premio, index) => {
                  const imgPremio = imagenesPremios[index];
                  return (
                  <div
                    key={index}
                    className="mb-3 rounded-xl border-2 border-yellow-400/60 bg-gradient-to-br from-amber-900/30 to-purple-900/30 p-3 shadow-md shadow-yellow-900/10"
                  >
                    <div className="flex gap-2 items-stretch">
                      {/* Miniatura / Botón subir foto a la izquierda */}
                      {imgPremio?.previewUrl ? (
                        <div className="relative shrink-0">
                          <img
                            src={imgPremio.previewUrl}
                            alt={`Foto del ${index + 1}° premio`}
                            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border-2 border-yellow-400"
                          />
                          <button
                            type="button"
                            onClick={() => quitarImagenPremio(index)}
                            className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow"
                            aria-label="Quitar foto del premio"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label
                          className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-yellow-400 bg-black/20 hover:bg-black/30 cursor-pointer transition text-center px-1"
                          title="Subir foto del premio"
                        >
                          <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-300" />
                          <span className="text-yellow-100 text-[10px] sm:text-xs mt-1 leading-tight">Subir foto</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleSeleccionarImagenPremio(index, e.target.files?.[0] || null)}
                          />
                        </label>
                      )}

                      {/* Nombre del premio + botón borrar */}
                      <div className="flex-1 flex items-center gap-1 sm:gap-2 min-w-0">
                        <span className="text-yellow-400 font-bold text-sm sm:text-lg w-6 sm:w-8 shrink-0">{index + 1}°</span>
                        <input
                          type="text"
                          value={premio}
                          onChange={(e) => updatePremio(index, e.target.value)}
                          className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                          placeholder={`Nombre del ${index + 1}° premio`}
                        />
                      </div>
                      {rifaForm.premios.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePremio(index)}
                        className="px-3 sm:px-4 py-2 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition text-sm sm:text-base"
                      >
                        ✕
                      </button>
                      )}
                    </div>
                  </div>
                  );
                })}
                <button
                  type="button"
                  onClick={addPremio}
                  className="w-full py-2 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  Agregar Premio
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {rifaForm.tipo !== 'sorteo' && (
                <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Precio por Número</label>
                  <input
                    type="number"
                    value={rifaForm.precio}
                    onChange={(e) => setRifaForm({...rifaForm, precio: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    min="1"
                  />
                </div>
                )}
                <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Total de Números</label>
                  <input
                    type="number"
                    value={rifaForm.totalNumeros}
                    onChange={(e) => setRifaForm({...rifaForm, totalNumeros: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    min="1"
                  />
                </div>
              </div>
              
              {/* Tipo de Finalización - Solo para rifas y rapaditas */}
              {rifaForm.tipo !== 'sorteo' && (
                <>
                  <div>
                    <label className="block text-white mb-2 font-semibold text-sm sm:text-base">¿Cuándo termina la rifa?</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setRifaForm({...rifaForm, tipoFinalizacion: 'fecha'})}
                        className={`py-3 px-4 rounded-lg font-semibold transition text-sm sm:text-base ${
                          rifaForm.tipoFinalizacion === 'fecha'
                            ? 'bg-yellow-500 text-black'
                            : 'bg-white bg-opacity-20 text-white border border-purple-400'
                        }`}
                      >
                        📅 Por Fecha
                      </button>
                      <button
                        type="button"
                        onClick={() => setRifaForm({...rifaForm, tipoFinalizacion: 'venta_completa'})}
                        className={`py-3 px-4 rounded-lg font-semibold transition text-sm sm:text-base ${
                          rifaForm.tipoFinalizacion === 'venta_completa'
                            ? 'bg-yellow-500 text-black'
                            : 'bg-white bg-opacity-20 text-white border border-purple-400'
                        }`}
                      >
                        🎯 Cuando se vendan todos
                      </button>
                    </div>
                    <p className="text-purple-300 text-xs mt-2">
                      {rifaForm.tipoFinalizacion === 'fecha' 
                        ? 'La rifa terminará en la fecha que selecciones'
                        : 'La rifa terminará automáticamente cuando se vendan todos los números'
                      }
                    </p>
                  </div>
                  
                  {/* Campo de Fecha (solo visible si se selecciona "Por Fecha") */}
                  {rifaForm.tipoFinalizacion === 'fecha' && (
              <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Fecha del Sorteo</label>
                <input
                  type="date"
                  value={rifaForm.fechaSorteo}
                  onChange={(e) => setRifaForm({...rifaForm, fechaSorteo: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                />
              </div>
                  )}
                </>
              )}
              
            {/* Datos para Transferencias - Solo para rifas y rapaditas */}
            {rifaForm.tipo !== 'sorteo' && (
            <div className="bg-yellow-900 bg-opacity-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <h4 className="text-yellow-400 font-bold mb-2 text-sm sm:text-base">🏦 Datos para Transferencias (Alias / CBU)</h4>
              <p className="text-yellow-200 text-xs sm:text-sm mb-3 sm:mb-4">
                Para pagos por transferencia bancaria. Es independiente de Mercado Pago (abajo).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Alias</label>
                    <input
                      type="text"
                    value={rifaForm.alias}
                    onChange={(e) => setRifaForm({...rifaForm, alias: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    placeholder="mi.alias"
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">CBU</label>
                    <input
                      type="text"
                    value={rifaForm.cbu}
                    onChange={(e) => setRifaForm({...rifaForm, cbu: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    placeholder="1234567890123456789012"
                    />
                  </div>
              </div>
              <p className="text-yellow-200 text-xs mt-2">
                * Completa al menos uno de los dos campos (Alias o CBU)
              </p>
            </div>
            )}

            {/* Correo para notificaciones - Solo para rifas y rapaditas */}
            {rifaForm.tipo !== 'sorteo' && (
            <div className="bg-green-900 bg-opacity-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <h4 className="text-green-400 font-bold mb-2 text-sm sm:text-base">📧 Correo para notificaciones</h4>
              <p className="text-green-200 text-xs sm:text-sm mb-3 sm:mb-4">
                A este correo te enviaremos un aviso cuando alguien reserve números (pago pendiente). Así sabés a qué email enviar el aviso de venta.
              </p>
              <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Correo del vendedor</label>
                <input
                  type="email"
                  value={rifaForm.emailVendedor}
                  onChange={(e) => setRifaForm({ ...rifaForm, emailVendedor: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-green-400 focus:border-green-300 focus:outline-none text-sm sm:text-base"
                  placeholder={user?.email || (users.find(u => u.id === user?.id)?.email) || 'tu@email.com'}
                />
              </div>
              <p className="text-green-200 text-xs mt-2">
                * Si está vacío, se usará el email de tu cuenta. Completa este campo para asegurarte de recibir los avisos.
              </p>
            </div>
            )}
            
            {/* WhatsApp - Solo para rifas y rapaditas */}
            {rifaForm.tipo !== 'sorteo' && (
            <div className="bg-blue-900 bg-opacity-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <h4 className="text-blue-400 font-bold mb-2 text-sm sm:text-base">💬 Contacto WhatsApp (Opcional)</h4>
              <p className="text-blue-200 text-xs sm:text-sm mb-3 sm:mb-4">
                Agrega tu número de WhatsApp para que los compradores puedan contactarte directamente
              </p>
                  <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">WhatsApp</label>
                    <input
                      type="tel"
                  value={rifaForm.whatsapp}
                  onChange={(e) => {
                    // Solo permitir números
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setRifaForm({...rifaForm, whatsapp: value});
                  }}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-blue-400 focus:border-green-400 focus:outline-none text-sm sm:text-base"
                  placeholder="5491112345678"
                  maxLength={15}
                    />
                  </div>
              <p className="text-blue-200 text-xs mt-2">
                * Formato: Código de país + número (ejemplo: 5491112345678) - Solo números
              </p>
            </div>
            )}
            
            {/* Mercado Pago — opcional, aparte de Alias/CBU */}
            {rifaForm.tipo !== 'sorteo' && (
            <div className="bg-gradient-to-r from-sky-900/80 to-blue-900/80 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4 border-2 border-sky-400">
              <h4 className="text-sky-200 font-bold mb-2 text-sm sm:text-base flex items-center gap-2">
                <span className="text-xl">💳</span>
                Mercado Pago (opcional)
              </h4>
              <p className="text-sky-100/90 text-xs sm:text-sm mb-3">
                Es <span className="font-semibold">otra opción de cobro</span>, independiente del Alias y CBU. Si cargás un link, los compradores podrán pagar por Mercado Pago además de transferir.
              </p>

              <div className="bg-amber-900/60 border-2 border-amber-500 rounded-lg p-3 mb-4">
                <p className="text-amber-100 font-bold text-xs sm:text-sm mb-2 flex items-center gap-2">
                  <span>⚠️</span> Aviso sobre costos de Mercado Pago
                </p>
                <ul className="text-amber-50/95 text-xs sm:text-sm space-y-1.5 list-disc list-inside">
                  <li>Mercado Pago cobra <span className="font-semibold text-amber-200">comisión</span> al vendedor por cada cobro con link.</li>
                  <li>Si el comprador paga en <span className="font-semibold text-amber-200">cuotas</span>, Mercado Pago puede cobrarle <span className="font-semibold text-amber-200">intereses</span> (según el medio de pago que elija).</li>
                  <li>Los montos exactos dependen de tu cuenta y del plan del comprador; revisalos en la app de Mercado Pago.</li>
                </ul>
              </div>

              <div className="mb-3">
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">
                  Link de pago de Mercado Pago <span className="text-sky-300 font-normal">(no obligatorio)</span>
                </label>
                <input
                  type="url"
                  value={rifaForm.linkMercadoPago}
                  onChange={(e) => setRifaForm({ ...rifaForm, linkMercadoPago: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-sky-400 focus:border-sky-300 focus:outline-none text-sm sm:text-base"
                  placeholder="https://mpago.la/tu-link"
                />
              </div>

              <div className="bg-blue-950/50 rounded-lg p-3 mb-2">
                <p className="text-blue-200 text-xs font-semibold mb-2">ℹ️ ¿Cómo obtener tu link?</p>
                <ol className="text-blue-200/90 text-xs space-y-1 list-decimal list-inside">
                  <li>Entrá a Mercado Pago → Cobrar → Link de pago</li>
                  <li>Creá un link por el precio de <span className="font-semibold">un solo número</span> (el comprador solo podrá pagar 1 número por Mercado Pago)</li>
                  <li>Copiá el link (ej: https://mpago.la/xxxxx) y pegalo arriba</li>
                </ol>
                <p className="text-green-300 text-xs mt-2 font-semibold">
                  ✅ Con el link, el comprador paga en Mercado Pago y el número queda pendiente (naranja) hasta que vos confirmes el pago.
                </p>
              </div>

              <p className="text-sky-200/80 text-xs">
                * Si dejás este campo vacío, solo se usará transferencia con Alias/CBU (confirmación manual).
              </p>
            </div>
            )}

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
                <button
                  onClick={handleCreateRifa}
                  disabled={isCreatingRifa}
                  className={`flex-1 py-2 sm:py-3 text-white rounded-lg font-bold transition text-sm sm:text-base ${
                    isCreatingRifa 
                      ? 'bg-gray-500 cursor-not-allowed opacity-50' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {isCreatingRifa ? (imagenUploading ? '⏳ Subiendo imagen...' : '⏳ Creando...') : 'Crear Rifa'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateRifa(false);
                    if (typeof window !== 'undefined' && window.location.pathname === '/crear-rifa') {
                      window.history.replaceState({}, '', '/inicio');
                      setCurrentPage('home');
                    }
                  }}
                  disabled={isCreatingRifa}
                  className={`flex-1 py-2 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition text-sm sm:text-base ${
                    isCreatingRifa ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Transferencia */}
      {showTransferenciaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-2xl w-full border-2 border-purple-400">
            <h2 className="text-3xl font-bold text-yellow-400 mb-6 text-center">💳 Instrucciones de Transferencia</h2>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-white mb-4">Datos Bancarios</h3>
              <div className="space-y-3">
                  <div>
                  <p className="text-purple-300 text-sm">CBU</p>
                  <TextoCopiable
                    texto="0000003100010123456789"
                    etiqueta="CBU"
                    className="text-white font-bold text-lg"
                  />
                  </div>
                  <div>
                  <p className="text-purple-300 text-sm">Alias</p>
                  <TextoCopiable
                    texto="ACCESO7.RIFAS"
                    etiqueta="Alias"
                    className="text-white font-bold text-lg"
                  />
                  </div>
                  <div>
                  <p className="text-purple-300 text-sm">Titular</p>
                  <p className="text-white font-bold">Juan Pérez</p>
                  </div>
                  <div>
                  <p className="text-purple-300 text-sm">Banco</p>
                  <p className="text-white font-bold">Banco Ejemplo</p>
                  </div>
                  </div>
                </div>

            <div className="bg-yellow-500 bg-opacity-20 rounded-lg p-6 border border-yellow-400 mb-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-4">⚠️ IMPORTANTE</h3>
              <div className="text-center">
                <p className="text-white text-lg mb-4">Incluir en la transferencia el siguiente código:</p>
                <div className="bg-black bg-opacity-40 rounded-lg p-4 mb-4">
                  <p className="text-yellow-400 font-bold text-2xl">REF-{selectedRifa?.id || 'RIFA'}-{new Date().toISOString().split('T')[0].replace(/-/g, '')}-{Math.random().toString(36).substring(2, 6).toUpperCase()}</p>
              </div>
                <p className="text-white text-sm">Este código vincula tu pago con tus números reservados</p>
              </div>
            </div>

            <div className="bg-blue-500 bg-opacity-20 rounded-lg p-6 border border-blue-400 mb-6">
              <h3 className="text-xl font-bold text-blue-400 mb-4">📋 Estado de tu Reserva</h3>
              <div className="space-y-2">
                <p className="text-white">• Tus números están <span className="font-bold text-yellow-400">RESERVADOS</span> por 24 horas</p>
                <p className="text-white">• Tiempo restante: <span className="font-bold text-yellow-400">24 horas</span></p>
                <p className="text-white">• Te avisaremos por email cuando se confirme el pago</p>
                <p className="text-white">• Si no se confirma en 24h, los números se liberarán automáticamente</p>
              </div>
            </div>

            <div className="flex gap-3">
                <button
                onClick={() => {
                  setShowTransferenciaModal(false);
                  handleConfirmPayment();
                }}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition"
                >
                ✅ Confirmar Transferencia
                </button>
                <button
                onClick={() => setShowTransferenciaModal(false)}
                  className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
                >
                  Cancelar
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Datos Personales */}
      {showDatosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-4xl w-full border-2 border-purple-400 my-8">
            <h2 className="text-3xl font-bold text-yellow-400 mb-8 text-center">Tus Datos Personales</h2>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-white mb-4">Resumen de tu Compra</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-purple-300 text-sm">Rifa</p>
                  <p className="text-white font-bold text-lg">{selectedRifa.titulo}</p>
                  <p className="text-purple-300 text-sm mt-2">Premio</p>
                  <p className="text-white font-bold">{selectedRifa.premio}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Números seleccionados</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedNumbers.sort((a, b) => a - b).map(num => (
                      <span key={num} className="bg-yellow-500 text-black px-3 py-1 rounded-lg font-bold">
                        {num}
                      </span>
                    ))}
                  </div>
                  <p className="text-purple-300 text-sm">Total a pagar</p>
                  <p className="text-yellow-400 font-bold text-2xl">${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="bg-black bg-opacity-30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-6">Información de Contacto</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2 font-semibold">Nombre *</label>
                    <input
                      type="text"
                    value={buyerForm.nombre}
                    onChange={(e) => setBuyerForm({...buyerForm, nombre: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="Juan"
                    required
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-2 font-semibold">Apellido *</label>
                    <input
                      type="text"
                    value={buyerForm.apellido}
                    onChange={(e) => setBuyerForm({...buyerForm, apellido: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="Pérez"
                    required
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-2 font-semibold">Correo Electrónico *</label>
                    <input
                    type="email"
                    value={buyerForm.email}
                    onChange={(e) => setBuyerForm({...buyerForm, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="juan@ejemplo.com"
                    required
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-2 font-semibold">Número de Teléfono *</label>
                    <input
                    type="tel"
                    value={buyerForm.telefono}
                    onChange={(e) => setBuyerForm({...buyerForm, telefono: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="+54 9 11 1234-5678"
                    required
                    />
                  </div>
                  <div className="md:col-span-2">
                  <label className="block text-white mb-2 font-semibold">Dirección *</label>
                    <input
                      type="text"
                    value={buyerForm.direccion}
                    onChange={(e) => setBuyerForm({...buyerForm, direccion: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="Av. Corrientes 1234, CABA, Argentina"
                    required
                    />
                  </div>
                </div>
              <p className="text-purple-300 text-sm mt-4">* Campos obligatorios</p>
              </div>

            <div className="flex gap-3 mt-8">
                <button
                onClick={() => setShowDatosModal(false)}
                className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
                >
                Cancelar
                </button>
                <button
                onClick={handleProceedToConfirmation}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Continuar a Confirmación
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Condiciones Legales para Habilitar Rifas */}
      {showCondicionesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl p-8 max-w-4xl w-full border-4 border-red-500 my-8">
            <div className="text-center mb-6">
              <h2 className="text-4xl font-bold text-yellow-400 mb-2">⚠️ TÉRMINOS Y CONDICIONES LEGALES</h2>
              <p className="text-white text-lg">Antes de continuar, lea atentamente los siguientes términos</p>
            </div>

            <div className="bg-black bg-opacity-40 rounded-xl p-6 mb-6 max-h-96 overflow-y-auto border border-yellow-500">
              <div className="space-y-6 text-white text-justify">
                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">1. DESCARGO DE RESPONSABILIDAD</h3>
                  <p className="text-gray-200 leading-relaxed">
                    La plataforma Acceso7 ("Plataforma", "nosotros", "nuestro") proporciona únicamente un servicio técnico de gestión de rifas en línea. NO somos responsables del uso indebido, ilegal o fraudulento que cualquier usuario pueda hacer de esta plataforma. El usuario es el único responsable de:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Garantizar que sus rifas cumplen con todas las leyes y regulaciones locales, estatales y nacionales aplicables.</li>
                    <li>Obtener todas las licencias, permisos y autorizaciones necesarias para llevar a cabo rifas.</li>
                    <li>Verificar que las rifas son legales en su jurisdicción antes de crearlas.</li>
                    <li>Cumplir con todas las obligaciones fiscales y tributarias relacionadas con sus rifas.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">2. RESPONSABILIDAD DEL USUARIO</h3>
                  <p className="text-gray-200 leading-relaxed">
                    El usuario reconoce y acepta que:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Es el único responsable de verificar la legalidad de las rifas en su jurisdicción.</li>
                    <li>Deberá consultar con un abogado si tiene dudas sobre la legalidad de su rifa.</li>
                    <li>Será responsable de todos los daños, pérdidas, reclamos o demandas que surjan del uso de la plataforma.</li>
                    <li>Indemnizará y mantendrá indemne a Acceso7 de cualquier reclamo, demanda, costo o gasto que surja de su uso de la plataforma.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">3. LIMITACIÓN DE RESPONSABILIDAD</h3>
                  <p className="text-gray-200 leading-relaxed">
                    EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, ACCESO7 NO SERÁ RESPONSABLE DE:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Cualquier pérdida, daño o lesión directa, indirecta, incidental, especial o consecuente.</li>
                    <li>Pérdidas de beneficios, datos o oportunidades comerciales.</li>
                    <li>Cualquier actividad ilegal realizada por los usuarios.</li>
                    <li>El incumplimiento de obligaciones fiscales o tributarias por parte del usuario.</li>
                    <li>Problemas legales que puedan surgir del uso de la plataforma.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">4. PROHIBICIONES</h3>
                  <p className="text-gray-200 leading-relaxed">
                    El usuario NO podrá:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Utilizar la plataforma para actividades ilegales o fraudulentas.</li>
                    <li>Crear rifas que violen leyes de juegos de azar, loterías o apuestas.</li>
                    <li>Manipular o interferir con el funcionamiento de la plataforma.</li>
                    <li>Utilizar la plataforma para estafar o engañar a otros usuarios.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">5. ACEPTACIÓN DE RIESGOS</h3>
                  <p className="text-gray-200 leading-relaxed">
                    Al utilizar esta plataforma, el usuario acepta que:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Corre con todos los riesgos asociados al uso de la plataforma.</li>
                    <li>Es responsable de su propio cumplimiento legal.</li>
                    <li>No tendrá derecho a reclamar contra Acceso7 por problemas legales que surjan.</li>
                    <li>Acepta estos términos sin condiciones ni reservas.</li>
                  </ul>
                </div>

                <div className="bg-red-900 bg-opacity-50 border-2 border-red-500 rounded-lg p-4">
                  <p className="text-yellow-300 font-bold text-lg">
                    ⚠️ IMPORTANTE: Si tiene dudas sobre la legalidad de sus rifas, consulte con un abogado antes de continuar. Acceso7 NO proporciona asesoramiento legal y NO se hace responsable de problemas legales.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={aceptoCondiciones}
                  onChange={(e) => setAceptoCondiciones(e.target.checked)}
                  className="mt-1 w-6 h-6 text-yellow-500 rounded focus:ring-2 focus:ring-yellow-400 cursor-pointer"
                />
                <span className="text-white text-lg font-semibold group-hover:text-yellow-400 transition">
                  He leído y acepto los términos y condiciones legales expuestos anteriormente. Entiendo que Acceso7 NO se responsabiliza por el mal uso, uso ilegal o fraudulento de esta plataforma. Acepto todos los riesgos y responsabilidades asociados.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
                <button
                  onClick={() => {
                  setShowCondicionesModal(false);
                  navigateTo('/inicio');
                }}
                className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition text-lg"
              >
                No Acepto - Salir
              </button>
              <button
                onClick={() => {
                  if (aceptoCondiciones) {
                    // Guardar la aceptación en localStorage
                    localStorage.setItem('condiciones_habilitar_rifa', 'true');
                    localStorage.setItem('condiciones_fecha_aceptacion', new Date().toISOString());
                    
                    setShowCondicionesModal(false);
                    // Ya estamos en la página de habilitar-rifa, no necesitamos navegar
                  } else {
                    alert('❌ Debe aceptar los términos y condiciones para continuar');
                  }
                }}
                disabled={!aceptoCondiciones}
                className={`flex-1 py-4 font-bold rounded-lg transition text-lg ${
                  aceptoCondiciones
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                ✅ Acepto y Continuar
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar rifa (solo creador: nombre, descripción, alias, link MP) */}
      {showEditarRifaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-amber-500/40">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-amber-400 flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                Editar rifa
              </h2>
              <button
                type="button"
                onClick={() => setShowEditarRifaModal(false)}
                disabled={guardandoEdicionRifa}
                className="text-gray-400 hover:text-white text-2xl leading-none"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Solo podés cambiar el nombre, la descripción, el alias y el link de Mercado Pago. No se pueden modificar premios, cantidad de números ni precios.
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-white text-sm font-semibold mb-1">Nombre de la rifa</label>
                <input
                  type="text"
                  value={rifaEdicionForm.titulo}
                  onChange={(e) => setRifaEdicionForm((f) => ({ ...f, titulo: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none"
                  placeholder="Título"
                />
              </div>
              <div>
                <label className="block text-white text-sm font-semibold mb-1">Descripción</label>
                <textarea
                  value={rifaEdicionForm.descripcion}
                  onChange={(e) => setRifaEdicionForm((f) => ({ ...f, descripcion: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none resize-y"
                  placeholder="Descripción de la rifa"
                />
              </div>
              {rifaEdicionForm.tipo !== 'sorteo' && (
                <>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-1">Alias (transferencia)</label>
                    <input
                      type="text"
                      value={rifaEdicionForm.alias}
                      onChange={(e) => setRifaEdicionForm((f) => ({ ...f, alias: e.target.value }))}
                      className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none"
                      placeholder="ej. mi.alias.mp"
                    />
                    <p className="text-gray-500 text-xs mt-1">El CBU no se puede editar desde aquí.</p>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-1">Link de Mercado Pago (opcional)</label>
                    <input
                      type="url"
                      value={rifaEdicionForm.linkMercadoPago}
                      onChange={(e) => setRifaEdicionForm((f) => ({ ...f, linkMercadoPago: e.target.value }))}
                      className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none"
                      placeholder="https://mpago.la/..."
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowEditarRifaModal(false)}
                disabled={guardandoEdicionRifa}
                className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 disabled:opacity-60 text-white rounded-lg font-semibold transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleGuardarEdicionRifa}
                disabled={guardandoEdicionRifa}
                className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white rounded-lg font-semibold transition"
              >
                {guardandoEdicionRifa ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Editar Premios */}
      {showEditarPremiosModal && selectedRifa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-yellow-400">Editar Premios</h2>
              <button
                onClick={() => {
                  setShowEditarPremiosModal(false);
                  setPremiosEditando([]);
                  setSelectedRifa(null);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Rifa: {selectedRifa.titulo}
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Modifica los premios de tu rifa. Los cambios se aplicarán inmediatamente.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <label className="block text-white mb-2 font-semibold">Premios</label>
              {premiosEditando.map((premio, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={premio}
                    onChange={(e) => updatePremioEditando(index, e.target.value)}
                    className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    placeholder={`${index + 1}° Premio`}
                  />
                  {premiosEditando.length > 1 && (
                    <button
                      onClick={() => removePremioEditando(index)}
                      className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                      title="Eliminar premio"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addPremioEditando}
                className="w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2"
              >
                ➕ Agregar Premio
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditarPremiosModal(false);
                  setPremiosEditando([]);
                  setSelectedRifa(null);
                }}
                className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition"
              >
                Cancelar
              </button>
              <button
                onClick={guardarPremiosEditados}
                className="flex-1 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition"
              >
                💾 Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Instrucciones de Compra */}
      {showInstrucciones && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-blue-900 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-purple-400 shadow-2xl">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-yellow-400 mb-2">
                📚 Instrucciones de Compra
              </h2>
              <p className="text-purple-200">
                Sigue estos pasos para comprar tus números de rifa
              </p>
            </div>

            {/* Indicador de progreso */}
            <div className="flex justify-center mb-8">
              <div className="flex space-x-2">
                {pasosInstrucciones.map((_, index) => (
                  <div
                    key={index}
                    className={`w-4 h-4 rounded-full transition-all duration-300 ${
                      index <= pasoActual
                        ? 'bg-yellow-400'
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Contenido del paso actual */}
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">
                {pasosInstrucciones[pasoActual]?.icono}
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                {pasosInstrucciones[pasoActual]?.titulo}
              </h3>
              <p className="text-purple-200 text-lg leading-relaxed">
                {pasosInstrucciones[pasoActual]?.descripcion}
              </p>
            </div>

            {/* Botones de navegación */}
            <div className="flex justify-between">
              <button
                onClick={anteriorPaso}
                disabled={pasoActual === 0}
                className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                  pasoActual === 0
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white transform hover:scale-105'
                }`}
              >
                ← Anterior
              </button>

              <div className="flex space-x-4">
                <button
                  onClick={cerrarInstrucciones}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition-all duration-300 transform hover:scale-105"
                >
                  Cerrar
                </button>

                {pasoActual < pasosInstrucciones.length - 1 ? (
                  <button
                    onClick={siguientePaso}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-all duration-300 transform hover:scale-105"
                  >
                    Siguiente →
                  </button>
                ) : (
                  <button
                    onClick={cerrarInstrucciones}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition-all duration-300 transform hover:scale-105"
                  >
                    ¡Empezar a Comprar! 🎯
                  </button>
                )}
              </div>
            </div>

            {/* Información adicional */}
            <div className="mt-8 p-4 bg-black bg-opacity-30 rounded-lg">
              <p className="text-purple-200 text-sm text-center">
                💡 <strong>Tip:</strong> Puedes cerrar estas instrucciones en cualquier momento y volver a verlas haciendo clic en el botón "Ver Instrucciones de Compra"
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de QR para Sorteos */}
      {showQRModal && currentQRUrl && (
        <>
          {/* Contenedor para impresión - solo visible al imprimir */}
          <div className="print-qr-container hidden">
            <div className="print-qr-title">
              {selectedRifa?.titulo || 'QR Code para Sorteo'}
            </div>
            <div className="print-qr-code">
              <QRCodeSVG
                value={currentQRUrl}
                size={300}
                level="H"
                includeMargin={true}
              />
            </div>
            <div className="print-qr-instructions">
              <p><strong>Escanea este código QR para participar</strong></p>
              <p style={{ marginTop: '10px', fontSize: '12px' }}>
                Este QR siempre funciona. Cada escaneo genera un link único automáticamente.
              </p>
            </div>
          </div>

          {/* Modal normal - visible en pantalla */}
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 no-print">
            <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-4 border-yellow-400">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-yellow-400 mb-2">📱 QR Code para Sorteo</h2>
              <p className="text-purple-200 text-sm">
                Este QR SIEMPRE funciona. Cada escaneo genera un nuevo link único automáticamente.
              </p>
            </div>

              <div className="bg-white rounded-lg p-6 mb-6 flex justify-center">
                <QRCodeSVG
                  value={currentQRUrl}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>

            <div className="bg-black bg-opacity-40 rounded-lg p-4 mb-6">
              <p className="text-white text-sm mb-2 font-semibold">URL del QR (Fija - Siempre funciona):</p>
              <p className="text-purple-300 text-xs break-all">{currentQRUrl}</p>
              <p className="text-yellow-400 text-xs mt-2 font-semibold">
                ⚠️ Cada escaneo genera un link único que funciona solo una vez
              </p>
            </div>

            <div className="bg-green-900 bg-opacity-50 rounded-lg p-4 mb-6 border border-green-400">
              <p className="text-green-200 text-sm font-semibold mb-2">✅ VENTAJA:</p>
              <ul className="text-green-100 text-xs space-y-1 list-disc list-inside">
                <li>Este QR <strong>SIEMPRE funciona</strong> - puedes imprimirlo una vez</li>
                <li>Cada vez que alguien lo escanee, se genera automáticamente un nuevo link único</li>
                <li>Cada link generado funciona <strong>SOLO UNA VEZ</strong> y permite seleccionar solo <strong>UN número</strong></li>
                <li>El QR físico nunca expira - siempre genera nuevos links</li>
                <li>Puedes usar el mismo QR impreso para todos los clientes</li>
              </ul>
            </div>

            <div className="flex gap-3 flex-col sm:flex-row">
              <button
                onClick={() => {
                  // Mostrar contenedor de impresión y luego imprimir
                  const printContainer = document.querySelector('.print-qr-container');
                  if (printContainer) {
                    printContainer.classList.remove('hidden');
                    setTimeout(() => {
                      window.print();
                      printContainer.classList.add('hidden');
                    }, 100);
                  } else {
                    window.print();
                  }
                }}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition flex items-center justify-center gap-2"
              >
                🖨️ Imprimir QR
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(currentQRUrl);
                  alert('✅ URL copiada al portapapeles');
                }}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition"
              >
                📋 Copiar URL
              </button>
              <button
                onClick={() => setShowQRModal(false)}
                className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
